import React, { useState, useEffect, useRef } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import {
  Sparkles, Upload, Type, Wand2, Play, Trophy, Brain, Baby, Globe,
  Volume2, VolumeX, Mic, Bookmark, BookmarkCheck, Check, X, ChevronRight, ChevronLeft,
  RotateCcw, Target, TrendingUp, Lightbulb, Shuffle, Flame, ArrowRight, Home, Layers,
  FileText, Camera, Landmark, Languages, BadgeCheck, Stethoscope, Atom, Calculator,
  Banknote, ShieldCheck, Loader2, BookOpen, Rocket, GraduationCap, LogIn, UserPlus,
  Heart, Users, Crown, Zap, PartyPopper, ShieldAlert,
  ThumbsUp, ThumbsDown, Award, Star, Clock, Share2, TrendingDown, Plus,
  Eye, EyeOff,
} from "lucide-react";
import * as mammothNS from "mammoth";
import * as XLSX from "xlsx";
import { api as backend } from "./api.js";
import { trackPageView } from "./analytics";
import { SOURCE_MODES, examCodeForLabel } from "./exam/registry";
import { supportsExamIntelligenceMode } from "./exam/examGenerationService";
const NeetPlayPage = React.lazy(() => import("./neet/NeetPlayPage"));

/* ============================================================================
   Quizkosh  —  AI-powered quiz studio (single-file React app)
   ----------------------------------------------------------------------------
   Brand colors (logo): navy #0c2a4d  +  orange #f48626
   Real generation via the OpenAI Chat Completions API (proxied through the
   secure backend), with an offline sample fallback. Aesthetic: "Electric
   Editorial Arcade".
   =========================================================================== */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Unbounded:wght@400;600;700;800;900&family=Hanken+Grotesk:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
#qz-root{
  --bg:#080b10; --bg2:#0d121a; --surface:rgba(255,255,255,.045); --surface2:rgba(255,255,255,.08);
  --border:rgba(255,255,255,.10); --border2:rgba(255,255,255,.18);
  --text:#f4efe6; --muted:#9aa6b2; --faint:#5d6773;
  --accent:#ff7a1a; --accent2:#27e3c4; --accent3:#ff3d81;
  --correct:#2fe09e; --wrong:#ff5470; --gold:#ffd24a;
  --radius:20px; --radius-sm:13px;
  --fd:'Unbounded',system-ui,sans-serif; --fb:'Hanken Grotesk',system-ui,sans-serif; --fm:'Space Mono',monospace;
  --b1:rgba(255,122,26,.20); --b2:rgba(39,227,196,.16); --b3:rgba(255,61,129,.14);
  font-family:var(--fb); color:var(--text); min-height:100vh; width:100%; position:relative; overflow-x:hidden;
  background:
    radial-gradient(120% 90% at 12% -10%, var(--b1) 0%, transparent 45%),
    radial-gradient(110% 90% at 100% 0%, var(--b3) 0%, transparent 42%),
    radial-gradient(120% 110% at 85% 110%, var(--b2) 0%, transparent 50%),
    linear-gradient(180deg, var(--bg) 0%, var(--bg2) 100%);
  background-attachment:fixed;
}
#qz-root[data-theme="bubblegum"]{ --bg:#fff6fb; --bg2:#ffeef6; --surface:rgba(255,255,255,.75); --surface2:rgba(255,255,255,.95);
  --border:rgba(255,90,160,.16); --border2:rgba(255,90,160,.3); --text:#3a1430; --muted:#9c6f8c; --faint:#c79bb6;
  --accent:#ff4fa3; --accent2:#37c6ff; --accent3:#ffb020; --correct:#16c47f; --wrong:#ff4d6d; --gold:#ffb800;
  --radius:26px; --radius-sm:18px; --b1:rgba(255,79,163,.22); --b2:rgba(55,198,255,.20); --b3:rgba(255,176,32,.20); }
#qz-root[data-theme="paper"]{ --bg:#f3efe6; --bg2:#ebe5d8; --surface:rgba(255,255,255,.7); --surface2:#ffffff;
  --border:rgba(40,30,20,.12); --border2:rgba(40,30,20,.26); --text:#211a12; --muted:#6f6555; --faint:#a89c87;
  --accent:#0e7c66; --accent2:#c2521f; --accent3:#3d5a80; --correct:#0e8a5f; --wrong:#c0392b; --gold:#b8860b;
  --radius:14px; --radius-sm:10px; --b1:rgba(14,124,102,.10); --b2:rgba(194,82,31,.08); --b3:rgba(61,90,128,.08); }
#qz-root[data-theme="arena"]{ --bg:#05060f; --bg2:#0a0a1c; --surface:rgba(255,255,255,.05); --surface2:rgba(255,255,255,.09);
  --border:rgba(120,140,255,.18); --border2:rgba(120,140,255,.36); --text:#eaf0ff; --muted:#8fa0c8; --faint:#5a6792;
  --accent:#ff2d8e; --accent2:#1fd6ff; --accent3:#a855ff; --correct:#28ffb0; --wrong:#ff4d6d; --gold:#ffd24a;
  --radius:16px; --radius-sm:11px; --b1:rgba(255,45,142,.22); --b2:rgba(31,214,255,.18); --b3:rgba(168,85,255,.16); }
#qz-root::before{ content:""; position:fixed; inset:0; pointer-events:none; z-index:0; opacity:.04; mix-blend-mode:overlay;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); }
.qz-wrap{position:relative; z-index:1; max-width:1060px; margin:0 auto; padding:0 20px 64px}

.qz-display{font-family:var(--fd); font-weight:800; letter-spacing:-.02em; line-height:1.04}
.qz-mono{font-family:var(--fm)}
.qz-muted{color:var(--muted)}
.qz-eyebrow{font-family:var(--fm); font-size:12px; letter-spacing:.28em; text-transform:uppercase; color:var(--accent2)}

.qz-top{position:sticky; top:0; z-index:30; backdrop-filter:blur(14px); background:linear-gradient(180deg,rgba(8,11,16,.55),transparent);
  display:flex; align-items:center; justify-content:space-between; gap:10px; padding:clamp(8px,1.4vw,14px) clamp(10px,2vw,22px); max-width:1060px; margin:0 auto}
#qz-root[data-theme="bubblegum"] .qz-top,#qz-root[data-theme="paper"] .qz-top{background:linear-gradient(180deg,rgba(255,255,255,.5),transparent)}
.qz-logo{display:flex; align-items:center; gap:10px; cursor:pointer; user-select:none; flex:0 0 auto; min-width:0}
.qz-logoimg{height:clamp(38px,5.4vw,56px); width:auto; max-width:none; display:block; object-fit:contain; background:#fff; padding:2px 8px; border-radius:10px; box-shadow:0 2px 8px rgba(0,0,0,.18); transition:transform .25s ease; image-rendering:-webkit-optimize-contrast}
.qz-logo:hover .qz-logoimg{ transform: scale(1.04) }
.qz-logo .mark{width:34px;height:34px;border-radius:11px;display:grid;place-items:center;color:#0a0a0a;
  background:linear-gradient(135deg,var(--accent),var(--accent3)); box-shadow:0 6px 22px var(--b1)}
.qz-logo b{font-family:var(--fd); font-weight:900; font-size:19px; letter-spacing:-.03em}
.qz-logo b span{background:linear-gradient(90deg,var(--accent),var(--accent3));-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}
.qz-toolbtns{display:flex; align-items:center; gap:8px; flex-wrap:wrap; justify-content:flex-end; min-width:0}

.qz-brand-wrap{display:flex; align-items:center; gap:clamp(8px,1.2vw,16px); min-width:0; flex:1 1 auto}
.qz-logo-sep{display:inline-block; width:1px; height:clamp(26px,3.8vw,42px); background:linear-gradient(180deg,transparent,var(--border),transparent); flex:none}
.qz-school-logo{height:clamp(38px,5.4vw,56px); width:auto; flex:none; display:flex; align-items:center; background:#fff; padding:2px 8px; border-radius:10px; box-shadow:0 2px 8px rgba(0,0,0,.18); max-width:clamp(80px,18vw,180px)}
.qz-school-logo img{display:block; height:100%; width:auto; max-width:100%; object-fit:contain; image-rendering:-webkit-optimize-contrast}

@media (max-width: 640px){
  .qz-top{flex-wrap:wrap; row-gap:8px}
  .qz-brand-wrap{flex:1 1 100%; min-width:0}
  .qz-toolbtns{flex:1 1 100%; justify-content:flex-start; gap:6px}
  .qz-toolbtns .chip,.qz-toolbtns .btn{font-size:12px}
}
@media (max-width: 480px){
  .qz-logoimg{padding:2px 6px; border-radius:8px}
  .qz-top{gap:6px}
  .qz-school-logo{padding:2px 6px; max-width:36vw}
  .qz-brand-wrap{gap:8px}
  .qz-toolbtns .btn--icon{width:34px; height:34px}
}

.btn{font-family:var(--fb); font-weight:700; font-size:15px; cursor:pointer; border-radius:var(--radius-sm); padding:12px 18px;
  display:inline-flex; align-items:center; gap:9px; transition:transform .14s, box-shadow .2s, background .2s; color:var(--text);
  background:var(--surface2); border:1px solid var(--border)}
.btn:hover{transform:translateY(-2px)} .btn:active{transform:translateY(0)}
.btn:disabled{opacity:.4; cursor:not-allowed; transform:none}
.btn--primary{color:#0a0a0a; background:linear-gradient(135deg,var(--accent),var(--accent3)); border:none;
  box-shadow:0 10px 30px -8px var(--b1), inset 0 1px 0 rgba(255,255,255,.25)}
.btn--primary:hover{box-shadow:0 16px 40px -8px var(--b1)}
.btn--ghost{background:transparent}
.btn--icon{width:42px;height:42px;padding:0;justify-content:center;border-radius:12px}
.btn--icon.on{color:#0a0a0a;background:linear-gradient(135deg,var(--accent2),#39f0c8);border:none}
.btn--lg{font-size:18px; padding:17px 30px; border-radius:16px}
.btn--block{width:100%; justify-content:center}

.glass{background:var(--surface); border:1px solid var(--border); border-radius:var(--radius); backdrop-filter:blur(12px)}
.card{background:var(--surface); border:1px solid var(--border); border-radius:var(--radius); padding:22px; backdrop-filter:blur(12px)}
.glow{box-shadow:0 0 0 1px var(--border), 0 20px 60px -22px var(--b1)}

.chip{display:inline-flex; align-items:center; gap:7px; padding:8px 13px; border-radius:999px; font-size:13px; font-weight:600;
  background:var(--surface2); border:1px solid var(--border); color:var(--muted)}
.tag{font-family:var(--fm); font-size:10.5px; letter-spacing:.12em; text-transform:uppercase; padding:4px 9px; border-radius:7px;
  background:var(--surface2); border:1px solid var(--border); color:var(--accent2)}

.input,.textarea{width:100%; font-family:var(--fb); font-size:16px; color:var(--text); background:var(--surface2);
  border:1.5px solid var(--border); border-radius:var(--radius-sm); padding:15px 16px; outline:none; transition:border .18s, box-shadow .18s}
.input:focus,.textarea:focus{border-color:var(--accent2); box-shadow:0 0 0 4px var(--b2)}
.input::placeholder,.textarea::placeholder{color:var(--faint)}
/* Ensure native <select> dropdown options stay readable on every OS theme.
   Without this, some browsers render the opened panel with the OS palette,
   which can yield white-on-white when the app is in dark mode. */
select.input{color:var(--text); background:var(--surface2)}
select.input option{color:var(--text); background:var(--surface2)}
select.input option:checked,select.input option:hover{background:var(--accent2); color:#0b1020}
.textarea{min-height:130px; resize:vertical; line-height:1.5}

.tile{position:relative; text-align:left; cursor:pointer; background:var(--surface); border:1.5px solid var(--border);
  border-radius:var(--radius-sm); padding:16px; transition:transform .15s, border .15s, background .15s; color:var(--text)}
.tile:hover{transform:translateY(-3px); border-color:var(--border2)}
.tile.sel{border-color:var(--accent2); background:var(--surface2); box-shadow:0 0 0 4px var(--b2)}
.tile .ic{width:42px;height:42px;border-radius:12px;display:grid;place-items:center;margin-bottom:11px;
  background:linear-gradient(135deg,var(--surface2),transparent); border:1px solid var(--border); color:var(--accent)}
.tile h4{font-family:var(--fd); font-weight:700; font-size:15px; margin-bottom:3px}
.tile p{font-size:12.5px; color:var(--muted); line-height:1.35}
.tile .check{position:absolute; top:12px; right:12px; width:22px;height:22px;border-radius:50%;display:grid;place-items:center; color:#0a0a0a; background:var(--accent2)}

.grid{display:grid; gap:13px}
.g2{grid-template-columns:repeat(2,1fr)} .g3{grid-template-columns:repeat(3,1fr)} .g4{grid-template-columns:repeat(4,1fr)}
@media(max-width:760px){.g3,.g4{grid-template-columns:repeat(2,1fr)} .hero{font-size:13vw!important}}
@media(max-width:480px){.g2{grid-template-columns:1fr}}

.seg{display:inline-flex; background:var(--surface2); border:1px solid var(--border); border-radius:13px; padding:5px; gap:4px; flex-wrap:wrap}
.seg button{font-family:var(--fb); font-weight:700; font-size:13.5px; border:none; background:transparent; color:var(--muted); padding:9px 15px; border-radius:9px; cursor:pointer; transition:all .15s}
.seg button.act{color:#0a0a0a; background:linear-gradient(135deg,var(--accent),var(--accent3))}
.seg button:not(.act):hover{color:var(--text)}

.opt{position:relative; width:100%; text-align:left; cursor:pointer; display:flex; align-items:center; gap:14px;
  background:var(--surface); border:1.6px solid var(--border); border-radius:15px; padding:17px 18px; font-size:16.5px; font-weight:600; color:var(--text);
  transition:transform .12s, border .15s, background .15s}
.opt:hover{transform:translateX(4px); border-color:var(--border2)}
.opt__k{flex:none; width:34px;height:34px;border-radius:10px;display:grid;place-items:center;font-family:var(--fm);font-weight:700;font-size:15px; background:var(--surface2); border:1px solid var(--border); color:var(--accent2)}
.opt--chosen{border-color:var(--accent2)}
.opt--correct{border-color:var(--correct); background:rgba(47,224,158,.12)} .opt--correct .opt__k{background:var(--correct);color:#062018}
.opt--wrong{border-color:var(--wrong); background:rgba(255,84,112,.12)} .opt--wrong .opt__k{background:var(--wrong);color:#fff}
.opt--dim{opacity:.5}
.opt[disabled]{cursor:default} .opt[disabled]:hover{transform:none}

.pbar{height:7px;border-radius:99px;background:var(--surface2);overflow:hidden}
.pbar i{display:block;height:100%;border-radius:99px;background:linear-gradient(90deg,var(--accent),var(--accent2));transition:width .4s}

.ring-wrap{position:relative;width:64px;height:64px}
.ring-wrap .t{position:absolute;inset:0;display:grid;place-items:center;font-family:var(--fm);font-weight:700;font-size:18px}

.loaderdots span{display:inline-block;width:9px;height:9px;border-radius:50%;background:var(--accent);margin:0 3px;animation:bounce 1.2s infinite}
.loaderdots span:nth-child(2){background:var(--accent2);animation-delay:.15s}
.loaderdots span:nth-child(3){background:var(--accent3);animation-delay:.3s}
@keyframes bounce{0%,80%,100%{transform:translateY(0);opacity:.5}40%{transform:translateY(-12px);opacity:1}}
.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}

.stat{flex:1;min-width:0;text-align:center;padding:16px 10px;border-radius:14px;border:1px solid var(--border);background:var(--surface)}
.stat .v{font-family:var(--fm);font-weight:700;font-size:26px;line-height:1}
.stat .l{font-size:11.5px;color:var(--muted);margin-top:5px;text-transform:uppercase;letter-spacing:.08em}

.divider{height:1px;background:var(--border);margin:18px 0}
.fadeUp{animation:fadeUp .5s cubic-bezier(.2,.8,.2,1) both}@keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
.pop{animation:pop .35s cubic-bezier(.2,1.4,.4,1) both}@keyframes pop{from{opacity:0;transform:scale(.8)}to{opacity:1;transform:none}}
.shake{animation:shake .4s}@keyframes shake{10%,90%{transform:translateX(-2px)}20%,80%{transform:translateX(4px)}30%,50%,70%{transform:translateX(-7px)}40%,60%{transform:translateX(7px)}}
.floaty{animation:floaty 7s ease-in-out infinite}@keyframes floaty{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}

/* hover note (tooltip) */
.tip{position:relative; display:inline-flex}
.tip__b{position:absolute; top:calc(100% + 10px); right:0; width:236px; padding:10px 12px; border-radius:12px;
  background:var(--surface2); border:1px solid var(--border2); color:var(--text); font-size:12.5px; font-weight:600; line-height:1.45;
  box-shadow:0 14px 34px -12px rgba(0,0,0,.55); opacity:0; transform:translateY(-4px); pointer-events:none; transition:opacity .16s, transform .16s; z-index:50; backdrop-filter:blur(12px)}
.tip:hover .tip__b{opacity:1; transform:translateY(0)}
.tip__b::before{content:""; position:absolute; bottom:100%; right:18px; border:6px solid transparent; border-bottom-color:var(--border2)}

/* modal */
.ovl{position:fixed; inset:0; z-index:80; background:rgba(4,6,10,.62); backdrop-filter:blur(6px); display:grid; place-items:center; padding:20px; animation:fadeUp .2s ease both}
.modal{width:100%; max-width:440px; max-height:88vh; overflow:auto}
.modal__x{position:absolute; top:14px; right:14px; width:38px; height:38px}
.banner{background:var(--surface2); border:1px solid var(--border); border-radius:12px; padding:11px 13px; font-size:13px; color:var(--muted)}
.note-sec{display:flex; gap:9px; align-items:flex-start; background:var(--surface2); border:1px solid var(--border); border-radius:12px; padding:11px 13px; font-size:12.5px; color:var(--text); line-height:1.45}

/* footer + privacy */
.foot{margin-top:48px; padding-top:22px; border-top:1px solid var(--border)}
.foot-row{display:flex; flex-wrap:wrap; gap:14px; align-items:center; justify-content:space-between}
.foot-social{display:flex; gap:8px; align-items:center}
.foot-social-btn{display:inline-grid; place-items:center; width:36px; height:36px; border-radius:50%; background:transparent; color:var(--muted); border:1.5px solid var(--border); cursor:pointer; transition: all .22s ease}
.foot-social-btn:hover{ transform: translateY(-2px); color: var(--accent2); border-color: var(--accent2); background: rgba(39,227,196,.06) }
.foot .links{display:flex; gap:18px; flex-wrap:wrap}
.foot a{color:var(--muted); font-size:13.5px; cursor:pointer; text-decoration:none; font-weight:600}
.foot a:hover{color:var(--text)}
.policy h3{font-family:var(--fd); font-size:22px}
.policy h4{font-family:var(--fd); font-size:14.5px; margin:15px 0 4px}
.policy p{color:var(--muted); font-size:13.5px; line-height:1.55}
@media(max-width:560px){.authbtn{padding:9px 12px!important; font-size:13.5px}}
.qz-confetti{position:absolute; inset:0; overflow:hidden; pointer-events:none; z-index:6}
.qz-confetti i{position:absolute; top:-26px; width:9px; height:15px; border-radius:2px; animation-name:qzfall; animation-timing-function:linear; animation-fill-mode:forwards}
@keyframes qzfall{0%{transform:translateY(0) rotate(0); opacity:1}85%{opacity:1}100%{transform:translateY(112vh) rotate(680deg); opacity:0}}
.r3-grid{display:grid; grid-template-columns:1fr 1fr; gap:14px}
@media(max-width:720px){.r3-grid{grid-template-columns:1fr}}
.r3-row{display:flex; align-items:center; gap:11px; margin:11px 0}
.r3-row .lab{width:72px; font-size:13px; font-weight:700; flex:none}
.r3-track{flex:1; height:11px; border-radius:99px; background:var(--faint); overflow:hidden}
.r3-track i{display:block; height:100%; border-radius:99px; transition:width .8s cubic-bezier(.2,.8,.2,1)}
.r3-row .pct{width:46px; text-align:right; font-size:13px; font-weight:700}
.badges{display:grid; grid-template-columns:repeat(auto-fill,minmax(118px,1fr)); gap:10px}
.badge{border:1px solid var(--border); border-radius:16px; padding:14px 10px; text-align:center; background:var(--bg2); transition:transform .15s}
.badge:hover{transform:translateY(-2px)}
.badge .ic{display:grid; place-items:center; width:44px; height:44px; border-radius:13px; margin:0 auto 9px}
.badge .bn{font-weight:800; font-size:13px; line-height:1.2}
.badge .bd{font-size:11px; color:var(--muted); margin-top:4px; line-height:1.3}
.badge--lock{opacity:.45; filter:grayscale(.5)}
.toast{position:fixed; left:50%; bottom:26px; transform:translateX(-50%); background:var(--text); color:var(--bg); padding:11px 18px; border-radius:99px; font-weight:700; font-size:14px; z-index:90; box-shadow:0 12px 34px rgba(0,0,0,.32)}
.theme-menu{position:absolute; right:0; top:50px; width:206px; max-width:calc(100vw - 16px); padding:8px; z-index:60; background:var(--bg2); border:1px solid var(--border2); border-radius:16px; box-shadow:0 20px 48px rgba(0,0,0,.5)}
/* Mobile: pin to viewport edge so the menu cannot overflow left or right.
   Using position:fixed anchors to the viewport (not the toolbar button), and
   max-width:calc(100vw - 16px) guarantees full visibility on every device. */
@media (max-width: 520px){
  .theme-menu{position:fixed; top:64px; right:8px; left:auto; width:auto; min-width:220px; max-width:calc(100vw - 16px)}
}
.theme-menu .hd{font-size:11px; letter-spacing:.13em; text-transform:uppercase; color:var(--muted); font-weight:800; padding:5px 8px 9px}
.theme-menu .titem{width:100%; display:flex; align-items:center; justify-content:space-between; gap:10px; text-align:left; cursor:pointer; background:var(--surface2); border:1px solid var(--border); border-radius:11px; padding:10px 12px; margin-bottom:6px; color:var(--text); transition:transform .12s, border-color .15s}
.theme-menu .titem:last-child{margin-bottom:0}
.theme-menu .titem:hover{transform:translateY(-1px); border-color:var(--border2)}
.theme-menu .titem.sel{border-color:var(--accent2); box-shadow:0 0 0 3px var(--border2)}
.theme-menu .sw{width:34px; height:20px; border-radius:6px; flex:none; box-shadow:inset 0 0 0 1px rgba(0,0,0,.18)}
.adm-f{display:flex; flex-direction:column; gap:6px; font-size:12.5px; font-weight:700}
.adm-row{display:flex; align-items:center; justify-content:space-between; padding:11px 0; border-bottom:1px solid var(--border)}
.adm-row:last-child{border-bottom:none}
.tog{width:46px; height:26px; border-radius:99px; background:var(--faint); border:1px solid var(--border); position:relative; cursor:pointer; transition:background .15s; flex:none}
.tog.on{background:var(--accent2); border-color:var(--accent2)}
.tog .knob{position:absolute; top:2px; left:2px; width:20px; height:20px; border-radius:50%; background:#fff; transition:left .16s; box-shadow:0 1px 3px rgba(0,0,0,.3)}
.tog.on .knob{left:22px}
.adm-fab{position:fixed; left:18px; bottom:18px; z-index:70; display:inline-flex; align-items:center; gap:7px; padding:9px 14px; border-radius:99px; font-weight:800; font-size:13px; cursor:pointer; color:var(--bg); background:var(--accent2); border:none; box-shadow:0 10px 28px rgba(0,0,0,.35)}
.req-row{display:flex; align-items:flex-start; gap:10px; padding:11px 0; border-bottom:1px solid var(--border)}
.req-row:last-child{border-bottom:none}
.req-badge{font-size:9.5px; text-transform:uppercase; letter-spacing:.06em; font-weight:800; padding:2px 7px; border-radius:99px; margin-left:8px; vertical-align:middle}
.req-pending{background:rgba(245,200,66,.16); color:var(--gold)}
.req-approved{background:rgba(110,231,160,.16); color:var(--correct)}
.req-rejected{background:rgba(255,107,107,.16); color:var(--wrong)}
`;

const THEMES = [
  { id: "voltage", name: "Voltage", hint: "All-purpose", sw: "linear-gradient(135deg,#ff7a18,#13d6c4)" },
  { id: "arena", name: "Arena", hint: "Battle", sw: "linear-gradient(135deg,#6b7cff,#b06bff)" },
  { id: "bubblegum", name: "Bubblegum", hint: "Kids", sw: "linear-gradient(135deg,#ffd1e8,#ff77c6)" },
  { id: "paper", name: "Paper", hint: "Focus", sw: "linear-gradient(135deg,#efe7d3,#cbb98f)" },
];

const CATEGORIES = [
  { group: "School", items: [
    { id: "Primary", icon: Baby, desc: "Ages 5–10 · foundations" },
    { id: "Middle School", icon: BookOpen, desc: "Grades 6–8" },
    { id: "High School", icon: GraduationCap, desc: "Grades 9–12" },
    { id: "College", icon: Layers, desc: "Undergrad & beyond" },
  ]},
  { group: "Entrance & Competitive", items: [
    { id: "NEET", icon: Stethoscope, desc: "Medical entrance" },
    { id: "JEE", icon: Atom, desc: "Engineering entrance" },
    { id: "UPSC", icon: Landmark, desc: "Civil services" },
    { id: "SSC", icon: ShieldCheck, desc: "Staff selection" },
    { id: "CAT", icon: Calculator, desc: "MBA entrance" },
    { id: "Bank Exams", icon: Banknote, desc: "PO / Clerk / RBI" },
  ]},
  { group: "Study Abroad", items: [
    { id: "GRE", icon: GraduationCap, desc: "Grad admissions" },
    { id: "GMAT", icon: TrendingUp, desc: "B-school" },
    { id: "SAT", icon: BookOpen, desc: "US college" },
    // ─── Hidden from UI (Jun-2026). Backend + prompts + registry + validators
    // are FULLY INTACT — only the tile is suppressed. To restore: flip the
    // `hidden` flag to `false` (or delete the property). Do not delete the
    // entry itself. No backend changes are required for restoration.
    { id: "IELTS", icon: Languages, desc: "English proficiency", hidden: true },
    { id: "TOEFL", icon: Languages, desc: "English proficiency", hidden: true },
  ]},
  { group: "Professional", items: [
    { id: "PMP", icon: BadgeCheck, desc: "Project management" },
    { id: "EA", icon: Banknote, desc: "Enrolled Agent · tax" },
    { id: "Technical Certifications", icon: ShieldCheck, desc: "Cloud / dev / security" },
    { id: "General Knowledge", icon: Globe, desc: "Trivia & current affairs" },
  ]},
];

// Part 1 ships Classic + Practice. (Cartoon, Challenge, Survival, Multiplayer arrive in Part 2.)
const MODES = [
  { id: "classic", name: "Classic", icon: Play, desc: "One question at a time. Beat the timer.", color: "var(--accent)" },
  { id: "cartoon", name: "Cartoon", icon: Sparkles, desc: "Quizkosh reacts to every answer — playful and fun.", color: "var(--accent3)" },
  { id: "challenge", name: "Challenge", icon: Zap, desc: "Rising difficulty and a shrinking clock.", color: "var(--gold)" },
  { id: "survival", name: "Survival", icon: Heart, desc: "Three lives. One mistake too many and it's over.", color: "var(--wrong)" },
  { id: "multiplayer", name: "Multiplayer", icon: Users, desc: "2–4 players, pass-and-play, live scoreboard.", color: "var(--accent2)" },
  { id: "practice", name: "Practice", icon: Brain, desc: "No pressure. Hints + instant explanations.", color: "var(--accent2)" },
];

const SOURCES = [
  { id: "topic", icon: Type, name: "Topic / Prompt", desc: "Type anything — AI builds a quiz from its knowledge." },
  { id: "paste", icon: FileText, name: "Paste Text", desc: "Drop notes or an article; questions come from it." },
  { id: "file", icon: Upload, name: "Upload File", desc: ".pdf .docx .xlsx .txt & more — read & quizzed." },
  { id: "image", icon: Camera, name: "Image", desc: "A photo of notes/slides — read via vision." },
  // Phase-1 architecture: visible for all exams; advanced pipeline activates only
  // for NEET in Phase 2. For everything else this is a thin alias for Topic mode
  // using the selected exam/category as the topic.
  { id: "exam_intel", icon: Brain, name: "Exam Intelligence", desc: "Generate exam-style questions from the exam's blueprint — no upload needed." },
];

const DIFFS = [
  { id: "easy", name: "Easy" }, { id: "medium", name: "Medium" }, { id: "hard", name: "Hard" },
  { id: "random", name: "Mixed" }, { id: "progressive", name: "Progressive" },
];

const EXAMS_AUTO_QUESTION_TYPE = new Set(["NEET", "CAT", "UPSC"]);

const DAILY_TOPICS = ["World capitals & geography", "Famous science discoveries", "Logical reasoning puzzles", "History that shaped the world", "Everyday vocabulary builder"];

// Areas of interest captured at signup (and editable later). Each maps to a
// canonical quiz category used to seed the Daily Challenge generator.
// NOTE (Jun-2026): "IELTS" and "TOEFL" are temporarily hidden from the user
// interface but kept in the codebase (backend prompts, registry, validators
// fully intact). To restore visibility, re-add the labels to this array and
// flip `hidden: false` on their entries in `CATEGORIES` above.
const INTEREST_OPTIONS = [
  "School Subjects", "NEET", "JEE", "UPSC", "SSC", "Bank Exams",
  "CAT", "PMP", "EA", "Politics", "Current Affairs", "Others",
];
const INTEREST_TOPIC_HINTS = {
  "School Subjects": ["Class 8 Science", "Class 9 Math", "Class 10 Social Studies", "Class 11 Biology", "English grammar basics"],
  "NEET": ["NEET Biology — Human Physiology", "NEET Chemistry — Organic basics", "NEET Physics — Mechanics", "NEET Botany — Plant Kingdom", "NEET Zoology — Animal Diversity"],
  "JEE": ["JEE Math — Calculus essentials", "JEE Physics — Rotational motion", "JEE Chemistry — Chemical bonding", "JEE Math — Coordinate geometry", "JEE Physics — Electrostatics"],
  "UPSC": ["Indian Polity — Fundamental Rights", "Modern Indian History — Freedom struggle", "Indian Geography — Climate & monsoons", "Indian Economy — Banking & RBI", "Current Affairs — last 6 months"],
  "SSC": ["SSC Quant — Number system", "SSC English — Sentence improvement", "SSC Reasoning — Coding-decoding", "SSC GK — Indian history", "SSC GK — Static GK"],
  "Bank Exams": ["Quantitative Aptitude — DI", "Reasoning — Puzzles & seating", "English — Cloze tests", "General Awareness — Banking awareness", "Computer Awareness — Basics"],
  "CAT": ["CAT QA — Arithmetic", "CAT VARC — Reading comprehension", "CAT DILR — Sets & games", "CAT QA — Algebra", "CAT VARC — Para-jumbles"],
  "IELTS": ["IELTS — Reading skim", "IELTS — Writing Task 2 vocab", "IELTS — Listening section 3 prep", "IELTS — Speaking part 2 cue-cards", "IELTS — Grammar accuracy"],
  "PMP": ["PMP — Process groups", "PMP — Risk management", "PMP — Stakeholder engagement", "PMP — Agile vs predictive", "PMP — Earned value management"],
  "EA": ["Enrolled Agent — Individual taxation", "Enrolled Agent — Business taxation", "Enrolled Agent — Representation, Practices & Procedures", "EA — IRS audit basics", "EA — Estates & trusts"],
  "Politics": ["World politics — major elections", "Indian politics — state vs centre", "Comparative political systems", "Famous political theorists", "Political concepts — democracy & federalism"],
  "Current Affairs": ["This month's headlines", "Recent global events", "Indian economy this quarter", "Science & tech news", "Sports & awards this year"],
};
function topicForInterest(user) {
  const day = new Date().getDay();
  const interest = user && (user.interest || "");
  if (interest === "Others" && user.interest_custom) return user.interest_custom;
  if (interest && INTEREST_TOPIC_HINTS[interest]) {
    const arr = INTEREST_TOPIC_HINTS[interest];
    return arr[day % arr.length];
  }
  return DAILY_TOPICS[day % DAILY_TOPICS.length];
}
function categoryForInterest(user) {
  if (!user || !user.interest) return "Daily Challenge";
  if (user.interest === "Others") return user.interest_custom || "Daily Challenge";
  return user.interest;
}

// Offline fallback so the app always demonstrates, even if the API is unreachable.
const SAMPLE_QUIZ = [
  { q: "Which planet is known as the Red Planet?", options: ["Venus", "Mars", "Jupiter", "Mercury"], correct: 1, explanation: "Mars looks red due to iron oxide (rust) on its surface.", hint: "Named after the Roman god of war.", difficulty: "easy", style: "fact" },
  { q: "What is the largest organ in the human body?", options: ["Liver", "Brain", "Skin", "Heart"], correct: 2, explanation: "The skin is the body's largest organ by surface area and weight.", hint: "You can see all of it.", difficulty: "easy", style: "fact" },
  { q: "If a train travels 60 km in 45 minutes, what is its speed in km/h?", options: ["75", "80", "90", "45"], correct: 1, explanation: "45 min = 0.75 h; 60 ÷ 0.75 = 80 km/h.", hint: "Convert minutes to hours first.", difficulty: "medium", style: "reasoning" },
  { q: "Which gas do plants primarily absorb during photosynthesis?", options: ["Oxygen", "Nitrogen", "Carbon dioxide", "Hydrogen"], correct: 2, explanation: "Plants take in CO₂ and release oxygen during photosynthesis.", hint: "We exhale it.", difficulty: "easy", style: "concept" },
  { q: "A company's revenue rose 25% then fell 20% the next year. Net change?", options: ["+5%", "0%", "-5%", "+10%"], correct: 1, explanation: "1.25 × 0.80 = 1.00, so net change is 0%.", hint: "Multiply the factors.", difficulty: "hard", style: "analytical" },
  { q: "Who wrote the play 'Romeo and Juliet'?", options: ["Charles Dickens", "William Shakespeare", "Jane Austen", "Mark Twain"], correct: 1, explanation: "It is one of Shakespeare's best-known tragedies.", hint: "The Bard of Avon.", difficulty: "easy", style: "fact" },
  { q: "Which data structure uses Last-In-First-Out ordering?", options: ["Queue", "Stack", "Array", "Tree"], correct: 1, explanation: "A stack pushes and pops from the same end (LIFO).", hint: "Think of a pile of plates.", difficulty: "medium", style: "concept" },
  { q: "The capital of Australia is:", options: ["Sydney", "Melbourne", "Canberra", "Perth"], correct: 2, explanation: "Canberra is the purpose-built capital, not Sydney or Melbourne.", hint: "Not the biggest city.", difficulty: "medium", style: "fact" },
];

/* ------------------------- AI GENERATION --------------------------------- */
/* Subscribers are notified whenever a backend AI response includes quota info. */
const _quotaSubscribers = new Set();
function subscribeQuota(fn) { _quotaSubscribers.add(fn); return () => _quotaSubscribers.delete(fn); }
function _emitQuota(q) { if (!q) return; for (const fn of _quotaSubscribers) { try { fn(q); } catch {} } }

async function callClaude(messages, system, extras) {
  try {
    const data = await backend.aiQuery(messages, system, null, extras);
    if (data && data.quota) _emitQuota(data.quota);
    return data.text || "";
  } catch (e) {
    if (e && e.status === 401) {
      const err = new Error("Please log in to generate quizzes.");
      err.authRequired = true;
      throw err;
    }
    if (e && (e.status === 429 || e.status === 402)) {
      const err = new Error(e.message || "Not enough credits for this request.");
      err.quotaReached = true;
      throw err;
    }
    throw e;
  }
}
const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 80);
function shuffle(a) { const x = [...a]; for (let i = x.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [x[i], x[j]] = [x[j], x[i]]; } return x; }

// PDF text extraction. PDF.js isn't a bundled library, so load the classic-script
// build from cdnjs on demand (only when a PDF is picked). Falls back gracefully if
// the network/sandbox blocks it, and detects scanned PDFs that have no text layer.
const PDFJS_VERSION = "3.11.174";
let _pdfjsPromise = null;
function loadPdfJs() {
  if (typeof window !== "undefined" && window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (_pdfjsPromise) return _pdfjsPromise;
  _pdfjsPromise = new Promise((resolve, reject) => {
    try {
      const base = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`;
      const s = document.createElement("script");
      s.src = `${base}/pdf.min.js`;
      s.onload = () => {
        const lib = window.pdfjsLib;
        if (!lib) { reject(new Error("The PDF reader failed to load.")); return; }
        try { lib.GlobalWorkerOptions.workerSrc = `${base}/pdf.worker.min.js`; } catch {}
        resolve(lib);
      };
      s.onerror = () => { _pdfjsPromise = null; reject(new Error("Couldn't load the PDF reader — check your connection and try again.")); };
      document.head.appendChild(s);
    } catch (e) { _pdfjsPromise = null; reject(e); }
  });
  return _pdfjsPromise;
}
async function extractPdfText(arrayBuffer) {
  const pdfjsLib = await loadPdfJs();
  const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const maxPages = Math.min(doc.numPages, 50);
  const parts = [];
  for (let i = 1; i <= maxPages; i++) {
    const page = await doc.getPage(i);
    const tc = await page.getTextContent();
    parts.push(tc.items.map((it) => (it && it.str ? it.str : "")).join(" "));
  }
  const text = parts.join("\n\n").replace(/[ \t]+\n/g, "\n").trim();
  if (!text) throw new Error("This PDF has no selectable text (it may be scanned images). Try a text-based PDF, paste the text, or upload a photo of a page.");
  return text;
}

// Extract plain text from an uploaded file. Handles .txt/.md/.csv/.tsv/.json,
// Word (.docx via mammoth), Excel (.xlsx/.xls via SheetJS), and PDF (via PDF.js).
// PowerPoint and other binary formats are rejected with a clear message.
async function extractFileText(file) {
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
  const type = file.type || "";
  const isText = /^(txt|md|markdown|csv|tsv|json|log|text)$/.test(ext) || type.startsWith("text/") || type === "application/json";
  const unsupported = /^(ppt|pptx|key|pages|numbers|odt|ods|odp|doc|rtf)$/.test(ext);
  if (unsupported) {
    const label = (ext === "ppt" || ext === "pptx") ? "PowerPoint" : ext === "doc" ? "old-format Word (.doc)" : ext.toUpperCase();
    throw new Error(`${label} files aren't supported in this preview. Paste the text instead, or upload .pdf, .txt, .md, .csv, .json, .docx or .xlsx.`);
  }
  if (isText) return await file.text();
  if (ext === "pdf" || type === "application/pdf") {
    const arrayBuffer = await file.arrayBuffer();
    return await extractPdfText(arrayBuffer);
  }
  if (ext === "docx") {
    const mammoth = mammothNS.default || mammothNS;
    const arrayBuffer = await file.arrayBuffer();
    const res = await mammoth.extractRawText({ arrayBuffer });
    return res && res.value ? res.value : "";
  }
  if (ext === "xlsx" || ext === "xls") {
    const arrayBuffer = await file.arrayBuffer();
    const wb = XLSX.read(arrayBuffer, { type: "array" });
    return wb.SheetNames.map((n) => XLSX.utils.sheet_to_csv(wb.Sheets[n])).join("\n\n");
  }
  const t = await file.text();
  if (/\uFFFD/.test(t.slice(0, 2000))) throw new Error("That file type isn't supported here. Paste the text, or upload .pdf, .txt, .md, .csv, .json, .docx or .xlsx.");
  return t;
}

/* --------------------------- CONTENT SAFETY ------------------------------ */
const BLOCK_MSG = {
  default: "I can't build a quiz from that. Quizkosh is a space for learning, so I won't use anything sexual, graphically violent, hateful, or otherwise unsafe. Pick a different topic or source and we'll get you started. 🙂",
  kids: "That topic isn't a fit for Little Learners \uD83C\uDF08 This corner is just for little ones, so I can only make gentle, age-appropriate picture games. Try something like animals, fruits, shapes, vehicles, or \u201Cthings on a farm!\u201D",
};
// Conservative client-side screen — catches blatant cases instantly (and saves an
// API call). Nuance, and uploaded images, are handled by the AI refusal contract.
const UNSAFE = [
  // sexual / exploitation
  /\bporn|p0rn|\bxxx\b|\bnsfw\b|hentai|\brape\b|child\s*(porn|sex|abuse)|\bcsam\b|sexually?\s+explicit|\bincest\b|bestiality|\bpedophil|molest/i,
  // making/getting weapons, explosives, or illegal drugs (intent lead-in + a specific illicit item)
  /(how\s+to|ways?\s+to|best\s+way\s+to|steps?\s+to|recipe\s+for|instructions?\s+(to|for)|guide\s+(to|for)|tutorial)\b.{0,30}\b(make|build|create|synthesi[sz]e|cook|manufacture|produce|brew|assemble|3d\s*print|get|buy|obtain|acquire)?\b.{0,24}\b(bomb|pipe\s*bomb|\bied\b|grenade|explosive\s*device|silencer|ghost\s*gun|machine\s*gun|automatic\s*weapon|meth|methamphetamine|cocaine|heroin|fentanyl|mdma|ecstasy|\blsd\b|nerve\s*agent|chemical\s*weapon|bio\s*weapon|sarin|ricin|anthrax)\b/i,
  // self-harm / suicide (direct intent — very low false-positive)
  /\b(kill|harm|hurt|cut)\s+(myself|yourself|himself|herself|themselves)\b|\bcommit\s+suicide\b|\bend\s+(my|your|his|her)\s+life\b|\btake\s+(my|your)\s+(own\s+)?life\b|\bhow\s+to\s+die\b|\b(suicide|self[-\s]?harm)\b.{0,24}(method|how|step|instruction|tip|way)/i,
  // mass-atrocity how-to
  /genocide.{0,20}(guide|how|step|instruction)|ethnic\s+cleansing.{0,20}(guide|how)/i,
  // slurs
  /\b(nigger|faggot|kike|spic|chink|tranny)\b/i,
];
const KID_UNSAFE = [
  /\b(sex|sexy|nude|naked|porn|drug|drunk|beer|wine|alcohol|cigarette|smoking|gun|knife|blade|blood|gore|kill|murder|war|weapon|gamble|gambling|casino|horror|zombie|demon|satan|hell|violence|violent|scary|nightmare|death|dead)\b/i,
];
function screenContent(text, kids) {
  const t = String(text || "");
  if (!t.trim()) return null;
  for (const re of UNSAFE) if (re.test(t)) return kids ? "kids" : "default";
  if (kids) for (const re of KID_UNSAFE) if (re.test(t)) return "kids";
  return null;
}
function parseRefusal(text) {
  if (!text) return null;
  const t = String(text);
  if (!/["']?refuse["']?\s*:\s*true/i.test(t)) return null;
  try { const s = t.indexOf("{"), e = t.lastIndexOf("}"); const o = JSON.parse(t.slice(s, e + 1)); if (o && o.refuse) return o.reason || "unsafe"; } catch {}
  return "unsafe";
}
function blockedError(kids) { const e = new Error(kids ? BLOCK_MSG.kids : BLOCK_MSG.default); e.blocked = true; return e; }

// Placeholder/filler detector — rejects questions where the AI used generic stand-ins
// like "Country A", "Person B", "Entity X", "Option A", standalone "A"/"B", etc.
// Conservative noun list to avoid false positives ("Group A of UEFA", "Plan A").
const PLACEHOLDER_NOUNS = "country|countries|person|people|entity|entities|place|places|city|cities|company|companies|option|choice|candidate|candidates|leader|leaders|organization|organizations|organisation|organisations";
const PLACEHOLDER_RE = new RegExp(`\\b(?:${PLACEHOLDER_NOUNS})\\s+[A-Z]\\b`, "i");
const STANDALONE_LETTER_RE = /^[A-Z]$/;
const STANDALONE_OPTION_RE = /^(option|choice)\s+[a-z0-9]$/i;
function hasPlaceholderText(s) {
  if (!s) return false;
  const t = String(s).trim();
  if (!t) return false;
  if (STANDALONE_LETTER_RE.test(t)) return true;
  if (STANDALONE_OPTION_RE.test(t)) return true;
  return PLACEHOLDER_RE.test(t);
}
function questionHasPlaceholder(q, opts) {
  if (hasPlaceholderText(q)) return true;
  for (const o of opts) { if (hasPlaceholderText(o)) return true; }
  return false;
}

function parseQuestions(text) {
  if (!text) return [];
  let t = String(text).trim().replace(/```json/gi, "").replace(/```/g, "").trim();
  const s = t.indexOf("["), e = t.lastIndexOf("]");
  if (s === -1 || e === -1 || e < s) return [];
  let arr; try { arr = JSON.parse(t.slice(s, e + 1)); } catch { return []; }
  if (!Array.isArray(arr)) return [];
  const out = [];
  for (const r of arr) {
    if (!r || typeof r.q !== "string" || !Array.isArray(r.options)) continue;
    let opts = r.options.map((o) => String(o)).filter(Boolean);
    if (opts.length < 4) continue;
    // Deduplicate options while preserving order
    const seenOpt = new Set();
    opts = opts.filter((o) => { const k = o.trim().toLowerCase(); if (seenOpt.has(k)) return false; seenOpt.add(k); return true; });
    if (opts.length < 4) continue;
    opts = opts.slice(0, 4);
    // Reject any question that uses placeholder fillers like "Country A", "Person B",
    // "Entity X", "Option A", or standalone letters — these are AI cop-outs and must
    // never reach users. Caller will fetch more to compensate.
    if (questionHasPlaceholder(r.q, opts)) continue;

    // `correct` can be number (single) or array (multi, max 2)
    let correct;
    let multi = false;
    if (Array.isArray(r.correct)) {
      const idxs = r.correct.map(Number).filter((n) => Number.isInteger(n) && n >= 0 && n < opts.length);
      const uniq = Array.from(new Set(idxs)).sort((a, b) => a - b);
      if (uniq.length === 1) {
        correct = uniq[0];
      } else if (uniq.length >= 2) {
        correct = uniq.slice(0, 2); // hard cap at 2 correct
        multi = true;
      } else {
        continue;
      }
    } else {
      const n = Number(r.correct);
      if (!Number.isInteger(n) || n < 0 || n >= opts.length) continue;
      correct = n;
    }

    // NEET V2 schema fields (preserved when present; renderer auto-handles them).
    // Sanitize SVG defensively even though the backend should already have done so.
    const NEET_QTYPES = ["mcq", "numerical", "diagram", "assertion_reason", "matching", "statement_based", "experimental_observation", "multiple_correct", "rich_text", "data_sufficiency", "quantitative_comparison", "reading_comprehension", "data_interpretation", "critical_reasoning", "sentence_equivalence", "text_completion", "writing_skill_mcq", "vocab_in_context", "scenario_based", "paragraph_based"];
    const NEET_SUBJECTS = ["Physics", "Chemistry", "Botany", "Zoology", "Biology"];
    const safeSvg = (s) => {
      if (typeof s !== "string") return undefined;
      const t = s.trim();
      if (!t.startsWith("<svg")) return undefined;
      // Forbid scripts, event handlers, external URLs, and foreign objects.
      if (/<script|<foreignObject|on[a-z]+\s*=|javascript:|<iframe|<object|<embed|<link|<meta/i.test(t)) return undefined;
      return t.length > 8000 ? undefined : t;  // size cap
    };
    const item = {
      q: r.q.trim(),
      options: opts,
      correct,
      multi,
      explanation: typeof r.explanation === "string" ? r.explanation.trim() : "",
      hint: typeof r.hint === "string" ? r.hint.trim() : "",
      difficulty: ["easy", "medium", "hard"].includes(r.difficulty) ? r.difficulty : "medium",
      style: typeof r.style === "string" ? r.style.trim() : "concept",
    };
    if (typeof r.questionType === "string" && NEET_QTYPES.includes(r.questionType)) item.questionType = r.questionType;
    if (typeof r.subject === "string" && NEET_SUBJECTS.includes(r.subject)) item.subject = r.subject;
    if (typeof r.chapter === "string" && r.chapter.trim()) item.chapter = r.chapter.trim().slice(0, 80);
    if (Array.isArray(r.latexBlocks)) {
      const lb = r.latexBlocks
        .filter((b) => b && typeof b === "object" && typeof b.code === "string" && b.code.length < 600)
        .slice(0, 6);
      if (lb.length) item.latexBlocks = lb;
    }
    const svg = safeSvg(r.svgCode);
    if (svg) item.svgCode = svg;
    if (r.diagramBlueprint && typeof r.diagramBlueprint === "object" && !Array.isArray(r.diagramBlueprint)) {
      const bp = r.diagramBlueprint;
      // Keep only known keys, drop anything else.
      const allowed = {};
      for (const k of ["kind", "title", "labels", "points", "xLabel", "yLabel"]) {
        if (k in bp) allowed[k] = bp[k];
      }
      if (Object.keys(allowed).length) item.diagramBlueprint = allowed;
    }
    out.push(item);
  }
  return out;
}

function correctIndexes(q) {
  // Returns a sorted array of correct indexes regardless of q.correct shape
  if (Array.isArray(q.correct)) return [...q.correct].sort((a, b) => a - b);
  return typeof q.correct === "number" ? [q.correct] : [];
}

function isOptionCorrect(q, optionIndex) {
  if (Array.isArray(q.correct)) return q.correct.includes(optionIndex);
  return q.correct === optionIndex;
}

function checkMultiCorrect(q, selectedActualIndexes) {
  // selectedActualIndexes is an array of actual option indexes (not display order)
  const want = correctIndexes(q);
  if (selectedActualIndexes.length !== want.length) return false;
  const sel = [...selectedActualIndexes].sort((a, b) => a - b);
  return sel.every((v, i) => v === want[i]);
}

function rampDifficulty(cfg, made, total) {
  if (cfg.difficulty === "progressive") { const r = total ? made / total : 0; return r < 0.34 ? "easy" : r < 0.67 ? "medium" : "hard"; }
  if (cfg.difficulty === "random") return "varied";
  return cfg.difficulty;
}

function CostPreview({ questions, extractedChars, fileInventory, sourceType }) {
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Debounced fetch when any input changes
  useEffect(() => {
    let cancelled = false;
    if (!questions || questions < 1) { setQuote(null); return; }
    const t = setTimeout(async () => {
      setLoading(true); setErr("");
      try {
        const inv = fileInventory || {};
        const chars = Math.max(0, parseInt(extractedChars || 0, 10) || 0);
        const d = await backend.creditsQuote(null, null, questions, inv, chars);
        if (!cancelled) setQuote(d);
      } catch (e) {
        if (!cancelled) { setErr(e.message || "Couldn't get a cost preview."); setQuote(null); }
      } finally { if (!cancelled) setLoading(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [questions, extractedChars, JSON.stringify(fileInventory || {}), sourceType]);

  if (!quote && !loading && !err) return null;
  return (
    <div className="card" data-testid="cost-preview" style={{ marginTop: 16, borderLeft: "3px solid var(--accent2)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
          <Zap size={16} color="var(--accent2)" /> Cost preview
        </div>
        {quote && <span className="tag" style={{ color: "var(--accent2)" }}>{quote.tier} tier · {quote.model}</span>}
      </div>
      {loading && <p className="qz-muted" style={{ fontSize: 12.5 }}>Calculating…</p>}
      {err && <p style={{ color: "var(--wrong)", fontSize: 12.5 }}>{err}</p>}
      {quote && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13.5 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span className="qz-muted">Questions ({quote.questions} × {quote.tier_cost})</span>
            <b>{quote.question_credits}</b>
          </div>
          {quote.document_credits > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between" }} data-testid="cost-preview-document">
              <span className="qz-muted">
                Document ({(quote.extracted_chars || 0).toLocaleString()} chars · {quote.document_breakdown?.tier})
              </span>
              <b>{quote.document_credits}</b>
            </div>
          )}
          <div style={{ height: 1, background: "var(--border)", margin: "6px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800 }} data-testid="cost-preview-total">
            <span>Total cost</span>
            <span style={{ color: quote.affordable ? "var(--accent2)" : "var(--wrong)" }}>{quote.total} credits</span>
          </div>
          <div className="qz-muted" style={{ fontSize: 12, marginTop: 2 }}>
            Wallet balance: <b>{quote.unlimited ? "∞" : quote.balance}</b>
            {!quote.unlimited && !quote.affordable && (
              <span style={{ color: "var(--wrong)", marginLeft: 6 }} data-testid="cost-preview-shortfall">
                · need {quote.shortfall} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function buildGenRequest(cfg, need, existing, diff) {
  const cat = cfg.category || "General Knowledge";
  // Map our setup difficulty (PDF §1.1) into prompt language.
  // Mixed default per PDF: Easy 30% / Medium 40% / Hard 30% (no Professional tier).
  // Progressive: first third Easy, middle third Medium, final third Hard.
  let diffLine;
  if (diff === "varied" || diff === "mixed" || diff === "random") diffLine = "Mixed: ~30% easy, 40% medium, 30% hard";
  else if (diff === "progressive") diffLine = "Progressive: split the set into thirds — first third Easy, middle third Medium, final third Hard";
  else diffLine = diff;
  const styleLine = "Vary styles across: concept-based, direct fact, reasoning, analytical, scenario/case-based, and exam-pattern.";
  const avoid = existing.length ? `Do NOT repeat or closely paraphrase: ${existing.slice(-14).join(" | ")}.` : "Do not repeat questions.";
  const isMixed = cfg.questionType === "mixed";
  const schema = isMixed
    ? 'Each item keys EXACTLY: {"q":string,"options":[string,string,string,string],"correct":either integer 0-3 (single-correct) OR array of 2 distinct integers 0-3 (multi-correct, never more than 2 correct answers),"explanation":string (1-2 sentences),"hint":string (short),"difficulty":"easy"|"medium"|"hard","style":string,"subtopic":string}.'
    : 'Each item keys EXACTLY: {"q":string,"options":[string,string,string,string],"correct":integer 0-3 (index of the right option),"explanation":string (1-2 sentences),"hint":string (short),"difficulty":"easy"|"medium"|"hard","style":string,"subtopic":string}.';
  const mixGuide = isMixed
    ? " Mix is allowed: roughly 25-40% of questions should have exactly 2 correct answers (use an array for `correct` like [0,2]). Never produce more than 2 correct answers."
    : "";
  // Category-specific guardrails (so 'General Knowledge' isn't accidentally a Current Affairs quiz)
  const isGK = /general knowledge/i.test(cat);
  const isCA = /current affairs|news|market|weather|elections|recent (tech|ai)/i.test(cat);
  const isHist = /history/i.test(cat);
  let catLine = "";
  if (isGK) catLine = " Generate a BALANCED mix across history, geography, science, sports, culture, miscellaneous knowledge, with current-affairs as AT MOST one component (≤15%). Do not turn this into a current-affairs quiz.";
  else if (isCA) catLine = " 80-100% of items must be current/recent (last 30-90 days where possible). Use the RECENT-INFORMATION CONTEXT provided in the system message as the source of truth.";
  else if (isHist) catLine = " Generate primarily historical questions; do NOT replace with current affairs unless explicitly requested.";
  const eduLine = "";
  const sys = `You are Quizkosh, an elite multiple-choice question author for the exam/category: ${cat}.${eduLine}${catLine} Write rigorous, unambiguous questions. ${isMixed ? "Single-correct items must have exactly ONE correct answer; multi-correct items must have EXACTLY 2 correct answers." : "Each question must have exactly ONE correct answer and three plausible distractors."}${mixGuide} Distractors must be realistic, relevant, and clearly wrong on close reading. Validate every correct answer factually before emitting. Never duplicate options within a question and never repeat questions. Respond with a single minified JSON array and NOTHING else — no prose, no markdown, no code fences. SAFETY: Legitimate academic topics are fine even with mature themes. But for sexual/pornographic, gratuitously violent, hateful, self-harm, or weapons/drugs instructions — DO NOT write questions and respond with EXACTLY {"refuse":true,"reason":"short reason"}.`;
  let messages;
  if (cfg.sourceType === "image" && cfg.imageData) {
    const userText = `Create ${need} NEW multiple-choice questions for "${cat}" based on the attached image content.\nDifficulty: ${diffLine}. ${styleLine}\n${avoid}\n${schema}\nReturn ONLY the JSON array.`;
    messages = [{ role: "user", content: [
      { type: "image", source: { type: "base64", media_type: cfg.imageMime || "image/png", data: cfg.imageData } },
      { type: "text", text: userText },
    ]}];
  } else {
    const src = (cfg.sourceText || "").slice(0, 3500);
    const basis = src ? `Base the questions ONLY on this material:\n"""${src}"""` : `Topic / prompt: ${cfg.topic || cat}. Use your knowledge of this topic.`;
    const userText = `Create ${need} NEW multiple-choice questions for "${cat}".\n${basis}\nDifficulty: ${diffLine}. ${styleLine}\n${avoid}\n${schema}\nReturn ONLY the JSON array.`;
    messages = [{ role: "user", content: userText }];
  }
  return { messages, sys };
}

async function generateQuiz(cfg, onProgress) {
  const target = Math.max(1, cfg.count);
  // ── Replay-with-same-setup support (Feb 2026, additive only) ─────────
  // If `cfg.priorQuestions` is a non-empty array of question-text strings
  // from a previous run, we (a) pre-seed the `seen` set so any AI-emitted
  // duplicates are dropped client-side and (b) prepend the texts to the
  // first-batch "avoid" list passed to the prompt so the AI tries not to
  // repeat them. This is purely additive — when `priorQuestions` is
  // unset (the normal "New quiz" path), behaviour is unchanged.
  const priorQs = Array.isArray(cfg.priorQuestions)
    ? cfg.priorQuestions.filter((s) => typeof s === "string" && s.trim()).map((s) => String(s))
    : [];
  // Try the question bank first (zero AI cost). Only when the topic is a topic-based
  // run on a category we have banked, AND there's no user-provided source material.
  const isTopicRun = (!cfg.sourceType || cfg.sourceType === "topic") && !cfg.sourceText && !cfg.sourceImage;
  if (isTopicRun && cfg.category) {
    try {
      const d = await backend.bankSample(cfg.category, target);
      // Strip any banked rows that contain placeholder fillers — defensive only.
      let bankClean = (d && Array.isArray(d.questions)) ? d.questions.filter((q) => !questionHasPlaceholder(q.q, q.options || [])) : [];
      // Replay mode: drop bank rows that the user has just seen so we
      // truly serve a "fresh" quiz when the bank can satisfy the request.
      if (priorQs.length > 0 && bankClean.length > 0) {
        const priorSet = new Set(priorQs.map(norm));
        bankClean = bankClean.filter((q) => !priorSet.has(norm(q.q)));
      }
      if (bankClean.length >= target) {
        onProgress && onProgress(target, target);
        return bankClean.slice(0, target).map((q) => ({ ...q, _source: "bank", _order: shuffle([0, 1, 2, 3]) }));
      }
    } catch {}
  }
  const collected = [], seen = new Set();
  // Pre-seed seen-set with previous-quiz questions so any AI repeats are
  // silently dropped on the client side too (belt-and-braces).
  for (const pq of priorQs) seen.add(norm(pq));
  const perCall = 5; let attempts = 0; const maxAttempts = Math.ceil(target / perCall) + Math.max(3, Math.ceil(target / 10));
  while (collected.length < target && attempts < maxAttempts) {
    attempts++;
    const need = Math.min(perCall, target - collected.length) + 1;
    const diff = rampDifficulty(cfg, collected.length, target);
    // On the FIRST batch include the prior-quiz texts in the "avoid" list
    // so the prompt explicitly steers away from them; on follow-up batches
    // we keep the original behaviour (only the in-run collected items)
    // to preserve token budget. Prior questions are appended LAST so the
    // existing `.slice(-14)` truncation in buildGenRequest keeps them
    // (the most recent items in the slice).
    const avoidList = (attempts === 1 && priorQs.length > 0)
      ? [...collected.map((q) => q.q), ...priorQs]
      : collected.map((q) => q.q);
    const { messages, sys } = buildGenRequest(cfg, need, avoidList, diff);
    let text;
    try {
      // Per-batch cost preview: only the *first* batch carries the file inventory
      // (the AI cost for follow-ups should only count question credits).
      const extras = {
        questions_planned: need,
        // Char-based billing: only the *first* batch carries chars + legacy
        // inventory (the AI cost for follow-ups should only count question
        // credits — the document was already paid for in batch 1).
        extracted_chars: attempts === 1 ? (cfg.extractedChars || 0) : 0,
        file_inventory: attempts === 1 ? (cfg.fileInventory || {}) : {},
        category: cfg.category || "",
        topic: cfg.topic || "",
        // Phase-1 forward-compat (server schema accepts these; no logic yet)
        exam_code: cfg.examCode || examCodeForLabel(cfg.category) || null,
        source_mode: cfg.sourceType || SOURCE_MODES.TOPIC,
      };
      text = await callClaude(messages, sys, extras);
    } catch (err) { if (collected.length > 0) break; throw err; }
    if (parseRefusal(text)) throw blockedError(false);
    for (const q of parseQuestions(text)) {
      const k = norm(q.q);
      if (!seen.has(k)) { seen.add(k); collected.push(q); }
      if (collected.length >= target) break;
    }
    onProgress && onProgress(Math.min(collected.length, target), target);
  }
  if (collected.length === 0) throw new Error("No questions returned. Try a clearer topic or fewer questions.");
  return collected.slice(0, target).map((q) => ({ ...q, _source: "ai", _order: shuffle([0, 1, 2, 3]) }));
}

/* --------------------------- SOUND + VOICE ------------------------------- */
let _audio;
function beep(kind, on) {
  if (!on) return;
  try {
    _audio = _audio || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audio, now = ctx.currentTime;
    const seq = kind === "correct" ? [[660, 0], [880, .09]] : kind === "wrong" ? [[300, 0], [200, .12]] : [[520, 0]];
    seq.forEach(([f, t]) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = kind === "wrong" ? "sawtooth" : "sine"; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, now + t); g.gain.exponentialRampToValueAtTime(0.18, now + t + .02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t + .22);
      o.connect(g); g.connect(ctx.destination); o.start(now + t); o.stop(now + t + .24);
    });
  } catch {}
}
function speak(text, on) {
  if (!on || !("speechSynthesis" in window)) return;
  try { window.speechSynthesis.cancel(); const u = new SpeechSynthesisUtterance(text); window.speechSynthesis.speak(u); } catch {}
}

/* ------------------------------ COMPONENTS ------------------------------- */
function TimerRing({ remaining, total }) {
  const r = 28, c = 2 * Math.PI * r;
  const frac = total > 0 ? Math.max(0, remaining / total) : 0;
  const danger = frac < 0.3;
  return (
    <div className="ring-wrap">
      <svg width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke="var(--surface2)" strokeWidth="5" />
        <circle cx="32" cy="32" r={r} fill="none" stroke={danger ? "var(--wrong)" : "var(--accent2)"} strokeWidth="5"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - frac)} transform="rotate(-90 32 32)"
          style={{ transition: "stroke-dashoffset .15s linear" }} />
      </svg>
      <div className="t" style={{ color: danger ? "var(--wrong)" : "var(--text)" }}>{Math.ceil(remaining / 1000)}</div>
    </div>
  );
}

function TopBar({ theme, setTheme, sound, setSound, voice, setVoice, onHome, showHome, onAuth, user, onLogout, quota, onTeacher, onManageLogo, onBuyCredits }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="qz-top">
      <div className="qz-brand-wrap">
        <div className="qz-logo" onClick={onHome} data-testid="brand-logo">
          <img src="/quizkosh-logo.jpg" alt="Quizkosh" className="qz-logoimg" />
        </div>
        {user && user.school_logo_data && (
          <>
            <span className="qz-logo-sep" aria-hidden="true" />
            <div className="qz-school-logo" data-testid="school-logo" title={user.school_name || "School logo"}>
              <img src={`data:${user.school_logo_mime || "image/png"};base64,${user.school_logo_data}`} alt={user.school_name ? `${user.school_name} logo` : "School logo"} />
            </div>
          </>
        )}
      </div>
      <div className="qz-toolbtns">
        {showHome && <button className="btn btn--icon" title="Home" onClick={onHome} data-testid="home-btn"><Home size={18} /></button>}
        <div style={{ position: "relative" }}>
          <button className="btn btn--icon" title="Theme" onClick={() => setOpen((o) => !o)} data-testid="theme-btn"><Layers size={18} /></button>
          {open && (
            <div className="theme-menu pop" onMouseLeave={() => setOpen(false)}>
              <div className="hd">Theme</div>
              {THEMES.map((t) => (
                <button key={t.id} className={"titem" + (theme === t.id ? " sel" : "")} onClick={() => { setTheme(t.id); setOpen(false); }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="sw" style={{ background: t.sw }} />
                    <span style={{ fontWeight: 800, fontSize: 14 }}>{t.name}</span>
                  </span>
                  <span className="tag">{t.hint}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {user ? (
          <>
            {(user.user_type === "school" || user.role === "admin") && (
              <button data-testid="teacher-fab-btn" className="btn btn--primary authbtn" style={{ padding: "10px 14px", background: "linear-gradient(135deg, #f48626, #ff6b35)" }} title="Build exam papers from study material" onClick={onTeacher}><Wand2 size={16} /> Teachers Section – Build Question Paper</button>
            )}
            {quota && (
              quota.unlimited ? (
                <span className="chip" data-testid="quota-chip" title="Admins have unlimited AI quota">
                  <Zap size={14} color="var(--gold)" /> <b style={{ marginLeft: 4 }}>Unlimited</b>
                </span>
              ) : (
                <span
                  className="chip"
                  data-testid="quota-chip"
                  title={`Total ${quota.balance ?? quota.credits ?? 0} credits available — Free today: ${quota.free_credits ?? 0} · Purchased: ${quota.purchased_credits ?? 0}. ${quota.daily_free_grant ?? 40} free credits are refreshed every day; purchased credits never expire.`}
                  style={{ borderColor: (quota.balance ?? quota.credits ?? 0) <= 5 ? "var(--wrong)" : ((quota.balance ?? quota.credits ?? 0) <= 15 ? "var(--gold)" : "var(--border)") }}
                >
                  <Zap size={14} color={(quota.balance ?? quota.credits ?? 0) <= 5 ? "var(--wrong)" : "var(--accent2)"} />
                  <b className="qz-mono" style={{ marginLeft: 4 }}>{quota.balance ?? quota.credits ?? 0}</b>
                  <span className="qz-muted" style={{ marginLeft: 4, fontSize: 11 }}>credits</span>
                  {(quota.free_credits != null || quota.purchased_credits != null) && (
                    <span className="qz-muted" data-testid="quota-breakdown" style={{ marginLeft: 6, fontSize: 10.5, opacity: 0.85 }}>
                      ({quota.free_credits ?? 0} free + {quota.purchased_credits ?? 0} paid)
                    </span>
                  )}
                </span>
              )
            )}
            {onBuyCredits && !(quota && quota.unlimited) && (
              <button data-testid="buy-credits-btn" className="btn" style={{ padding: "6px 12px", fontSize: 12, background: "linear-gradient(135deg, var(--accent2), var(--accent3))", color: "#0b0f17", fontWeight: 700, border: "none" }} onClick={onBuyCredits} title="Buy more credits"><Plus size={13} /> Buy credits</button>
            )}
            <span className="chip" data-testid="user-chip" title={user.email}>
              {user.role === "admin" ? <ShieldCheck size={14} color="var(--accent2)" /> : <Sparkles size={14} />}
              <b style={{ marginLeft: 4 }}>{user.name}</b>
              {user.role === "admin" && <span className="tag" style={{ marginLeft: 6 }}>admin</span>}
            </span>
            {(user.user_type === "school" || user.role === "admin") && (
              <button data-testid="school-logo-manage-btn" className="btn btn--icon" title="Manage school logo" onClick={onManageLogo} style={{ width: 38, height: 38 }}><Upload size={16} /></button>
            )}
            <button data-testid="logout-btn" className="btn btn--ghost authbtn" style={{ padding: "10px 13px" }} onClick={onLogout}>Log out</button>
          </>
        ) : (
          <>
            <div className="tip">
              <button data-testid="topbar-login-btn" className="btn btn--ghost authbtn" style={{ padding: "10px 13px" }} onClick={() => onAuth && onAuth("login")}>Log in</button>
              <span className="tip__b">Welcome back! Sign in to continue.</span>
            </div>
            <div className="tip">
              <button data-testid="topbar-signup-btn" className="btn btn--primary authbtn" style={{ padding: "10px 14px" }} onClick={() => onAuth && onAuth("signup")}>Sign up</button>
              <span className="tip__b">Create a free account and generate quizzes from any topic or document.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ChallengeBanner({ challenge, onAccept, onDismiss }) {
  if (!challenge) return null;
  return (
    <div className="qz-wrap" style={{ paddingTop: 0 }} data-testid="challenge-banner">
      <div className="card glow pop" style={{ padding: 20, borderLeft: "4px solid var(--gold)", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <Crown size={28} color="var(--gold)" />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="qz-eyebrow" style={{ marginBottom: 4 }}>You've been challenged</div>
          <div className="qz-display" style={{ fontSize: 20, lineHeight: 1.2 }}>
            {challenge.challenger_name} scored <span style={{ color: "var(--accent)" }}>{challenge.accuracy}%</span> on {challenge.category}
          </div>
          <p className="qz-muted" style={{ fontSize: 13.5, marginTop: 4 }}>{challenge.correct}/{challenge.total} correct · score {challenge.score} · best streak {challenge.best_streak}. Same questions — beat them!</p>
        </div>
        <button data-testid="accept-challenge-btn" className="btn btn--primary btn--lg" onClick={onAccept}><Play size={18} /> Take the challenge</button>
        <button data-testid="dismiss-challenge-btn" className="btn btn--ghost" onClick={onDismiss} aria-label="Dismiss"><X size={18} /></button>
      </div>
    </div>
  );
}

// ─── Promotional bonus components (Feb 2026, additive only) ──────────────
// Renders the locally-saved /Offers.png mascot. The image lives in
// /app/frontend/public/Offers.png so it is served as a same-origin static
// asset — no CDN, no external URL, fully offline-capable.
function PromoMascot() {
  return (
    <div style={{ display: "block", maxWidth: 320, margin: "0 auto" }}>
      <img
        src="/Offers.png"
        alt="Quizkosh promotional offer mascot"
        loading="lazy"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          // Cap the image so it never dominates the viewport on short
          // screens (laptops in landscape, phones, etc.) — the popup body
          // below must always remain visible without scrolling.
          maxHeight: "min(38vh, 240px)",
          objectFit: "contain",
          borderRadius: 12,
        }}
        data-testid="promo-mascot-img"
      />
    </div>
  );
}

// PromoModal — all visible text comes from the backend `/promotions/active`
// payload. If the API call fails or the promo is inactive we render nothing.
// The component never depends on any hard-coded title / description / pct.
function PromoModal({ promo, onClose, onBuyCredits }) {
  const [claimed, setClaimed] = useState(false);
  if (!promo) return null;
  return (
    <div className="ovl" onClick={onClose} data-testid="promo-modal">
      <div
        className="modal pop"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 440,
          padding: 0,
          // Allow inner content to scroll if the viewport is too short
          // (was previously `overflow: hidden`, which clipped the body).
          // `.modal`'s own `max-height: 88vh` keeps the popup contained.
          overflow: "auto",
          position: "relative",
        }}
      >
        <button data-testid="promo-modal-close" className="btn btn--icon" onClick={onClose} title="Close" style={{ position: "absolute", top: 12, right: 12, zIndex: 4, width: 34, height: 34, background: "rgba(0,0,0,.28)", color: "#fff", border: "none" }}><X size={18} /></button>
        <div style={{ background: "linear-gradient(135deg,var(--b1),var(--b2) 60%,var(--b3))", padding: "20px 18px 8px" }}>
          <PromoMascot />
        </div>
        <div style={{ padding: "6px 26px 26px", textAlign: "center" }}>
          {!claimed ? (
            <>
              {promo.subtitle && (
                <span className="tag" style={{ color: "var(--gold)", borderColor: "var(--gold)" }}>
                  <PartyPopper size={13} /> {promo.subtitle}
                </span>
              )}
              <h2 className="qz-display" data-testid="promo-modal-title" style={{ fontSize: 27, marginTop: 12, lineHeight: 1.1 }}>
                {promo.title || `Get ${promo.bonus_percentage || 0}% more credits`}
              </h2>
              {promo.description && (
                <p className="qz-muted" data-testid="promo-modal-description" style={{ marginTop: 10, lineHeight: 1.55, fontSize: 14.5 }}>
                  {promo.description}
                </p>
              )}
              <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20, flexWrap: "wrap" }}>
                <button data-testid="promo-modal-cta" className="btn btn--primary btn--lg" onClick={() => setClaimed(true)}>
                  <Sparkles size={18} /> {promo.button_text || "Avail now"}
                </button>
                <button className="btn btn--ghost" onClick={onClose}>Maybe later</button>
              </div>
              <p className="qz-muted" style={{ fontSize: 11.5, marginTop: 14 }}>Offer ends soon · bonus added instantly</p>
            </>
          ) : (
            <div className="pop">
              <PartyPopper size={40} color="var(--gold)" />
              <h2 className="qz-display" style={{ fontSize: 25, marginTop: 10 }}>You&apos;re in!</h2>
              <p className="qz-muted" data-testid="promo-modal-success" style={{ marginTop: 8, lineHeight: 1.55, fontSize: 14.5 }}>
                {promo.success_message || `Your +${promo.bonus_percentage || 0}% credits bonus is unlocked for your next top-up.`}
              </p>
              <button className="btn btn--primary btn--lg" style={{ marginTop: 18 }} data-testid="promo-modal-lets-go" onClick={() => {
                // Close the promo popup, then open the existing Buy Credits
                // modal (reuses the app's normal top-up flow — no changes
                // to that UI). Falls back to onClose if the parent didn't
                // wire up the handler (e.g. admin preview).
                onClose && onClose();
                if (onBuyCredits) onBuyCredits();
              }}>
                <ArrowRight size={18} /> Let&apos;s go
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function Landing({ onStart, onDaily, onKids, onPolicy, access, onAdmin, onRequest, onContact, onFeedback, onPromo, promo }) {
  return (
    <div className="qz-wrap">
      <div style={{ textAlign: "center", paddingTop: 40 }}>
        {onPromo && promo && promo.active && (
          <button
            data-testid="promo-banner-btn"
            onClick={onPromo}
            className="pop"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "7px 15px", borderRadius: 99,
              border: "1px solid var(--gold)",
              background: "linear-gradient(90deg,var(--b3),var(--b1))",
              color: "var(--text)", fontWeight: 700, fontSize: 13,
              cursor: "pointer", marginBottom: 18,
            }}
          >
            <PartyPopper size={15} color="var(--gold)" /> {promo.subtitle || "Limited time"}:{" "}
            <b style={{ color: "var(--accent)" }}>+{promo.bonus_percentage}% credits</b>{" "}
            <ArrowRight size={14} />
          </button>
        )}
        <div className="qz-eyebrow fadeUp">AI quiz studio · any subject · any exam</div>
        <h1 className="qz-display hero fadeUp" style={{ fontSize: 74, marginTop: 16, animationDelay: ".05s" }}>
          Turn anything<br />into a <span style={{ background: "linear-gradient(90deg,var(--accent),var(--accent3))", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>quiz</span>.
        </h1>
        <p className="qz-muted fadeUp" style={{ fontSize: 18, maxWidth: 560, margin: "20px auto 0", lineHeight: 1.5, animationDelay: ".1s" }}>
          Type a topic, paste your notes, or snap a photo of a slide. The AI writes a fresh, exam-accurate quiz in seconds — then coaches you with instant explanations and analytics.
        </p>
        <div className="fadeUp" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginTop: 30, animationDelay: ".14s" }}>
          <button className="btn btn--primary btn--lg" onClick={onStart}><Wand2 size={20} /> Build my quiz</button>
          <button className="btn btn--primary btn--lg" onClick={onDaily}><Flame size={20} /> Daily challenge</button>
        </div>
        <div className="fadeUp" style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginTop: 22, animationDelay: ".18s" }}>
          {["Topic, text, files & images", "Latest exam patterns", "Instant explanations"].map((t) => (
            <span key={t} className="chip"><Check size={14} color="var(--correct)" /> {t}</span>
          ))}
        </div>

        {(!access || access.kids !== false) && (
          <button className="btn btn--lg fadeUp" onClick={onKids}
            style={{ marginTop: 20, animationDelay: ".22s", border: "none", color: "#3a1430", fontWeight: 800,
              background: "linear-gradient(120deg,#ff9ec7,#ffd166 55%,#7ee8fa)", boxShadow: "0 14px 36px -10px rgba(255,158,199,.6)" }}>
            <Baby size={20} /> Little learners · ages 3–5 · tap-the-picture games
          </button>
        )}
      </div>

      <div className="grid g3 fadeUp" style={{ marginTop: 52, animationDelay: ".22s" }}>
        {[
          { ic: Layers, t: "Any source", d: "Topic, pasted text, files or images — the AI reads and understands the material." },
          { ic: Brain, t: "Real questions", d: "Concept, reasoning, analytical and scenario styles — tuned to your exam, not just trivia." },
          { ic: Target, t: "Coaching built in", d: "Per-quiz analytics, a breakdown of what you missed, and one-tap retry of wrong answers." },
        ].map((f, i) => (
          <div className="card floaty" key={i} style={{ animationDelay: i * 0.6 + "s" }}>
            <div className="tile" style={{ display: "inline-grid", placeItems: "center", width: 48, height: 48, padding: 0, marginBottom: 12, pointerEvents: "none" }}><f.ic size={22} color="var(--accent)" /></div>
            <h3 className="qz-display" style={{ fontSize: 19, marginBottom: 6 }}>{f.t}</h3>
            <p className="qz-muted" style={{ fontSize: 14.5, lineHeight: 1.5 }}>{f.d}</p>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginTop: 44, textAlign: "center", padding: 30 }}>
        <h3 className="qz-display" style={{ fontSize: 24 }}>Ready when you are.</h3>
        <p className="qz-muted" style={{ margin: "8px auto 18px", maxWidth: 460 }}>Primary school to PMP, NEET to UPSC. Pick your exam, pick your source, and play.</p>
        <button className="btn btn--primary btn--lg" onClick={onStart}><Rocket size={20} /> Start now</button>
      </div>

      <Footer onPolicy={onPolicy} onAdmin={onAdmin} onRequest={onRequest} onContact={onContact} onFeedback={onFeedback} />
    </div>
  );
}

function Setup({ onComplete, initialName, access, presets }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState(initialName || "");
  const [category, setCategory] = useState("");
  const [customCat, setCustomCat] = useState("");
  const [sourceType, setSourceType] = useState("topic");
  const [topic, setTopic] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [pasteFetching, setPasteFetching] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");
  const [pasteFromUrl, setPasteFromUrl] = useState(false);
  const [fileText, setFileText] = useState(""); const [fileName, setFileName] = useState(""); const [fileBusy, setFileBusy] = useState(false);
  const [filePages, setFilePages] = useState(0); // number of PDF pages, when source is a PDF
  const [fileKind, setFileKind] = useState("");  // pdf | word | excel | powerpoint | text
  const [imageData, setImageData] = useState(""); const [imageMime, setImageMime] = useState(""); const [imageName, setImageName] = useState("");
  const [fileErr, setFileErr] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [count, setCount] = useState(5);
  const [timer, setTimer] = useState(15);
  const [questionType, setQuestionType] = useState("single"); // single | mixed
  const [customRule, setCustomRule] = useState({ method: "weighted", perCorrect: 4, perWrong: -1, perBlank: 0, min: 0, max: 100, pass: 40, passPct: 50 });
  const [mode, setMode] = useState("classic");
  const countOpts = mode === "multiplayer" ? [10, 20, 30, 40, 50] : [5, 10, 25, 50, 100];
  const MAX_COUNT = 200;
  const MIN_COUNT = 1;
  useEffect(() => {
    // Clamp count if user switches to multiplayer with too-large value
    if (mode === "multiplayer" && (typeof count !== "number" || count > 50)) setCount(20);
    // eslint-disable-next-line
  }, [mode]);
  useEffect(() => {
    if (step === 2 && category === "__custom__" && customCat.trim() && !topic.trim()) setTopic(customCat.trim());
    // eslint-disable-next-line
  }, [step]);
  useEffect(() => {
    if (access && access[mode] === false) setMode("classic");
    // eslint-disable-next-line
  }, [access, mode]);

  const steps = ["You", "Subject", "Source", "Play"];
  const sourceReady = sourceType === "topic" ? topic.trim().length > 1
    : sourceType === "paste" ? pasteText.trim().length > 20
    : sourceType === "file" ? fileText.trim().length > 20
    : sourceType === "image" ? !!imageData
    : sourceType === "exam_intel" ? true   // exam-driven: no user content required
    : false;

  // Character count for character-based credit billing (Feb 2026 refactor).
  // For files (PDF/DOC/TXT) we use the extracted `fileText`. For paste mode
  // we count the typed text. For image uploads, the backend will OCR the
  // bytes; until then we send 0 and the backend will fall back gracefully.
  // `file_inventory` is kept only for backward compat with any older API
  // consumers — the server ignores it for billing.
  const extractedChars = (() => {
    if (sourceType === "file") return (fileText || "").length;
    if (sourceType === "paste") return (pasteText || "").length;
    // image sourceType: OCR happens server-side / via vision — we don't
    // have the char count yet on the client, so report 0 here. The
    // backend will record actual chars when it processes the image.
    return 0;
  })();
  const fileInventory = (() => {
    const inv = { pdf_pages: 0, images: 0, word: 0, excel: 0, powerpoint: 0, text: 0 };
    if (sourceType === "image" && imageData) inv.images = 1;
    if (sourceType === "file") {
      if (fileKind === "pdf") inv.pdf_pages = filePages || 1;
      else if (fileKind === "word") inv.word = 1;
      else if (fileKind === "excel") inv.excel = 1;
      else if (fileKind === "powerpoint") inv.powerpoint = 1;
      else if (fileKind === "text") inv.text = 1;
    }
    if (sourceType === "paste" && pasteText.trim().length > 200) inv.text = 1;
    return inv;
  })();

  async function readFile(e) {
    const f = e.target.files?.[0]; if (!f) return; setFileErr("");
    if (f.size > 10 * 1024 * 1024) { setFileErr("Please use a file under 10 MB for the demo."); return; }
    setFileBusy(true);
    setFilePages(0); setFileKind("");
    try {
      const text = await extractFileText(f);
      if (!text || text.trim().length < 1) throw new Error("That file looks empty — try another.");
      setFileText(text); setFileName(f.name);
      const name = (f.name || "").toLowerCase();
      const type = (f.type || "").toLowerCase();
      if (type === "application/pdf" || name.endsWith(".pdf")) {
        setFileKind("pdf");
        // Best-effort page count via PDF.js (loadPdfJs is already cached after extract)
        try {
          const pdfjs = await loadPdfJs();
          const buf = await f.arrayBuffer();
          const pdf = await pdfjs.getDocument({ data: buf }).promise;
          setFilePages(pdf.numPages || 1);
        } catch { setFilePages(1); }
      } else if (name.match(/\.(docx?|odt)$/i)) {
        setFileKind("word"); setFilePages(0);
      } else if (name.match(/\.(xlsx?|csv)$/i)) {
        setFileKind("excel"); setFilePages(0);
      } else if (name.match(/\.(pptx?)$/i)) {
        setFileKind("powerpoint"); setFilePages(0);
      } else {
        setFileKind("text"); setFilePages(0);
      }
    } catch (err) {
      setFileText(""); setFileName(""); setFilePages(0); setFileKind("");
      setFileErr(err && err.message ? err.message : "Could not read that file.");
    } finally { setFileBusy(false); }
  }
  function readImage(e) {
    const f = e.target.files?.[0]; if (!f) return; setFileErr("");
    const ok = /^image\/(png|jpe?g|gif|webp)$/i.test(f.type);
    if (!ok) { setFileErr("Use a PNG, JPEG, GIF, or WEBP image."); return; }
    if (f.size > 4 * 1024 * 1024) { setFileErr("Please use an image under 4 MB."); return; }
    if (f.size < 2 * 1024) { setFileErr("That image is too small to read. Try a clearer photo (at least a few KB)."); return; }
    const rd = new FileReader(); rd.onload = () => { const d = String(rd.result || ""); setImageData(d.split(",")[1] || ""); setImageMime(f.type); setImageName(f.name); }; rd.readAsDataURL(f);
  }

  function buildScoreRule() {
    if (category !== "__custom__" || customRule.method === "weighted") return null;
    const n = (v, d) => (v === "" || isNaN(Number(v)) ? d : Number(v));    const m = customRule.method;
    if (m === "negative") return { method: "negative", perCorrect: n(customRule.perCorrect, 1), perWrong: n(customRule.perWrong, 0), perBlank: n(customRule.perBlank, 0) };
    if (m === "scaled") return { method: "scaled", min: n(customRule.min, 0), max: n(customRule.max, 100), pass: n(customRule.pass, 0) };
    if (m === "passfail") return { method: "proficiency", tiers: [[n(customRule.passPct, 50), "Pass"], [0, "Fail"]] };
    return null;
  }
  function finish() {
    const cat = category === "__custom__" ? (customCat.trim() || topic.trim() || "Custom Quiz") : category;
    // Exam Intelligence Mode (Phase 1): no user content. Fall back to the
    // selected exam/category as the topic so the legacy pipeline has something
    // to work with — preserves current behavior for every exam.
    const effectiveTopic = sourceType === "exam_intel"
      ? (topic.trim() || cat)
      : (sourceType === "paste" && pasteFromUrl && pasteUrl) ? `(source URL: ${pasteUrl})` : topic.trim();
    onComplete({
      name: name.trim() || "You", category: cat, sourceType,
      topic: effectiveTopic,
      sourceText: sourceType === "paste" ? pasteText : sourceType === "file" ? fileText : "",
      sourceUrl: pasteFromUrl ? pasteUrl : "",
      imageData, imageMime, difficulty, count, timer, mode, questionType,
      fileInventory, extractedChars,
      // Phase-1 forward-compat (consumed by exam services + backend log only)
      examCode: examCodeForLabel(cat),
      custom: category === "__custom__", scoreRule: buildScoreRule(),
    });
  }
  function loadSample() {
    const cat = category === "__custom__" ? (customCat.trim() || topic.trim() || "Sample") : (category || "General Knowledge");
    onComplete({ name: name.trim() || "You", category: cat,
      sourceType: "sample", difficulty, count: Math.min(count, SAMPLE_QUIZ.length), timer, mode, questionType,
      examCode: examCodeForLabel(cat),
      _sample: true, custom: category === "__custom__", scoreRule: buildScoreRule() });
  }

  const canNext = step === 0 ? true : step === 1 ? !!category : step === 2 ? sourceReady : true;

  return (
    <div className="qz-wrap" style={{ paddingTop: 14 }}>
      <div style={{ display: "flex", gap: 10, marginBottom: 26, flexWrap: "wrap" }}>
        {steps.map((s, i) => (
          <div key={s} style={{ flex: 1, minWidth: 70 }}>
            <div className="pbar" style={{ marginBottom: 7 }}><i style={{ width: i <= step ? "100%" : "0%" }} /></div>
            <div style={{ fontSize: 12, fontWeight: 700, color: i <= step ? "var(--text)" : "var(--faint)", display: "flex", gap: 6, alignItems: "center" }}><span className="qz-mono">{i + 1}</span>{s}</div>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="fadeUp">
          <h2 className="qz-display" style={{ fontSize: 34 }}>What should we call you?</h2>
          <p className="qz-muted" style={{ marginTop: 8, marginBottom: 22 }}>Used on your scoreboard and results.</p>
          <input className="input" style={{ fontSize: 20, maxWidth: 460 }} placeholder="e.g. Aria" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setStep(1)} autoFocus />
        </div>
      )}

      {step === 1 && (
        <div className="fadeUp">
          <h2 className="qz-display" style={{ fontSize: 34 }}>Pick your subject or exam</h2>
          <p className="qz-muted" style={{ marginTop: 8, marginBottom: 22 }}>The AI tunes question style and difficulty to match. Not listed? Make a custom one.</p>
          {CATEGORIES.map((grp) => {
            // Filter out tiles flagged `hidden: true` (e.g. IELTS, TOEFL temporarily
            // hidden Jun-2026). Backend / prompts / registry stay intact; flip the
            // flag in the CATEGORIES table to restore.
            const visibleItems = grp.items.filter((c) => !c.hidden);
            if (visibleItems.length === 0) return null;
            return (
            <div key={grp.group} style={{ marginBottom: 22 }}>
              <div className="qz-eyebrow" style={{ marginBottom: 12 }}>{grp.group}</div>
              <div className="grid g4">
                {visibleItems.map((c) => (
                  <button key={c.id} className={"tile" + (category === c.id ? " sel" : "")} onClick={() => setCategory(c.id)}>
                    {category === c.id && <span className="check"><Check size={13} /></span>}
                    <div className="ic"><c.icon size={20} /></div><h4>{c.id}</h4><p>{c.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            );
          })}
          <div className="qz-eyebrow" style={{ marginBottom: 12 }}>Make your own</div>
          <button className={"tile" + (category === "__custom__" ? " sel" : "")} onClick={() => setCategory("__custom__")} style={{ marginBottom: 12 }}>
            {category === "__custom__" && <span className="check"><Check size={13} /></span>}
            <div className="ic"><Sparkles size={20} /></div><h4>Custom exam / topic group</h4><p>Name any course, certification, or subject area.</p>
          </button>
          {presets && presets.length > 0 && (
            <>
              <div className="qz-eyebrow" style={{ margin: "18px 0 12px" }}>Presets</div>
              <div className="grid g2">
                {presets.map((p) => (
                  <button key={p.name} className={"tile" + (category === p.name ? " sel" : "")} onClick={() => setCategory(p.name)}>
                    {category === p.name && <span className="check"><Check size={13} /></span>}
                    <div className="ic" style={{ color: "var(--gold)" }}><Star size={18} /></div><h4>{p.name}</h4><p>{METHOD_LABEL[p.rule && p.rule.method] || "Weighted scoring"}</p>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="fadeUp">
          <h2 className="qz-display" style={{ fontSize: 34 }}>Where should questions come from?</h2>
          <p className="qz-muted" style={{ marginTop: 8, marginBottom: 22 }}>Choose a source and give the AI something to work with.</p>
          <div className="grid g4" style={{ marginBottom: 22 }}>
            {SOURCES.map((s) => (
              <button key={s.id} className={"tile" + (sourceType === s.id ? " sel" : "")} onClick={() => { setSourceType(s.id); setFileErr(""); }}>
                {sourceType === s.id && <span className="check"><Check size={13} /></span>}
                <div className="ic"><s.icon size={20} /></div><h4>{s.name}</h4><p>{s.desc}</p>
              </button>
            ))}
          </div>
          {sourceType === "topic" && (
            <>
              <input className="input pop" style={{ fontSize: 18 }} placeholder={category === "__custom__" ? "Name your custom exam / topic — e.g. AWS Solutions Architect, Class 7 Biology" : "e.g. Photosynthesis · Indian Constitution · Big-O notation"} value={topic} onChange={(e) => setTopic(e.target.value)} autoFocus />
              {category === "__custom__" && <p className="qz-muted" style={{ fontSize: 12.5, marginTop: 10 }}>This becomes the name of your custom quiz. You can be broad ("Norse Mythology") or specific ("Class 9 — Algebra basics").</p>}
            </>
          )}
          {sourceType === "paste" && (
            <div>
              <textarea data-testid="paste-text-input" className="textarea pop" placeholder={"Paste notes, an article, study material — or a URL like https://example.com/article"} value={pasteText} onChange={(e) => { setPasteText(e.target.value); if (pasteFromUrl) { setPasteFromUrl(false); setPasteUrl(""); } }} autoFocus />
              {(() => {
                const trimmed = pasteText.trim();
                const looksLikeUrl = /^https?:\/\/\S+$/i.test(trimmed);
                if (looksLikeUrl && !pasteFetching && !pasteFromUrl) {
                  return (
                    <div className="banner" style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }} data-testid="url-detected-banner">
                      <Globe size={16} color="var(--accent2)" />
                      <span style={{ flex: 1, minWidth: 160 }}>Looks like a URL — we'll fetch and extract the article text for you.</span>
                      <button data-testid="fetch-url-btn" className="btn btn--primary" style={{ padding: "8px 12px" }} onClick={async () => {
                        setFileErr(""); setPasteFetching(true);
                        try {
                          const d = await backend.fetchUrl(trimmed);
                          setPasteText(d.text);
                          setPasteUrl(d.url);
                          setPasteFromUrl(true);
                        } catch (e) {
                          setFileErr(e.message || "Couldn't read that page.");
                        } finally { setPasteFetching(false); }
                      }}>Fetch &amp; extract</button>
                    </div>
                  );
                }
                if (pasteFetching) return <p className="qz-muted" style={{ marginTop: 10, fontSize: 13 }}><Loader2 size={14} className="spin" style={{ verticalAlign: -2, marginRight: 4 }} /> Fetching page…</p>;
                if (pasteFromUrl && pasteUrl) return <p className="qz-muted" style={{ marginTop: 10, fontSize: 12.5 }} data-testid="url-extracted-tag"><Globe size={12} style={{ verticalAlign: -1, marginRight: 4 }} /> Extracted from <b className="qz-mono">{pasteUrl}</b> · {pasteText.length} characters</p>;
                return null;
              })()}
            </div>
          )}
          {sourceType === "file" && (
            <label className="card pop" style={{ display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }}>
              <div className="ic tile" style={{ width: 46, height: 46, padding: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}><Upload size={20} color="var(--accent)" /></div>
              <div style={{ flex: 1 }}><div style={{ fontWeight: 800 }}>{fileBusy ? "Reading…" : (fileName || "Choose a file")}</div><div className="qz-muted" style={{ fontSize: 13 }}>{fileBusy ? "Extracting text from your file" : (fileName ? fileText.length + " characters read" : ".pdf · .docx · .xlsx · .txt · .md · .csv · .json")}</div></div>
              <span className="chip">Browse</span><input type="file" accept=".pdf,.txt,.md,.csv,.tsv,.json,.docx,.xlsx,.xls,application/pdf,text/*" onChange={readFile} style={{ display: "none" }} />
            </label>
          )}
          {sourceType === "image" && (
            <label className="card pop" style={{ display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }}>
              <div className="ic tile" style={{ width: 46, height: 46, padding: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}><Camera size={20} color="var(--accent)" /></div>
              <div style={{ flex: 1 }}><div style={{ fontWeight: 800 }}>{imageName || "Choose an image"}</div><div className="qz-muted" style={{ fontSize: 13 }}>{imageData ? "Image ready — read with vision" : "A photo of notes, a slide, or a page"}</div></div>
              <span className="chip">Browse</span><input type="file" accept="image/*" onChange={readImage} style={{ display: "none" }} />
            </label>
          )}
          {sourceType === "exam_intel" && (
            <div className="card pop" data-testid="exam-intel-info" style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div className="ic tile" style={{ width: 46, height: 46, padding: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}><Brain size={20} color="var(--accent)" /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800 }}>Exam Intelligence — {category === "__custom__" ? (customCat.trim() || "Custom Exam") : (category || "your exam")}</div>
                <div className="qz-muted" style={{ fontSize: 13 }}>
                  {supportsExamIntelligenceMode({ category }) ? "Advanced engine reserved for this exam (rolling out). Currently uses the standard generator with this exam as the source."
                    : "We'll generate exam-style questions using this exam as the topic — no upload needed."}
                </div>
              </div>
            </div>
          )}
          {fileErr && <p style={{ color: "var(--wrong)", marginTop: 12, fontSize: 14 }}>{fileErr}</p>}
          <p className="qz-muted" style={{ marginTop: 14, fontSize: 13 }}>In the full product, PDF, Word, PowerPoint, Excel, URLs and scanned docs are parsed server-side — and can be combined or mixed automatically.</p>
        </div>
      )}

      {step === 3 && (
        <div className="fadeUp">
          <h2 className="qz-display" style={{ fontSize: 34 }}>Set up your Quiz</h2>
          <p className="qz-muted" style={{ marginTop: 8, marginBottom: 22 }}>Fine-tune difficulty, length, and how you want to play.</p>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 800, marginBottom: 12 }}>Mode</div>
            <div className="grid g2">
              {MODES.filter((m) => !access || access[m.id] !== false).map((m) => (
                <button key={m.id} className={"tile" + (mode === m.id ? " sel" : "")} onClick={() => setMode(m.id)}>
                  {mode === m.id && <span className="check"><Check size={13} /></span>}
                  <div className="ic" style={{ color: m.color }}><m.icon size={18} /></div><h4>{m.name}</h4><p>{m.desc}</p>
                </button>
              ))}
            </div>
            <p className="qz-muted" style={{ fontSize: 12.5, marginTop: 12 }}>Multiplayer is pass-and-play on one device. Survival gives you 3 lives; Challenge speeds up as you go.</p>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 800, marginBottom: 12 }}>Difficulty</div>
            <div className="seg" style={{ flexWrap: "wrap" }}>{DIFFS.map((d) => <button key={d.id} data-testid={`diff-${d.id}`} className={difficulty === d.id ? "act" : ""} onClick={() => setDifficulty(d.id)}>{d.name}</button>)}</div>
            <p className="qz-muted" style={{ fontSize: 12, marginTop: 8 }}>
              {difficulty === "random" ? "Balanced spread: ~30% easy / 40% medium / 30% hard." :
               difficulty === "progressive" ? "First third easy, middle third medium, final third hard." :
               "Standard fact-and-concept questions tuned to the level."}
            </p>
          </div>

          <div className="grid g2">
            <div className="card">
              <div style={{ fontWeight: 800, marginBottom: 12 }}>Questions {mode === "multiplayer" && <span className="tag">split across players</span>}</div>
              <div className="seg" style={{ marginBottom: 10 }}>{countOpts.map((n) => <button key={n} data-testid={`count-preset-${n}`} className={count === n ? "act" : ""} onClick={() => setCount(n)}>{n}</button>)}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <label className="qz-muted" style={{ fontSize: 13, fontWeight: 600 }}>Custom</label>
                <input
                  data-testid="count-input"
                  className="input"
                  type="number"
                  min={MIN_COUNT}
                  max={MAX_COUNT}
                  value={count}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") { setCount(""); return; }
                    const n = parseInt(raw, 10);
                    if (isNaN(n)) return;
                    setCount(Math.max(MIN_COUNT, Math.min(MAX_COUNT, n)));
                  }}
                  onBlur={() => { if (count === "" || count < MIN_COUNT) setCount(MIN_COUNT); }}
                  style={{ width: 110, padding: "8px 10px" }}
                />
                <span className="qz-muted" style={{ fontSize: 12 }}>1 – {MAX_COUNT}</span>
              </div>
              {typeof count === "number" && count > 50 && (
                <p className="qz-muted" style={{ fontSize: 12, marginTop: 8 }}>Heads-up: large sets take a bit longer to generate.</p>
              )}
            </div>
            <div className="card">
              <div style={{ fontWeight: 800, marginBottom: 12 }}>Timer / question {mode === "practice" && <span className="tag">off in practice</span>}</div>
              <div className="seg">{[10, 15, 20, 30, 45, 60].map((n) => <button key={n} className={timer === n ? "act" : ""} onClick={() => setTimer(n)} disabled={mode === "practice"}>{n}s</button>)}</div>
            </div>
          </div>

          {!EXAMS_AUTO_QUESTION_TYPE.has(category) && (
          <div className="card" style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 800, marginBottom: 4 }}>Question type</div>
            <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>Choose how answers work. Single-choice keeps things classic; the mixed option lets the AI occasionally pick 2 correct answers.</p>
            <div className="seg" style={{ flexWrap: "wrap" }}>
              <button data-testid="qtype-single" className={questionType === "single" ? "act" : ""} onClick={() => setQuestionType("single")}>Single Choice</button>
              <button data-testid="qtype-mixed" className={questionType === "mixed" ? "act" : ""} onClick={() => setQuestionType("mixed")}>Single + Multiple Choice</button>
            </div>
            <p className="qz-muted" style={{ fontSize: 12, marginTop: 10 }}>
              {questionType === "single"
                ? "Always 1 correct answer out of 4 options."
                : "AI may mix in questions with 2 correct answers (still 4 options). Multi-answer questions show a Submit button."}
            </p>
          </div>
          )}
          {EXAMS_AUTO_QUESTION_TYPE.has(category) && (
          <div className="card" style={{ marginTop: 16 }} data-testid="qtype-auto-note">
            <div style={{ fontWeight: 800, marginBottom: 4 }}>Question type — Auto</div>
            <p className="qz-muted" style={{ fontSize: 12.5 }}>{category} questions are auto-shaped by the exam's blueprint (MCQ, Numerical, Assertion-Reason, Match-the-Following, Statement-based, etc.). No manual selection needed.</p>
          </div>
          )}

          {category === "__custom__" && (
            <div className="card" style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Scoring <span className="tag">custom quiz</span></div>
              <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>Choose how this quiz is scored. Keep Weighted for the standard game scoring, or set your own rule.</p>
              <div className="seg" style={{ marginBottom: 14, flexWrap: "wrap" }}>
                {[["weighted", "Weighted"], ["negative", "Points ±"], ["scaled", "Scaled"], ["passfail", "Pass / Fail"]].map(([m, lbl]) => (
                  <button key={m} className={customRule.method === m ? "act" : ""} onClick={() => setCustomRule((c) => ({ ...c, method: m }))}>{lbl}</button>
                ))}
              </div>
              {customRule.method === "weighted" && <p className="qz-muted" style={{ fontSize: 13 }}>Base points × difficulty (easy ×1, medium ×1.5, hard ×2) + speed bonus + streak bonus.</p>}
              {customRule.method === "negative" && (
                <div className="r3-grid">
                  <label className="adm-f">Correct<input className="input" type="number" step="any" value={customRule.perCorrect} onChange={(e) => setCustomRule((c) => ({ ...c, perCorrect: e.target.value }))} /></label>
                  <label className="adm-f">Wrong<input className="input" type="number" step="any" value={customRule.perWrong} onChange={(e) => setCustomRule((c) => ({ ...c, perWrong: e.target.value }))} /></label>
                  <label className="adm-f">Unattempted<input className="input" type="number" step="any" value={customRule.perBlank} onChange={(e) => setCustomRule((c) => ({ ...c, perBlank: e.target.value }))} /></label>
                </div>
              )}
              {customRule.method === "scaled" && (
                <div className="r3-grid">
                  <label className="adm-f">Min score<input className="input" type="number" step="any" value={customRule.min} onChange={(e) => setCustomRule((c) => ({ ...c, min: e.target.value }))} /></label>
                  <label className="adm-f">Max score<input className="input" type="number" step="any" value={customRule.max} onChange={(e) => setCustomRule((c) => ({ ...c, max: e.target.value }))} /></label>
                  <label className="adm-f">Pass mark<input className="input" type="number" step="any" value={customRule.pass} onChange={(e) => setCustomRule((c) => ({ ...c, pass: e.target.value }))} /></label>
                </div>
              )}
              {customRule.method === "passfail" && (
                <label className="adm-f" style={{ maxWidth: 240 }}>Pass mark (% correct)<input className="input" type="number" step="any" value={customRule.passPct} onChange={(e) => setCustomRule((c) => ({ ...c, passPct: e.target.value }))} /></label>
              )}
            </div>
          )}

          <div className="card" style={{ marginTop: 16, background: "var(--surface2)" }}>
            <div className="qz-muted" style={{ fontSize: 13, marginBottom: 8 }}>If AI generation is unavailable in this environment, try the experience with a ready-made set:</div>
            <button className="btn" onClick={loadSample}><Shuffle size={16} /> Load a sample quiz (no AI)</button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28, gap: 12 }}>
        <button className="btn btn--ghost" onClick={() => step > 0 && setStep(step - 1)} disabled={step === 0}><ChevronLeft size={18} /> Back</button>
        {step < 3
          ? <button className="btn btn--primary" disabled={!canNext} onClick={() => setStep(step + 1)}>Continue <ChevronRight size={18} /></button>
          : <button className="btn btn--primary btn--lg" onClick={finish}><Wand2 size={20} /> Generate quiz</button>}
      </div>
    </div>
  );
}

function Loader({ progress, config }) {
  const msgs = ["Reading your source…", "Understanding the material…", "Drafting questions…", "Balancing difficulty…", "Polishing distractors…"];
  const [mi, setMi] = useState(0);
  useEffect(() => { const id = setInterval(() => setMi((m) => (m + 1) % msgs.length), 1400); return () => clearInterval(id); }, []);
  const pct = progress.total ? Math.round((progress.made / progress.total) * 100) : 0;
  return (
    <div className="qz-wrap" style={{ display: "grid", placeItems: "center", minHeight: "70vh" }}>
      <div className="card glow" style={{ width: "100%", maxWidth: 460, textAlign: "center", padding: 36 }}>
        <Loader2 size={40} className="spin" color="var(--accent)" />
        <div className="loaderdots" style={{ margin: "14px 0 16px" }}><span /><span /><span /></div>
        <h3 className="qz-display" style={{ fontSize: 22 }}>Building your {config.category} quiz</h3>
        <p className="qz-muted" style={{ marginTop: 8, minHeight: 22 }}>{progress.made > 0 ? `Crafted ${progress.made} of ${progress.total} questions…` : msgs[mi]}</p>
        <div className="pbar" style={{ marginTop: 18 }}><i style={{ width: Math.max(8, pct) + "%" }} /></div>
      </div>
    </div>
  );
}

function diffMult(d) { return d === "hard" ? 2 : d === "medium" ? 1.5 : 1; }

function Quiz({ config, questions, settings, onFinish, onExit }) {
  const cartoon = config.mode === "cartoon";
  const survival = config.mode === "survival";
  const challenge = config.mode === "challenge";
  const timed = config.mode !== "practice";
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("answering"); // answering | reveal
  const [chosen, setChosen] = useState(null);           // single-choice: display-order index that was picked
  const [multiPicks, setMultiPicks] = useState([]);     // multi-choice: array of display-order indexes
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [mood, setMood] = useState("idle");
  const startRef = useRef(Date.now());
  const qStartRef = useRef(Date.now());

  const q = questions[index];
  const order = q?._order || [0, 1, 2, 3];
  const isMulti = !!(q && q.multi);
  const N = questions.length;
  const total = timed ? (challenge ? Math.max(5, config.timer - Math.floor(index / 2)) * 1000 : config.timer * 1000) : 0;
  const [remaining, setRemaining] = useState(total);

  useEffect(() => {
    setShowHint(false); setRevealed(false); setMood("idle"); setMultiPicks([]);
    if (phase !== "answering") return;
    qStartRef.current = Date.now();
    if (!timed) { setRemaining(0); return; }
    setRemaining(total);
    const start = Date.now();
    const id = setInterval(() => {
      const rem = Math.max(0, total - (Date.now() - start));
      setRemaining(rem);
      if (rem <= 0) { clearInterval(id); handleTimeout(); }
    }, 100);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [index, phase]);

  useEffect(() => {
    if (phase === "answering" && q && settings.voice)
      speak(q.q + ". " + order.map((o, i) => `Option ${String.fromCharCode(65 + i)}: ${q.options[o]}`).join(". "), true);
    // eslint-disable-next-line
  }, [index, phase]);

  function finishPayload() { return { config, questions, answers, score, bestStreak, bookmarks, durationMs: Date.now() - startRef.current, endedEarly: survival && gameOver }; }
  function record(selected, correct, timedOut) {
    let points = 0;
    if (correct) {
      const base = 100 * diffMult(q.difficulty);
      const timeBonus = timed && total > 0 ? Math.round(60 * (remaining / total)) : 0;
      const ns = streak + 1;
      const streakBonus = ns >= 2 ? (ns - 1) * 25 : 0;
      points = Math.round(base + timeBonus + streakBonus);
      setStreak(ns); setBestStreak((b) => Math.max(b, ns)); setScore((s) => s + points);
    } else {
      setStreak(0);
      if (survival) setLives((l) => { const nl = l - 1; if (nl <= 0) setGameOver(true); return nl; });
    }
    setAnswers((a) => [...a, { qIndex: index, selected, correct, timedOut, timeMs: Date.now() - qStartRef.current, points, difficulty: q.difficulty, style: q.style }]);
  }
  function handleAnswer(d) {
    if (phase !== "answering") return;
    if (isMulti) {
      // Use the snapshot (not the updater form) so React 18 StrictMode
      // double-invocation can't re-toggle the same pick on the same click.
      // Each click independently adds or removes one display index.
      if (multiPicks.includes(d)) {
        setMultiPicks(multiPicks.filter((x) => x !== d));
      } else if (multiPicks.length < 2) {
        setMultiPicks([...multiPicks, d]);
      }
      return;
    }
    const correct = order[d] === q.correct;
    setChosen(d); record(d, correct, false); if (cartoon) setMood(correct ? "happy" : "oops");
    beep(correct ? "correct" : "wrong", settings.sound); setPhase("reveal");
  }

  function submitMulti() {
    if (phase !== "answering" || !isMulti) return;
    if (multiPicks.length === 0) return;
    const selectedActual = multiPicks.map((d) => order[d]);
    const correct = checkMultiCorrect(q, selectedActual);
    record(multiPicks, correct, false);
    if (cartoon) setMood(correct ? "happy" : "oops");
    beep(correct ? "correct" : "wrong", settings.sound);
    setPhase("reveal");
  }
  function handleTimeout() { if (phase !== "answering") return; setChosen(null); setMultiPicks([]); record(null, false, true); if (cartoon) setMood("oops"); beep("wrong", settings.sound); setPhase("reveal"); }
  function next() {
    if (gameOver || index + 1 >= N) return onFinish(finishPayload());
    setIndex((i) => i + 1); setChosen(null); setMultiPicks([]); setPhase("answering");
  }
  function toggleBookmark() { setBookmarks((b) => b.includes(index) ? b.filter((x) => x !== index) : [...b, index]); }

  const lastQ = index + 1 >= N;
  // NEET-only presentation override (examCode === "NEET_UG"). All game mechanics
  // (state, scoring, timer, navigation, streaks) stay in this component; only
  // the JSX is replaced. Other exams render the legacy layout below untouched.
  if (config && config.examCode === "NEET_UG" && q) {
    return (
      <React.Suspense fallback={<div className="qz-wrap" style={{ paddingTop: 8 }}><div className="card" style={{ padding: 26 }}><Loader2 size={18} className="spin" /> Loading NEET renderer…</div></div>}>
        <NeetPlayPage
          q={q} index={index} N={N} phase={phase} order={order}
          chosen={chosen} multiPicks={multiPicks} isMulti={isMulti} revealed={revealed}
          score={score} streak={streak} lives={lives} gameOver={gameOver}
          mode={config.mode} settings={settings} remaining={remaining} total={total}
          bookmarks={bookmarks} showHint={showHint} lastQ={lastQ}
          isOptionCorrect={isOptionCorrect} correctIndexes={correctIndexes}
          handleAnswer={handleAnswer} submitMulti={submitMulti}
          next={next} toggleBookmark={toggleBookmark}
          setShowHint={setShowHint} setRevealed={setRevealed}
          speak={speak} onExit={onExit} onFinish={onFinish}
          finishPayload={finishPayload}
        />
      </React.Suspense>
    );
  }
  return (
    <div className="qz-wrap" style={{ paddingTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
        <button className="btn btn--ghost" style={{ padding: "8px 12px" }} onClick={onExit}><ChevronLeft size={18} /> Quit</button>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          {survival && <Hearts lives={lives} max={3} />}
          {challenge && phase === "answering" && <span className="chip" style={{ borderColor: "var(--gold)" }}><Zap size={15} color="var(--gold)" /> {Math.ceil(total / 1000)}s</span>}
          <span className="chip"><Trophy size={15} color="var(--gold)" /> <b className="qz-mono">{score}</b></span>
          {streak >= 2 && <span className="chip" style={{ borderColor: "var(--accent)" }}><Flame size={15} color="var(--accent)" /> {streak} streak</span>}
          {timed && phase === "answering" && <TimerRing remaining={remaining} total={total} />}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <span className="qz-mono qz-muted" style={{ fontSize: 13 }}>{String(index + 1).padStart(2, "0")}/{String(N).padStart(2, "0")}</span>
        <div className="pbar" style={{ flex: 1 }}><i style={{ width: ((index + (phase === "reveal" ? 1 : 0)) / N) * 100 + "%" }} /></div>
      </div>

      {cartoon && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, justifyContent: "center", marginBottom: 8 }}>
          <KidMascot mood={phase === "reveal" ? mood : "idle"} size={84} />
          <span className="chip pop" key={phase + "-" + index} style={{ fontWeight: 700 }}>
            {phase !== "reveal" ? "Quizkosh rooting for you — your turn! \uD83E\uDD14"
              : (mood === "happy") ? "Woohoo! Quizkosh is so proud! \uD83C\uDF89"
              : "Aw — Quizkosh knows you'll get the next one! \uD83D\uDCAA"}
          </span>
        </div>
      )}

      {q && (
        <div className={"card fadeUp" + (!isMulti && chosen !== null && order[chosen] !== q.correct ? " shake" : "")} key={index} style={{ padding: 26 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span className="tag" style={{ color: q.difficulty === "hard" ? "var(--wrong)" : q.difficulty === "medium" ? "var(--gold)" : "var(--correct)" }}>{q.difficulty}</span>
              <span className="tag">{q.style}</span>
              {isMulti && <span className="tag" style={{ background: "rgba(126,232,250,.16)", color: "var(--accent2)", borderColor: "rgba(126,232,250,.4)" }} data-testid="multi-badge">Multi · select 2</span>}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {settings.voice && <button className="btn btn--icon" style={{ width: 36, height: 36 }} title="Read aloud" onClick={() => speak(q.q, true)}><Mic size={15} /></button>}
              <button className="btn btn--icon" style={{ width: 36, height: 36 }} title="Bookmark" onClick={toggleBookmark}>{bookmarks.includes(index) ? <BookmarkCheck size={16} color="var(--accent)" /> : <Bookmark size={16} />}</button>
            </div>
          </div>

          <h2 className="qz-display" style={{ fontSize: 24, lineHeight: 1.25, marginBottom: 20 }}>{q.q}</h2>

          <div className="grid" style={{ gap: 11 }}>
            {order.map((optActual, d) => {
              const isCorrect = isOptionCorrect(q, optActual);
              const isChosen = isMulti ? multiPicks.includes(d) : (chosen === d);
              let cls = "opt";
              if (phase === "reveal" || revealed) { if (isCorrect) cls += " opt--correct"; else if (isChosen) cls += " opt--wrong"; else cls += " opt--dim"; }
              else if (isChosen) cls += " opt--chosen";
              return (
                <button key={d} className={cls} disabled={phase === "reveal"} onClick={() => handleAnswer(d)}>
                  {isMulti && phase === "answering" ? (
                    <span className="opt__k" aria-hidden="true" style={{ background: isChosen ? "var(--accent2)" : "transparent", border: isChosen ? "none" : "2px solid var(--border)", display: "grid", placeItems: "center", color: "#0b0f17" }}>
                      {isChosen ? <Check size={14} strokeWidth={3} /> : <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700 }}>{String.fromCharCode(65 + d)}</span>}
                    </span>
                  ) : (
                    <span className="opt__k">{String.fromCharCode(65 + d)}</span>
                  )}
                  <span style={{ flex: 1 }}>{q.options[optActual]}</span>
                  {(phase === "reveal" || revealed) && isCorrect && <Check size={20} color="var(--correct)" />}
                  {phase === "reveal" && isChosen && !isCorrect && <X size={20} color="var(--wrong)" />}
                </button>
              );
            })}
          </div>

          {isMulti && phase === "answering" && (
            <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <span className="qz-muted" style={{ fontSize: 13 }} data-testid="multi-pick-count">{multiPicks.length}/2 selected</span>
              <button data-testid="multi-submit-btn" className="btn btn--primary" disabled={multiPicks.length === 0} onClick={submitMulti}><Check size={16} /> Submit answer</button>
            </div>
          )}

          {phase === "answering" && config.mode === "practice" && (
            <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {q.hint && <button className="btn" style={{ padding: "8px 14px" }} onClick={() => setShowHint((s) => !s)}><Lightbulb size={15} color="var(--gold)" /> {showHint ? "Hide hint" : "Hint"}</button>}
              <button className="btn" style={{ padding: "8px 14px" }} onClick={() => setRevealed(true)}><BookOpen size={15} /> Reveal answer</button>
              {showHint && q.hint && <span className="chip pop" style={{ borderColor: "var(--gold)" }}>{q.hint}</span>}
            </div>
          )}

          {(phase === "reveal" || revealed) && (
            <div className="pop" style={{ marginTop: 18 }}>
              <div className="glass" style={{ padding: 16, borderLeft: "3px solid var(--accent2)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, marginBottom: 6 }}><Lightbulb size={16} color="var(--accent2)" /> Explanation</div>
                <p style={{ lineHeight: 1.55 }}>{q.explanation || `The correct answer${isMulti ? "s are " : " is "}${correctIndexes(q).map((c) => String.fromCharCode(65 + order.indexOf(c))).join(" & ")}.`}</p>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                {phase === "reveal" && (
                  (survival && gameOver)
                    ? <button className="btn btn--primary btn--lg" onClick={() => onFinish(finishPayload())}><Heart size={18} /> Out of lives — see results</button>
                    : <button className="btn btn--primary btn--lg" onClick={next}>{lastQ ? <>See results <Trophy size={18} /></> : <>Next question <ArrowRight size={18} /></>}</button>
                )}
                {(revealed && phase === "answering") && <span className="qz-muted" style={{ fontSize: 13 }}>Pick an option above to lock in your answer.</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Hearts({ lives, max }) {
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {Array.from({ length: max }).map((_, i) => (
        <Heart key={i} size={18} color={i < lives ? "var(--wrong)" : "var(--faint)"} fill={i < lives ? "var(--wrong)" : "none"} />
      ))}
    </span>
  );
}

function Confetti({ go }) {
  if (!go) return null;
  const colors = ["var(--accent)", "var(--accent2)", "var(--accent3)", "var(--gold)", "var(--correct)"];
  return (
    <div className="qz-confetti" aria-hidden>
      {Array.from({ length: 46 }).map((_, i) => {
        const left = Math.random() * 100, dur = 2 + Math.random() * 1.6, delay = Math.random() * 0.5;
        return <i key={i} style={{ left: left + "%", background: colors[i % colors.length], animationDuration: dur + "s", animationDelay: delay + "s", transform: `scale(${0.7 + Math.random() * 0.9})` }} />;
      })}
    </div>
  );
}

function MultiplayerQuiz({ config, questions, settings, onHome, onNew }) {
  const timed = config.mode !== "practice";
  const total = timed ? config.timer * 1000 : 0;
  const N = questions.length;
  const [count, setCount] = useState(2);
  const [players, setPlayers] = useState([(config.name && config.name.trim()) || "", ""]);
  const [phase, setPhase] = useState("names"); // names | handoff | answer | reveal | done
  const [pi, setPi] = useState(0);
  const [qPtr, setQPtr] = useState(0);
  const [scores, setScores] = useState([0, 0]);
  const [chosen, setChosen] = useState(null);
  const [remaining, setRemaining] = useState(total);

  const q = questions[qPtr];
  const order = q?._order || [0, 1, 2, 3];

  function setNum(n) {
    setCount(n);
    setPlayers((p) => { const np = [...p]; while (np.length < n) np.push(""); return np.slice(0, n); });
  }
  function start() {
    const names = players.map((nm, i) => (nm.trim() || "Player " + (i + 1)));
    setPlayers(names); setScores(Array(count).fill(0)); setPi(0); setQPtr(0); setChosen(null); setPhase("handoff");
  }

  useEffect(() => {
    if (phase !== "answer") return;
    if (!timed) { setRemaining(0); return; }
    setRemaining(total);
    const s = Date.now();
    const id = setInterval(() => { const rem = Math.max(0, total - (Date.now() - s)); setRemaining(rem); if (rem <= 0) { clearInterval(id); answer(null); } }, 100);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [phase, qPtr]);

  function answer(d) {
    if (phase !== "answer") return;
    const correct = d !== null && isOptionCorrect(q, order[d]);
    if (correct) {
      const base = 100 * diffMult(q.difficulty);
      const timeBonus = timed && total > 0 ? Math.round(60 * (remaining / total)) : 0;
      const pts = Math.round(base + timeBonus);
      setScores((s) => { const ns = [...s]; ns[pi] += pts; return ns; });
    }
    setChosen(d === null ? -1 : d); beep(correct ? "correct" : "wrong", settings.sound); setPhase("reveal");
  }
  function nextTurn() {
    if (qPtr + 1 >= N) { setPhase("done"); return; }
    setQPtr((x) => x + 1); setPi((x) => (x + 1) % count); setChosen(null); setPhase("handoff");
  }

  const max = scores.length ? Math.max(...scores) : 0;
  const winners = players.filter((_, i) => scores[i] === max);

  if (phase === "names") {
    const PCOL = ["var(--accent)", "var(--accent2)", "var(--accent3)", "var(--gold)"];
    return (
      <div className="qz-wrap" style={{ paddingTop: 10 }}>
        <button className="btn btn--ghost" style={{ padding: "8px 12px", marginBottom: 14 }} onClick={onHome}><ChevronLeft size={18} /> Quit</button>
        <div className="qz-eyebrow"><Users size={13} style={{ verticalAlign: -2 }} /> Multiplayer · pass &amp; play</div>
        <h2 className="qz-display" style={{ fontSize: 32, marginTop: 6 }}>Who's playing?</h2>
        <p className="qz-muted" style={{ marginTop: 8, marginBottom: 18 }}>Add 2–4 players and enter each name. You'll pass the device around and take turns — highest score wins.</p>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 800, marginBottom: 12 }}>How many players?</div>
          <div className="seg">{[2, 3, 4].map((n) => <button key={n} className={count === n ? "act" : ""} onClick={() => setNum(n)}>{n} players</button>)}</div>
        </div>

        <div className="card">
          <div style={{ fontWeight: 800, marginBottom: 12 }}>Player names</div>
          <div className="grid" style={{ gap: 12 }}>
            {players.map((nm, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ flex: "none", width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center", fontWeight: 800, color: "#0b0b14", background: PCOL[i % PCOL.length] }}>{i + 1}</span>
                <input className="input" style={{ flex: 1 }} value={nm} maxLength={20} onChange={(e) => setPlayers((p) => { const np = [...p]; np[i] = e.target.value; return np; })} placeholder={`Player ${i + 1} name`} />
              </div>
            ))}
          </div>
          <p className="qz-muted" style={{ fontSize: 12.5, marginTop: 12 }}>Names appear on the scoreboard, the pass-the-device screen, and the winner card. Blanks become “Player 1”, “Player 2”, and so on.</p>
        </div>

        <button className="btn btn--primary btn--lg btn--block" style={{ marginTop: 18 }} onClick={start}><Users size={20} /> Start battle</button>
      </div>
    );
  }

  if (phase === "done") {
    const tie = winners.length > 1;
    const ranked = players.map((nm, i) => ({ nm, sc: scores[i] })).sort((a, b) => b.sc - a.sc);
    return (
      <div className="qz-wrap" style={{ paddingTop: 10, position: "relative" }}>
        <Confetti go />
        <div className="card glow pop" style={{ textAlign: "center", padding: 30 }}>
          <Crown size={40} color="var(--gold)" />
          <h2 className="qz-display" style={{ fontSize: 30, marginTop: 8 }}>{tie ? "It's a tie!" : `${winners[0]} wins!`}</h2>
          <p className="qz-muted" style={{ marginTop: 4 }}>{tie ? winners.join(" & ") + " tied it up." : "Champion of the quiz."}</p>
        </div>
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Final scores</div>
          {ranked.map((p, rank) => (
            <div key={rank} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: rank < ranked.length - 1 ? "1px solid var(--border)" : "none" }}>
              <span style={{ fontWeight: 700 }}>{rank === 0 ? " " : rank === 1 ? " " : rank === 2 ? " " : "• "}{p.nm}</span>
              <b className="qz-mono" style={{ fontSize: 18 }}>{p.sc}</b>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 18 }}>
          <button className="btn btn--primary" onClick={start}><RotateCcw size={18} /> Rematch</button>
          <button className="btn" onClick={onNew}><Wand2 size={18} /> New quiz</button>
          <button className="btn btn--ghost" style={{ marginLeft: "auto" }} onClick={onHome}><Home size={18} /> Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="qz-wrap" style={{ paddingTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn btn--ghost" style={{ padding: "8px 12px" }} onClick={onHome}><ChevronLeft size={18} /> Quit</button>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {players.map((nm, i) => (
            <span key={i} className="chip" style={{ borderColor: i === pi ? "var(--accent2)" : "var(--border)", color: i === pi ? "var(--text)" : "var(--muted)" }}>{nm}: <b className="qz-mono">{scores[i]}</b></span>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <span className="qz-mono qz-muted" style={{ fontSize: 13 }}>{String(qPtr + 1).padStart(2, "0")}/{String(N).padStart(2, "0")}</span>
        <div className="pbar" style={{ flex: 1 }}><i style={{ width: ((qPtr + (phase === "reveal" ? 1 : 0)) / N) * 100 + "%" }} /></div>
      </div>

      {phase === "handoff" && (
        <div className="card glow pop" style={{ textAlign: "center", padding: 34 }}>
          <Users size={34} color="var(--accent2)" />
          <h2 className="qz-display" style={{ fontSize: 26, marginTop: 10 }}>Pass the device to</h2>
          <div className="qz-display" style={{ fontSize: 30, color: "var(--accent)", margin: "6px 0 2px" }}>{players[pi]}</div>
          <p className="qz-muted">Question {qPtr + 1} of {N}. No peeking! </p>
          <button className="btn btn--primary btn--lg" style={{ marginTop: 16 }} onClick={() => setPhase("answer")}>I'm {players[pi]} — ready <ArrowRight size={18} /></button>
        </div>
      )}

      {(phase === "answer" || phase === "reveal") && q && (
        <div className={"card fadeUp" + (chosen >= 0 && !isOptionCorrect(q, order[chosen]) ? " shake" : "")} key={qPtr} style={{ padding: 26 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span className="tag" style={{ color: q.difficulty === "hard" ? "var(--wrong)" : q.difficulty === "medium" ? "var(--gold)" : "var(--correct)" }}>{q.difficulty}</span>
              <span className="tag" style={{ color: "var(--accent2)" }}>{players[pi]}'s turn</span>
            </div>
            {timed && phase === "answer" && <TimerRing remaining={remaining} total={total} />}
          </div>
          <h2 className="qz-display" style={{ fontSize: 24, lineHeight: 1.25, marginBottom: 20 }}>{q.q}</h2>
          <div className="grid" style={{ gap: 11 }}>
            {order.map((optActual, d) => {
              const isCorrect = isOptionCorrect(q, optActual), isChosen = chosen === d;
              let cls = "opt";
              if (phase === "reveal") { if (isCorrect) cls += " opt--correct"; else if (isChosen) cls += " opt--wrong"; else cls += " opt--dim"; }
              return (
                <button key={d} className={cls} disabled={phase === "reveal"} onClick={() => answer(d)}>
                  <span className="opt__k">{String.fromCharCode(65 + d)}</span>
                  <span style={{ flex: 1 }}>{q.options[optActual]}</span>
                  {phase === "reveal" && isCorrect && <Check size={20} color="var(--correct)" />}
                  {phase === "reveal" && isChosen && !isCorrect && <X size={20} color="var(--wrong)" />}
                </button>
              );
            })}
          </div>
          {phase === "reveal" && (
            <div className="pop" style={{ marginTop: 18 }}>
              <div className="glass" style={{ padding: 16, borderLeft: "3px solid var(--accent2)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, marginBottom: 6 }}><Lightbulb size={16} color="var(--accent2)" /> Explanation</div>
                <p style={{ lineHeight: 1.55 }}>{q.explanation || `The correct answer${q.multi ? "s are " : " is "}${correctIndexes(q).map((c) => String.fromCharCode(65 + order.indexOf(c))).join(" & ")}.`}</p>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                <button className="btn btn--primary btn--lg" onClick={nextTurn}>{qPtr + 1 >= N ? <>Final scores <Crown size={18} /></> : <>Next player <ArrowRight size={18} /></>}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function readiness(p) {
  if (p >= 85) return { t: "Exam ready", c: "var(--correct)" };
  if (p >= 70) return { t: "Almost there", c: "var(--gold)" };
  if (p >= 50) return { t: "Building up", c: "var(--accent)" };
  return { t: "Keep practicing", c: "var(--wrong)" };
}

const DIFF_META = { easy: { label: "Easy", c: "var(--correct)" }, medium: { label: "Medium", c: "var(--gold)" }, hard: { label: "Hard", c: "var(--wrong)" } };
const pctOf = (c, t) => (t ? Math.round((c / t) * 100) : 0);
const titleCase = (s) => String(s || "").replace(/(^|\s|-|\/)\w/g, (m) => m.toUpperCase());
const barColor = (p) => (p >= 70 ? "var(--correct)" : p >= 40 ? "var(--gold)" : "var(--wrong)");
function groupStats(answers, key) {
  const m = {};
  for (const a of answers) { const k = a[key] || "other"; if (!m[k]) m[k] = { total: 0, correct: 0 }; m[k].total++; if (a.correct) m[k].correct++; }
  return m;
}

function coachNotes(answers, config, accuracy, avg) {
  const out = [];
  const timed = config.mode !== "practice";
  const byD = groupStats(answers, "difficulty");
  const byS = groupStats(answers, "style");
  const styles = Object.entries(byS).filter(([, v]) => v.total >= 2 && v.correct < v.total).sort((a, b) => pctOf(a[1].correct, a[1].total) - pctOf(b[1].correct, b[1].total));
  if (styles.length) { const [name, v] = styles[0]; out.push({ icon: TrendingDown, tone: "var(--wrong)", text: `Your trickiest question type was ${titleCase(name)} (${pctOf(v.correct, v.total)}% right). A focused Practice run on these will pay off fast.` }); }
  const hard = byD.hard, easy = byD.easy;
  if (hard && hard.total && pctOf(hard.correct, hard.total) < 60) out.push({ icon: Target, tone: "var(--gold)", text: `Hard questions are your growth edge — ${hard.correct}/${hard.total} so far. Mix in more hard sets to raise your ceiling.` });
  else if (easy && easy.correct < easy.total) out.push({ icon: Lightbulb, tone: "var(--accent)", text: `A couple of easy ones slipped by — re-reading the question once before answering will catch those.` });
  if (timed && config.timer) {
    const ratio = avg / config.timer;
    if (ratio > 0.82) out.push({ icon: Clock, tone: "var(--accent2)", text: `You're using almost the whole clock (${avg}s of ${config.timer}s). Try spotting the key fact before reading every option.` });
    else if (ratio < 0.4 && accuracy < 70) out.push({ icon: Clock, tone: "var(--accent2)", text: `You're answering fast (${avg}s avg) but leaving points behind — a beat more per question could lift your score.` });
  }
  if (out.length === 0 || accuracy >= 85) out.unshift({ icon: TrendingUp, tone: "var(--correct)", text: accuracy >= 85 ? `Strong, consistent work — you're ready to bump up the difficulty or add more questions.` : `Solid run. Keep the streak going and review the misses below.` });
  return out.slice(0, 3);
}

function computeBadges(result, d) {
  const { config, bestStreak } = result;
  const { accuracy, avg, timedOut, N, byD, lastK, timed } = d;
  const hard = byD.hard || { total: 0, correct: 0 };
  return [
    { id: "perfect", name: "Perfect Score", desc: "100% correct", icon: Trophy, c: "var(--gold)", got: accuracy === 100 && N > 0 },
    { id: "sharp", name: "Sharpshooter", desc: "90%+ accuracy", icon: Target, c: "var(--correct)", got: accuracy >= 90 },
    { id: "fire", name: "On Fire", desc: "5+ answer streak", icon: Flame, c: "var(--accent)", got: bestStreak >= 5 },
    { id: "fast", name: "Lightning", desc: "Fast & accurate", icon: Zap, c: "var(--gold)", got: !!(timed && config.timer && avg < config.timer * 0.4 && accuracy >= 60) },
    { id: "brain", name: "Brainiac", desc: "Aced every hard Q", icon: GraduationCap, c: "var(--accent2)", got: hard.total >= 1 && hard.correct === hard.total },
    { id: "focus", name: "Flawless Focus", desc: "No timeouts", icon: Sparkles, c: "var(--accent3)", got: !!(timed && timedOut === 0 && N >= 5) },
    { id: "clutch", name: "Clutch Finish", desc: "Nailed the last 3", icon: PartyPopper, c: "var(--accent)", got: N >= 5 && lastK },
    { id: "survivor", name: "Survivor", desc: "Survived the run", icon: Heart, c: "var(--wrong)", got: config.mode === "survival" && !result.endedEarly && N > 0 },
    { id: "marathon", name: "Marathoner", desc: "15+ questions", icon: Rocket, c: "var(--accent2)", got: N >= 15 },
  ];
}

// Per-exam scoring rules (admin-editable). Anything NOT listed here — General
// Knowledge, Daily Challenge, Little Learners, and custom quizzes — falls back to
// the default weighted game scoring (base × difficulty + speed + streak).
// Methods: "negative" (marks ± penalty), "scaled" (accuracy → score range + pass
// mark), "band" (raw → band scale), "proficiency" (% → tiers/grades).
const SCORING_RULES = {
  // School — letter grades
  "Primary": { method: "proficiency", tiers: [[90, "A"], [75, "B"], [60, "C"], [40, "D"], [0, "F"]] },
  "Middle School": { method: "proficiency", tiers: [[90, "A"], [75, "B"], [60, "C"], [40, "D"], [0, "F"]] },
  "High School": { method: "proficiency", tiers: [[90, "A"], [75, "B"], [60, "C"], [40, "D"], [0, "F"]] },
  "College": { method: "proficiency", tiers: [[90, "A"], [75, "B"], [60, "C"], [40, "D"], [0, "F"]] },
  // Entrance & competitive — negative marking
  "NEET": { method: "negative", perCorrect: 4, perWrong: -1, perBlank: 0 },
  "JEE": { method: "negative", perCorrect: 4, perWrong: -1, perBlank: 0 },
  "UPSC": { method: "negative", perCorrect: 2, perWrong: -0.66, perBlank: 0 },
  "SSC": { method: "negative", perCorrect: 2, perWrong: -0.5, perBlank: 0 },
  "CAT": { method: "negative", perCorrect: 3, perWrong: -1, perBlank: 0 },
  "Bank Exams": { method: "negative", perCorrect: 1, perWrong: -0.25, perBlank: 0 },
  "Government Exams": { method: "negative", perCorrect: 1, perWrong: -0.25, perBlank: 0 },
  // Study abroad — scaled scores / band
  "GRE": { method: "scaled", min: 260, max: 340, pass: 320 },
  "GMAT": { method: "scaled", min: 205, max: 805, pass: 650 },
  "SAT": { method: "scaled", min: 400, max: 1600, pass: 1200 },
  "IELTS": { method: "band", max: 40, bands: [[39, 9.0], [37, 8.5], [35, 8.0], [33, 7.5], [30, 7.0], [27, 6.5], [23, 6.0], [19, 5.5], [15, 5.0], [13, 4.5], [10, 4.0], [0, 3.5]] },
  "TOEFL": { method: "scaled", min: 0, max: 120, pass: 90 },
  // Professional
  "PMP": { method: "proficiency", tiers: [[80, "Above Target"], [65, "Target"], [50, "Below Target"], [0, "Needs Improvement"]] },
  "EA": { method: "scaled", min: 40, max: 130, pass: 105 },
  "Technical Certifications": { method: "scaled", min: 100, max: 1000, pass: 700 },
};
const SCORING_GROUPS = [
  ["School", ["Primary", "Middle School", "High School", "College"]],
  ["Entrance & Competitive", ["NEET", "JEE", "UPSC", "SSC", "CAT", "Bank Exams"]],
  ["Study Abroad", ["GRE", "GMAT", "SAT", "IELTS", "TOEFL"]],
  ["Professional", ["PMP", "EA", "Technical Certifications"]],
];
const METHOD_LABEL = { negative: "Negative marking", scaled: "Scaled score", band: "Band scale", proficiency: "Tiers / grades" };
function examKeyFor(category, rules) {
  if (!category || !rules) return null;
  const lc = String(category).trim().toLowerCase();
  for (const k of Object.keys(rules)) if (lc === k.toLowerCase()) return k;
  return null;
}
function scoreByRule(r, answers) {
  if (!r) return null;
  const correct = answers.filter((a) => a.correct).length;
  const wrong = answers.filter((a) => !a.correct && !a.timedOut).length;
  const blank = answers.filter((a) => a.timedOut).length;
  const N = answers.length;
  if (r.method === "negative") {
    const raw = Math.round((correct * r.perCorrect + wrong * r.perWrong + blank * r.perBlank) * 100) / 100;
    const max = N * r.perCorrect;
    return { kind: "marks", value: raw, max, label: `${raw} / ${max}`, detail: `+${r.perCorrect} correct · ${r.perWrong} wrong · ${r.perBlank} unattempted` };
  }
  if (r.method === "scaled") {
    const frac = N ? correct / N : 0;
    const score = Math.round(r.min + frac * (r.max - r.min));
    const pass = score >= r.pass;
    return { kind: "scaled", value: score, pass, label: `${score}`, detail: `${correct}/${N} correct · scale ${r.min}–${r.max} · pass ${r.pass}` };
  }
  if (r.method === "band") {
    const max = r.max || 40;
    const scaled = N ? Math.round((correct / N) * max) : 0;
    let band = (r.bands[r.bands.length - 1] || [0, 0])[1];
    for (const [min, b] of r.bands) { if (scaled >= min) { band = b; break; } }
    return { kind: "band", value: band, label: `Band ${Number(band).toFixed(1)}`, detail: `${correct}/${N} correct → ${scaled}/${max} scaled` };
  }
  if (r.method === "proficiency") {
    const pct = N ? Math.round((correct / N) * 100) : 0;
    let lab = (r.tiers[r.tiers.length - 1] || [0, "—"])[1];
    for (const [min, l] of r.tiers) { if (pct >= min) { lab = l; break; } }
    return { kind: "proficiency", value: pct, label: lab, detail: `${pct}% correct (${correct}/${N})` };
  }
  return null;
}
function examScore(category, answers, rules) {
  const key = examKeyFor(category, rules);
  if (!key) return null;
  const res = scoreByRule(rules[key], answers);
  return res ? { ...res, key } : null;
}

function ReadinessGauge({ accuracy, r }) {
  const R = 84, C = Math.PI * R;
  const [on, setOn] = useState(false);
  useEffect(() => { const t = setTimeout(() => setOn(true), 90); return () => clearTimeout(t); }, []);
  const off = C * (1 - Math.max(0, Math.min(100, accuracy)) / 100);
  return (
    <div style={{ position: "relative", width: "100%", maxWidth: 260, margin: "0 auto" }}>
      <svg viewBox="0 0 200 120" style={{ width: "100%", display: "block" }}>
        <path d="M16,100 A84,84 0 0 1 184,100" fill="none" stroke="var(--faint)" strokeWidth="13" strokeLinecap="round" />
        <path d="M16,100 A84,84 0 0 1 184,100" fill="none" stroke={r.c} strokeWidth="13" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={on ? off : C} style={{ transition: "stroke-dashoffset 1s cubic-bezier(.2,.8,.2,1)" }} />
        <text x="100" y="90" textAnchor="middle" style={{ fontSize: 38, fontWeight: 800, fill: "var(--text)", fontFamily: "'Space Mono',monospace" }}>{accuracy}%</text>
      </svg>
      <div style={{ textAlign: "center", marginTop: -4 }}><span className="chip" style={{ borderColor: r.c, color: r.c, fontWeight: 800 }}><Target size={14} /> {r.t}</span></div>
    </div>
  );
}

/* Renders an SVG result card. Used inline + as the source for the downloadable PNG. */
function ResultCardSVG({ name, category, mode, accuracy, score, correct, total, bestStreak, readinessLabel, badges, themeAccent = "#ff7a1a", themeAccent2 = "#27e3c4", themeAccent3 = "#ff3d81" }) {
  const W = 1200, H = 630;
  const bg = "#080b10";
  const safeName = (name || "A friend").slice(0, 28);
  const safeCat = (category || "Quiz").slice(0, 36);
  const badgesShort = (badges || []).slice(0, 3);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto", display: "block", borderRadius: 16 }}>
      <defs>
        <linearGradient id="cardBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={bg} />
          <stop offset="100%" stopColor="#0d121a" />
        </linearGradient>
        <radialGradient id="g1" cx="14%" cy="-10%" r="60%">
          <stop offset="0%" stopColor={themeAccent} stopOpacity="0.45" />
          <stop offset="100%" stopColor={themeAccent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="g2" cx="100%" cy="0%" r="55%">
          <stop offset="0%" stopColor={themeAccent3} stopOpacity="0.4" />
          <stop offset="100%" stopColor={themeAccent3} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="g3" cx="80%" cy="110%" r="60%">
          <stop offset="0%" stopColor={themeAccent2} stopOpacity="0.3" />
          <stop offset="100%" stopColor={themeAccent2} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="num" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={themeAccent} />
          <stop offset="100%" stopColor={themeAccent3} />
        </linearGradient>
      </defs>
      <rect width={W} height={H} fill="url(#cardBg)" />
      <rect width={W} height={H} fill="url(#g1)" />
      <rect width={W} height={H} fill="url(#g2)" />
      <rect width={W} height={H} fill="url(#g3)" />

      {/* Brand top-left */}
      <g transform="translate(56,56)">
        <rect width="58" height="58" rx="14" fill="url(#num)" />
        <text x="29" y="40" textAnchor="middle" fontFamily="'Unbounded',sans-serif" fontWeight="900" fontSize="30" fill="#0a0a0a">Q</text>
        <text x="78" y="40" fontFamily="'Unbounded',sans-serif" fontWeight="900" fontSize="32" fill="#f4efe6">Quizk<tspan fill="url(#num)">o</tspan>sh</text>
      </g>

      {/* Eyebrow */}
      <text x="56" y="170" fontFamily="'Space Mono',monospace" fontSize="20" fill={themeAccent2} letterSpacing="4">{safeCat.toUpperCase()}  ·  {(mode || "CLASSIC").toUpperCase()}</text>

      {/* Big number */}
      <text x="56" y="382" fontFamily="'Unbounded',sans-serif" fontWeight="900" fontSize="280" fill="url(#num)" letterSpacing="-12">{accuracy}</text>
      <text x={56 + (String(accuracy).length * 145)} y="382" fontFamily="'Unbounded',sans-serif" fontWeight="800" fontSize="120" fill="#9aa6b2">%</text>

      <text x="56" y="438" fontFamily="'Hanken Grotesk',sans-serif" fontWeight="700" fontSize="32" fill="#f4efe6">{safeName} scored {score} on {safeCat}</text>
      <text x="56" y="478" fontFamily="'Hanken Grotesk',sans-serif" fontWeight="500" fontSize="24" fill="#9aa6b2">{correct} / {total} correct · best streak {bestStreak}{readinessLabel ? ` · ${readinessLabel}` : ""}</text>

      {/* Badges row */}
      <g transform="translate(56,520)">
        {badgesShort.map((b, i) => (
          <g key={i} transform={`translate(${i * 230},0)`}>
            <rect width="210" height="56" rx="28" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.18)" />
            <text x="105" y="36" textAnchor="middle" fontFamily="'Hanken Grotesk',sans-serif" fontWeight="800" fontSize="20" fill="#f4efe6">{String(b).slice(0, 18)}</text>
          </g>
        ))}
      </g>

      {/* CTA right */}
      <g transform="translate(820,440)">
        <rect width="324" height="86" rx="20" fill="url(#num)" />
        <text x="162" y="55" textAnchor="middle" fontFamily="'Unbounded',sans-serif" fontWeight="800" fontSize="26" fill="#0a0a0a">Beat my score</text>
      </g>
      <text x="982" y="560" textAnchor="middle" fontFamily="'Space Mono',monospace" fontSize="16" fill="#9aa6b2">quizkosh · take the challenge</text>
    </svg>
  );
}

function Results({ result, scoring, role, user, onRetryWrong, onReplay, onReplaySameSetup, onNew, onHome }) {
  const { config, questions, answers, score, bestStreak } = result;
  const [tab, setTab] = useState("summary");
  const [ratings, setRatings] = useState({});
  const [copied, setCopied] = useState(false);
  // Replay-with-same-setup confirmation dialog (Feb 2026, additive only).
  const [replaySetupConfirm, setReplaySetupConfirm] = useState(false);
  const correct = answers.filter((a) => a.correct).length;
  const wrong = answers.filter((a) => !a.correct && !a.timedOut).length;
  const timedOut = answers.filter((a) => a.timedOut).length;
  const N = answers.length;
  const accuracy = N ? Math.round((correct / N) * 100) : 0;
  const avg = N ? Math.round(answers.reduce((s, a) => s + a.timeMs, 0) / N / 100) / 10 : 0;
  const r = readiness(accuracy);
  const wrongList = answers.filter((a) => !a.correct);
  const bookmarks = result.bookmarks || [];
  const timed = config.mode !== "practice";
  const modeName = MODES.find((m) => m.id === config.mode)?.name || config.mode;

  const byD = groupStats(answers, "difficulty");
  const byS = groupStats(answers, "style");
  const styleKeys = Object.keys(byS);
  const lastK = N >= 3 && answers.slice(-3).every((a) => a.correct);
  const notes = coachNotes(answers, config, accuracy, avg);
  const badges = computeBadges(result, { accuracy, avg, timedOut, N, byD, lastK, timed });
  const earned = badges.filter((b) => b.got);
  const exam = config.scoreRule
    ? ((x) => (x ? { ...x, key: "Custom" } : null))(scoreByRule(config.scoreRule, answers))
    : examScore(config.category, answers, scoring || {});

  const [shareUrl, setShareUrl] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [shareErr, setShareErr] = useState("");
  const cardRef = useRef(null);

  function rate(qi, v) { setRatings((s) => ({ ...s, [qi]: s[qi] === v ? 0 : v })); }

  async function createChallenge() {
    if (shareUrl) {
      try { await navigator.clipboard?.writeText(shareUrl); } catch {}
      setCopied(true); setTimeout(() => setCopied(false), 1700);
      return;
    }
    setShareBusy(true); setShareErr("");
    try {
      const payload = {
        category: config.category,
        mode: config.mode,
        score, accuracy, correct, total: N,
        best_streak: bestStreak,
        badges: earned.map((b) => b.name),
        questions: questions.map((q) => ({ q: q.q, options: q.options, correct: q.correct, explanation: q.explanation || "", hint: q.hint || "", difficulty: q.difficulty || "medium", style: q.style || "concept" })),
        timer: config.timer || 15,
        difficulty: config.difficulty || "medium",
        challenger_name: (user && user.name) || config.name || "A friend",
        topic: config.topic || "",
      };
      const data = await backend.createShare(payload);
      const base = window.location.origin + window.location.pathname;
      const url = `${base}?c=${data.slug}`;
      setShareUrl(url);
      try { await navigator.clipboard?.writeText(url); } catch {}
      setCopied(true); setTimeout(() => setCopied(false), 1700);
    } catch (e) {
      if (e && e.status === 401) setShareErr("Log in to create a shareable challenge.");
      else setShareErr(e.message || "Couldn't create the challenge.");
    } finally { setShareBusy(false); }
  }

  function downloadCard() {
    try {
      const svg = cardRef.current?.querySelector("svg");
      if (!svg) return;
      const xml = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 1200; canvas.height = 630;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 1200, 630);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const pngUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = pngUrl;
          a.download = `quizkosh-${(config.category || "result").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${accuracy}pct.png`;
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => { URL.revokeObjectURL(pngUrl); URL.revokeObjectURL(url); }, 1500);
        }, "image/png");
      };
      img.onerror = () => { setShareErr("Couldn't render the card. Try again."); URL.revokeObjectURL(url); };
      img.src = url;
    } catch (e) { setShareErr(e.message || "Download failed."); }
  }
  const renderThumbs = (qi) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
      <span className="qz-muted" style={{ fontSize: 12.5 }}>Rate this question</span>
      <button className="btn btn--icon" style={{ width: 34, height: 34 }} title="Good question" onClick={() => rate(qi, 1)}><ThumbsUp size={15} color={ratings[qi] === 1 ? "var(--correct)" : "currentColor"} /></button>
      <button className="btn btn--icon" style={{ width: 34, height: 34 }} title="Needs work" onClick={() => rate(qi, -1)}><ThumbsDown size={15} color={ratings[qi] === -1 ? "var(--wrong)" : "currentColor"} /></button>
      {ratings[qi] ? <span className="qz-muted pop" style={{ fontSize: 12.5 }}>Thanks for the feedback!</span> : null}
    </div>
  );

  return (
    <div className="qz-wrap" style={{ paddingTop: 10 }}>
      <div className="card glow pop" style={{ textAlign: "center", padding: 34 }}>
        <div className="qz-eyebrow">{config.category} · {modeName}</div>
        {(() => {
          const src = (questions && questions[0] && questions[0]._source) || "ai";
          return (
            <div className="chip pop" data-testid="result-source" style={{ marginTop: 8, fontWeight: 700, fontSize: 12, color: src === "bank" ? "var(--accent2)" : "var(--accent)" }}>
              {src === "bank" ? <><BookOpen size={13} /> From your question bank</> : <><Sparkles size={13} /> Generated by AI</>}
            </div>
          );
        })()}
        <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "baseline", margin: "10px 0 2px" }}>
          <span className="qz-display qz-mono" style={{ fontSize: 64, color: "var(--accent)" }}>{accuracy}</span>
          <span className="qz-display" style={{ fontSize: 28, color: "var(--muted)" }}>%</span>
        </div>
        <p className="qz-muted">accuracy · {correct} of {N} correct{result.endedEarly ? " · ran out of lives" : ""}</p>
        <div className="chip pop" style={{ marginTop: 14, borderColor: r.c, color: r.c, fontWeight: 800 }}><Target size={15} /> {r.t}</div>
        {earned.length > 0 && (
          <div style={{ display: "flex", gap: 7, justifyContent: "center", flexWrap: "wrap", marginTop: 14 }}>
            {earned.map((b) => <span key={b.id} className="chip" style={{ borderColor: b.c, color: b.c, fontWeight: 700, fontSize: 12.5 }}><b.icon size={13} /> {b.name}</span>)}
          </div>
        )}
      </div>

      {exam && (
        <div className="card glow pop" style={{ marginTop: 14, padding: 22, borderLeft: "4px solid var(--accent2)" }}>
          <div className="qz-eyebrow" style={{ marginBottom: 8 }}><BadgeCheck size={13} style={{ verticalAlign: -2 }} /> {exam.key} scoring</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span className="qz-display" style={{ fontSize: 40, color: "var(--accent2)", lineHeight: 1 }}>{exam.label}</span>
            {exam.kind === "marks" && <span className="qz-muted">marks</span>}
            {exam.kind === "scaled" && <span className="chip" style={{ borderColor: exam.pass ? "var(--correct)" : "var(--gold)", color: exam.pass ? "var(--correct)" : "var(--gold)", fontWeight: 700 }}>{exam.pass ? "Pass" : "Below pass"}</span>}
          </div>
          <p className="qz-muted" style={{ marginTop: 8, fontSize: 13.5 }}>{exam.detail}</p>
          <p className="qz-muted" style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.5 }}>Scored with {exam.key}-specific rules{role === "admin" ? " — editable in the Admin panel." : "."} The game score above uses the standard weighted method.</p>
        </div>
      )}

      <div className="seg" style={{ margin: "20px 0 18px" }}>
        <button className={tab === "summary" ? "act" : ""} onClick={() => setTab("summary")}>Summary</button>
        <button className={tab === "review" ? "act" : ""} onClick={() => setTab("review")}>Review {wrongList.length ? `(${wrongList.length})` : ""}</button>
        <button className={tab === "saved" ? "act" : ""} onClick={() => setTab("saved")}>Saved {bookmarks.length ? `(${bookmarks.length})` : ""}</button>
      </div>

      {tab === "summary" && (
        <div className="fadeUp">
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <div className="stat"><div className="v" style={{ color: "var(--gold)" }}>{score}</div><div className="l">Score</div></div>
            <div className="stat"><div className="v" style={{ color: "var(--correct)" }}>{correct}</div><div className="l">Correct</div></div>
            <div className="stat"><div className="v" style={{ color: "var(--wrong)" }}>{wrong + timedOut}</div><div className="l">Missed</div></div>
            <div className="stat"><div className="v">{bestStreak}</div><div className="l">Best streak</div></div>
            {timed && <div className="stat"><div className="v">{avg}s</div><div className="l">Avg / Q</div></div>}
          </div>

          <div className="r3-grid" style={{ marginBottom: 14 }}>
            <div className="card">
              <div style={{ fontWeight: 800, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}><Award size={17} color="var(--gold)" /> Exam readiness</div>
              <ReadinessGauge accuracy={accuracy} r={r} />
            </div>
            <div className="card">
              <div style={{ fontWeight: 800, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}><Lightbulb size={17} color="var(--accent2)" /> Coach's notes</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 11, marginTop: 8 }}>
                {notes.map((n, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <span style={{ flex: "none", marginTop: 1 }}><n.icon size={17} color={n.tone} /></span>
                    <span style={{ fontSize: 14, lineHeight: 1.5 }}>{n.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}><Star size={17} color="var(--gold)" /> Achievements</div>
              <span className="qz-muted qz-mono" style={{ fontSize: 13 }}>{earned.length}/{badges.length}</span>
            </div>
            <div className="badges">
              {badges.map((b) => (
                <div key={b.id} className={"badge" + (b.got ? "" : " badge--lock")}>
                  <div className="ic" style={{ background: b.got ? b.c : "var(--faint)", color: b.got ? "#0b0b14" : "var(--muted)" }}><b.icon size={22} /></div>
                  <div className="bn">{b.name}</div>
                  <div className="bd">{b.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="r3-grid">
            <div className="card">
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Accuracy by difficulty</div>
              {["easy", "medium", "hard"].filter((k) => byD[k]).map((k) => {
                const v = byD[k], p = pctOf(v.correct, v.total);
                return (
                  <div className="r3-row" key={k} title={`${v.correct}/${v.total} correct`}>
                    <span className="lab" style={{ color: DIFF_META[k].c }}>{DIFF_META[k].label}</span>
                    <div className="r3-track"><i style={{ width: p + "%", background: DIFF_META[k].c }} /></div>
                    <span className="pct qz-mono">{p}%</span>
                  </div>
                );
              })}
            </div>
            <div className="card">
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Outcome breakdown</div>
              <div style={{ height: 168 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={[{ name: "Correct", value: correct }, { name: "Wrong", value: wrong }, { name: "Timed out", value: timedOut }].filter((d) => d.value)} dataKey="value" innerRadius={46} outerRadius={70} paddingAngle={3} stroke="none">
                      {[<Cell key="c" fill="var(--correct)" />, <Cell key="w" fill="var(--wrong)" />, <Cell key="t" fill="var(--faint)" />]}
                    </Pie>
                    <RTooltip contentStyle={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--text)" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", gap: 14, justifyContent: "center", fontSize: 12.5, flexWrap: "wrap" }}>
                <span><span style={{ color: "var(--correct)" }}>●</span> Correct</span>
                <span><span style={{ color: "var(--wrong)" }}>●</span> Wrong</span>
                <span><span style={{ color: "var(--faint)" }}>●</span> Timed out</span>
              </div>
            </div>
          </div>

          {styleKeys.length > 1 && (
            <div className="card" style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 800, marginBottom: 12 }}>Accuracy by question type</div>
              {styleKeys.map((k) => {
                const v = byS[k], p = pctOf(v.correct, v.total);
                return (
                  <div key={k} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
                      <span style={{ fontWeight: 600 }}>{titleCase(k)}</span>
                      <span className="qz-mono qz-muted">{v.correct}/{v.total}</span>
                    </div>
                    <div className="r3-track"><i style={{ width: p + "%", background: barColor(p) }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "review" && (
        <div className="fadeUp">
          {wrongList.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 40 }}><Check size={36} color="var(--correct)" /><h3 className="qz-display" style={{ fontSize: 22, marginTop: 10 }}>Clean sweep — nothing to review!</h3></div>
          ) : wrongList.map((a, i) => {
            const q = questions[a.qIndex];
            return (
              <div className="card" key={i} style={{ marginBottom: 12 }}>
                <h4 className="qz-display" style={{ fontSize: 17, marginBottom: 10 }}>{q.q}</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {a.selected !== null && !Array.isArray(a.selected) && <div className="chip" style={{ borderColor: "var(--wrong)", color: "var(--wrong)" }}><X size={14} /> Your answer: {q.options[q._order ? q._order[a.selected] : a.selected]}</div>}
                  {Array.isArray(a.selected) && <div className="chip" style={{ borderColor: "var(--wrong)", color: "var(--wrong)" }}><X size={14} /> Your answer: {a.selected.map((d) => q.options[q._order ? q._order[d] : d]).join(", ")}</div>}
                  {a.timedOut && <div className="chip" style={{ borderColor: "var(--faint)" }}> No answer (timed out)</div>}
                  <div className="chip" style={{ borderColor: "var(--correct)", color: "var(--correct)" }}><Check size={14} /> Correct: {correctIndexes(q).map((c) => q.options[c]).join(", ")}</div>
                </div>
                {q.explanation && <p className="qz-muted" style={{ marginTop: 10, lineHeight: 1.5, fontSize: 14.5 }}>{q.explanation}</p>}
                {renderThumbs(a.qIndex)}
              </div>
            );
          })}
        </div>
      )}

      {tab === "saved" && (
        <div className="fadeUp">
          {bookmarks.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 40 }}><Bookmark size={34} color="var(--accent)" /><h3 className="qz-display" style={{ fontSize: 20, marginTop: 10 }}>No saved questions yet</h3><p className="qz-muted" style={{ marginTop: 6 }}>Tap the bookmark icon during a quiz to keep questions here.</p></div>
          ) : bookmarks.map((bi, i) => {
            const q = questions[bi];
            return (
              <div className="card" key={i} style={{ marginBottom: 12 }}>
                <h4 className="qz-display" style={{ fontSize: 17, marginBottom: 10 }}>{q.q}</h4>
                <div className="chip" style={{ borderColor: "var(--correct)", color: "var(--correct)" }}><Check size={14} /> {correctIndexes(q).map((c) => q.options[c]).join(", ")}</div>
                {q.explanation && <p className="qz-muted" style={{ marginTop: 10, lineHeight: 1.5, fontSize: 14.5 }}>{q.explanation}</p>}
                {renderThumbs(bi)}
              </div>
            );
          })}
        </div>
      )}

      <div ref={cardRef} className="card pop" style={{ marginTop: 22, padding: 16, background: "var(--surface2)" }} data-testid="result-card-preview">
        <div className="qz-eyebrow" style={{ marginBottom: 10 }}><Share2 size={13} style={{ verticalAlign: -2 }} /> Shareable card</div>
        <ResultCardSVG
          name={(user && user.name) || config.name || "You"}
          category={config.category}
          mode={modeName}
          accuracy={accuracy}
          score={score}
          correct={correct}
          total={N}
          bestStreak={bestStreak}
          readinessLabel={r.t}
          badges={earned.map((b) => b.name)}
        />
        {shareUrl && (
          <div className="banner" style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }} data-testid="share-url-banner">
            <span className="qz-mono" style={{ fontSize: 12, wordBreak: "break-all", flex: 1, minWidth: 0 }}>{shareUrl}</span>
            <button data-testid="copy-link-btn" className="btn" style={{ padding: "8px 12px" }} onClick={createChallenge}><Share2 size={15} /> Copy link</button>
          </div>
        )}
        {shareErr && <p style={{ color: "var(--wrong)", marginTop: 10, fontSize: 13 }}>{shareErr}</p>}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 22 }}>
        {wrongList.length > 0 && <button className="btn btn--primary" onClick={onRetryWrong}><RotateCcw size={18} /> Retry wrong ({wrongList.length})</button>}
        <button className="btn" onClick={onReplay}><RotateCcw size={18} /> Replay all</button>
        <button className="btn" onClick={onNew}><Wand2 size={18} /> New quiz</button>
        {onReplaySameSetup && (
          <button
            className="btn"
            data-testid="replay-same-setup-btn"
            onClick={() => setReplaySetupConfirm(true)}
            title="Generate a fresh quiz using the same settings"
          >
            <Sparkles size={18} /> Replay with same setup
          </button>
        )}
        <button data-testid="challenge-friend-btn" className="btn" disabled={shareBusy} onClick={createChallenge}>
          {shareBusy ? <Loader2 size={18} className="spin" /> : <Crown size={18} color="var(--gold)" />}
          {shareUrl ? " Copy challenge link" : " Challenge a friend"}
        </button>
        <button className="btn btn--ghost" style={{ marginLeft: "auto" }} onClick={onHome}><Home size={18} /> Home</button>
      </div>

      {replaySetupConfirm && (
        <div
          className="modal-backdrop"
          data-testid="replay-same-setup-confirm"
          onClick={() => setReplaySetupConfirm(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            display: "grid", placeItems: "center", zIndex: 90,
          }}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 460, width: "92%", padding: 24,
              borderLeft: "3px solid var(--accent2)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <Sparkles size={20} color="var(--accent2)" />
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Generate a New Quiz?</h3>
            </div>
            <p style={{ margin: "8px 0 16px", color: "var(--muted)", fontSize: 14, lineHeight: 1.5 }}>
              This will create a brand-new quiz using your previous settings. New
              questions will be generated, and questions from your last quiz
              should not be repeated where possible. Do you want to continue?
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                className="btn btn--ghost"
                data-testid="replay-same-setup-cancel"
                onClick={() => setReplaySetupConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn--primary"
                data-testid="replay-same-setup-generate"
                onClick={() => {
                  setReplaySetupConfirm(false);
                  onReplaySameSetup && onReplaySameSetup();
                }}
              >
                <Wand2 size={16} /> Generate Quiz
              </button>
            </div>
          </div>
        </div>
      )}

      {copied && <div className="toast pop" data-testid="copied-toast">Copied to clipboard ✓</div>}
    </div>
  );
}

/* ===========================================================================
   KIDS ZONE — ages 3–5 · "Tap the picture" · separate full-screen experience
   Audio-first (questions are read aloud), big targets, gentle + celebratory.
   15 picture decks + a counting game + a grown-ups panel that builds a custom
   game from a topic / pasted text / uploaded doc or photo / link (via AI).
   =========================================================================== */
const KIDS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&display=swap');
#kidz{ position:fixed; inset:0; z-index:60; overflow-y:auto; overflow-x:hidden;
  font-family:'Fredoka','Hanken Grotesk',system-ui,sans-serif; color:#3b2b4a;
  background:linear-gradient(165deg,#fff3cf 0%,#d8f3ff 48%,#ffe1ef 100%); }
#kidz::before{ content:""; position:fixed; inset:0; pointer-events:none; z-index:0;
  background:
   radial-gradient(closest-side at 12% 16%, rgba(255,209,102,.55), transparent 70%),
   radial-gradient(closest-side at 88% 12%, rgba(126,232,250,.55), transparent 70%),
   radial-gradient(closest-side at 84% 88%, rgba(255,140,198,.5), transparent 70%),
   radial-gradient(closest-side at 16% 86%, rgba(168,230,160,.5), transparent 70%); }
#kidz *{ box-sizing:border-box; }
.kidz-wrap{ position:relative; z-index:1; max-width:760px; margin:0 auto; padding:0 18px 60px; }

.kidz-top{ position:sticky; top:0; z-index:5; display:flex; align-items:center; justify-content:space-between; padding:16px 14px; }
.kidz-round{ width:58px; height:58px; border-radius:50%; border:none; cursor:pointer; font-size:26px; line-height:1;
  display:inline-flex; align-items:center; justify-content:center; color:#0c2a4d;
  background:#fff; box-shadow:0 8px 0 rgba(0,0,0,.08),0 10px 24px rgba(0,0,0,.12); transition:transform .12s; }
.kidz-round svg{ display:block }
.kidz-round:active{ transform:translateY(3px); box-shadow:0 4px 0 rgba(0,0,0,.08); }
.kidz-brand{ font-weight:700; font-size:25px; color:#6a4c93; letter-spacing:-.01em; text-shadow:0 2px 0 #fff; }

.kidz-title{ text-align:center; font-weight:700; font-size:40px; line-height:1.1; margin:4px 0 2px; color:#5f3dc4; text-shadow:0 3px 0 #fff; }
.kidz-sub{ text-align:center; font-size:19px; color:#7a5ea8; margin-bottom:18px; font-weight:600; }

.kidz-cats{ display:grid; grid-template-columns:repeat(2,1fr); gap:16px; }
.kidz-cat{ border:none; cursor:pointer; border-radius:28px; padding:24px 16px; color:#fff; text-align:center;
  font-family:inherit; font-weight:700; font-size:22px; position:relative; text-shadow:0 2px 4px rgba(0,0,0,.18);
  box-shadow:0 10px 0 rgba(0,0,0,.13),0 16px 30px rgba(0,0,0,.16); transition:transform .14s, box-shadow .14s; }
.kidz-cat:hover{ transform:translateY(-4px); }
.kidz-cat:active{ transform:translateY(4px); box-shadow:0 4px 0 rgba(0,0,0,.13),0 8px 18px rgba(0,0,0,.16); }
.kidz-cat .e{ display:block; font-size:56px; line-height:1; margin-bottom:6px; filter:drop-shadow(0 4px 6px rgba(0,0,0,.18)); }
.kidz-cat--wide{ grid-column:1 / -1; }
.kidz-badge{ display:inline-block; background:#fff; color:#6a4c00; font-weight:700; font-size:12px; padding:3px 9px; border-radius:999px; margin-left:8px; vertical-align:middle; text-shadow:none; }

.kidz-stars{ display:flex; justify-content:center; gap:7px; margin:6px 0 14px; font-size:28px; flex-wrap:wrap; }
.kidz-stars .s{ filter:grayscale(1) opacity(.32); transition:transform .3s, filter .3s; }
.kidz-stars .s.on{ filter:none; transform:scale(1.18); }

.kidz-mascot{ display:flex; justify-content:center; margin:4px 0; }
.kidz-bob{ animation:kidbob 2.6s ease-in-out infinite; }
@keyframes kidbob{ 0%,100%{ transform:translateY(0) } 50%{ transform:translateY(-9px) } }
.kidz-pop{ animation:kidpop .5s cubic-bezier(.2,1.5,.4,1) both; }
@keyframes kidpop{ from{ transform:scale(.6); opacity:0 } to{ transform:scale(1); opacity:1 } }

.kidz-prompt{ display:flex; align-items:center; justify-content:center; gap:12px; text-align:center;
  background: linear-gradient(135deg, #fff7ed, #fff);
  border: 2px solid #ffe4c4;
  border-radius: 22px;
  padding: 18px 22px;
  box-shadow: 0 10px 26px rgba(245,134,38,.12);
  margin: 0 auto 22px;
  max-width: 720px;
}
.kidz-prompt .txt{ font-weight:900; font-size:32px; color:#0c2a4d; line-height: 1.15; }
.kidz-letter{ position:absolute; top:10px; left:14px; font-family: var(--fd); font-weight:900; font-size: 20px; color:#0c2a4d; background: rgba(255,255,255,.9); border-radius: 999px; min-width: 32px; height: 32px; display: grid; place-items: center; padding: 0 8px; box-shadow: 0 2px 6px rgba(0,0,0,.08); z-index: 2 }
  background:#fff; border-radius:26px; padding:16px 20px; margin:6px auto 18px; max-width:600px;
  box-shadow:0 10px 0 rgba(0,0,0,.06),0 14px 30px rgba(0,0,0,.12); }
.kidz-prompt .txt-legacy{ font-weight:700; font-size:27px; color:#4a2f7a; }
.kidz-say{ width:54px;height:54px;border-radius:50%;border:none;cursor:pointer;flex:none;font-size:24px; line-height:1;
  background:linear-gradient(135deg,#7ee8fa,#74c0fc); box-shadow:0 6px 0 rgba(0,0,0,.1); transition:transform .12s; }
.kidz-say:active{ transform:translateY(3px); }

.kidz-count{ display:flex; flex-wrap:wrap; justify-content:center; gap:10px 14px; background:#fff; border-radius:28px;
  padding:20px; margin:4px auto 14px; max-width:560px; font-size:56px; line-height:1;
  box-shadow:0 10px 0 rgba(0,0,0,.06),0 14px 30px rgba(0,0,0,.12); }

.kidz-grid{ display:grid; grid-template-columns:repeat(2,1fr); gap:18px; max-width:600px; margin:0 auto; }
.kidz-card{ --cc:#ff6b6b; border:6px solid var(--cc); background:#fff; border-radius:30px; cursor:pointer;
  min-height:168px; display:grid; place-items:center; position:relative; padding:14px;
  box-shadow:0 10px 0 rgba(0,0,0,.10),0 16px 30px rgba(0,0,0,.14); transition:transform .14s, box-shadow .14s; }
.kidz-card:hover{ transform:translateY(-4px); }
.kidz-card:active{ transform:translateY(4px) scale(.99); box-shadow:0 4px 0 rgba(0,0,0,.10); }
.kidz-card[disabled]{ cursor:default; }
.kidz-emoji{ font-size:84px; line-height:1; filter:drop-shadow(0 6px 8px rgba(0,0,0,.18)); }
.kidz-swatch{ width:104px;height:104px;border-radius:24px;display:block;box-shadow:inset 0 -8px 14px rgba(0,0,0,.18); }
.kidz-num{ font-size:84px; font-weight:700; color:var(--cc); line-height:1; }
.kidz-dots{ display:flex; gap:5px; justify-content:center; margin-top:8px; flex-wrap:wrap; max-width:180px; }
.kidz-dots i{ width:11px;height:11px;border-radius:50%;background:var(--cc);display:block; }
.kidz-card--correct{ border-color:#37c871 !important; background:#eafff3; animation:kidcorrect .5s cubic-bezier(.2,1.5,.4,1); }
@keyframes kidcorrect{ 0%{ transform:scale(1) } 40%{ transform:scale(1.12) } 100%{ transform:scale(1.04) } }
.kidz-card--wrong{ animation:kidwiggle .45s; opacity:.5; }
@keyframes kidwiggle{ 0%,100%{ transform:translateX(0) } 20%{ transform:translateX(-9px) } 40%{ transform:translateX(9px) } 60%{ transform:translateX(-6px) } 80%{ transform:translateX(6px) } }
.kidz-tick{ position:absolute; top:-14px; right:-14px; width:48px;height:48px;border-radius:50%;
  background:#37c871; color:#fff; font-size:28px; display:grid; place-items:center;
  box-shadow:0 6px 14px rgba(0,0,0,.2); animation:kidpop .4s cubic-bezier(.2,1.6,.4,1) both; }

.kidz-panel{ background:#fff; border-radius:28px; padding:20px; max-width:600px; margin:0 auto;
  box-shadow:0 10px 0 rgba(0,0,0,.06),0 14px 30px rgba(0,0,0,.12); }
.kidz-srcrow{ display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-bottom:14px; }
.kidz-src{ border:3px solid #ffe0ef; background:#fff7fb; border-radius:20px; padding:14px; cursor:pointer;
  font-family:inherit; font-weight:700; font-size:17px; color:#6a4c93; display:flex; align-items:center; gap:10px; transition:transform .12s, border .15s, background .15s; }
.kidz-src .si{ font-size:26px; line-height:1; }
.kidz-src.on{ border-color:#ff8fc0; background:#ffeaf4; }
.kidz-src:active{ transform:translateY(2px); }
.kidz-input,.kidz-textarea{ width:100%; font-family:inherit; font-size:18px; color:#3b2b4a; background:#f7f3ff;
  border:3px solid #e6dbff; border-radius:18px; padding:14px 16px; outline:none; transition:border .15s; }
.kidz-input:focus,.kidz-textarea:focus{ border-color:#b197fc; }
.kidz-textarea{ min-height:120px; resize:vertical; line-height:1.5; }
.kidz-file{ display:flex; align-items:center; gap:12px; background:#f7f3ff; border:3px dashed #cdb8ff; border-radius:18px;
  padding:16px; cursor:pointer; font-weight:700; color:#6a4c93; }
.kidz-note{ text-align:center; color:#9b8ab5; font-size:13.5px; margin-top:12px; font-weight:600; }
.kidz-err{ color:#e03131; font-weight:700; margin-top:10px; text-align:center; }

.kidz-loaddots{ display:flex; gap:10px; justify-content:center; margin-top:10px; }
.kidz-loaddots i{ width:14px;height:14px;border-radius:50%; background:#ff8fc0; animation:kidbob 1s ease-in-out infinite; }
.kidz-loaddots i:nth-child(2){ background:#7ee8fa; animation-delay:.15s; }
.kidz-loaddots i:nth-child(3){ background:#ffd166; animation-delay:.3s; }

.kidz-win{ text-align:center; padding-top:6px; }
.kidz-win h2{ font-weight:700; font-size:46px; color:#5f3dc4; text-shadow:0 3px 0 #fff; margin:4px 0; }
.kidz-bigstars{ font-size:50px; letter-spacing:6px; margin:6px 0 2px; }
.kidz-actions{ display:flex; flex-direction:column; gap:14px; max-width:420px; margin:18px auto 0; }
.kidz-btn{ border:none; cursor:pointer; font-family:inherit; font-weight:700; font-size:23px; color:#fff; text-shadow:0 2px 4px rgba(0,0,0,.18);
  border-radius:24px; padding:18px; box-shadow:0 9px 0 rgba(0,0,0,.14),0 14px 26px rgba(0,0,0,.18); transition:transform .12s; }
.kidz-btn:active{ transform:translateY(4px); box-shadow:0 4px 0 rgba(0,0,0,.14); }
.kidz-btn[disabled]{ opacity:.5; cursor:not-allowed; }

.kidz-confetti{ position:absolute; inset:0; pointer-events:none; z-index:9; overflow:hidden; }
.kidz-confetti span{ position:absolute; top:-50px; animation-name:kidfall; animation-timing-function:linear; animation-fill-mode:forwards; }
@keyframes kidfall{ 0%{ transform:translateY(0) rotate(0); opacity:1 } 85%{ opacity:1 } 100%{ transform:translateY(112vh) rotate(560deg); opacity:0 } }

@media(max-width:540px){
  .kidz-title{ font-size:31px } .kidz-cat{ font-size:18px; padding:18px 12px } .kidz-cat .e{ font-size:46px }
  .kidz-emoji{ font-size:64px } .kidz-num{ font-size:64px } .kidz-card{ min-height:138px; border-width:5px }
  .kidz-prompt .txt{ font-size:22px } .kidz-win h2{ font-size:36px } .kidz-brand{ font-size:21px } .kidz-count{ font-size:44px }
}
`;

const KID_DECKS = [
  { id: "animals", label: "Animals", icon: "🐶", color: "#ff8fab", items: [
    { n: "dog", v: "🐶" }, { n: "cat", v: "🐱" }, { n: "rabbit", v: "🐰" }, { n: "lion", v: "🦁" },
    { n: "frog", v: "🐸" }, { n: "monkey", v: "🐵" }, { n: "pig", v: "🐷" }, { n: "cow", v: "🐮" },
    { n: "chick", v: "🐥" }, { n: "penguin", v: "🐧" }, { n: "elephant", v: "🐘" }, { n: "tiger", v: "🐯" },
    { n: "bear", v: "🐻" }, { n: "horse", v: "🐴" }, { n: "fish", v: "🐟" }, { n: "owl", v: "🦉" },
    { n: "panda", v: "🐼" }, { n: "koala", v: "🐨" }, { n: "giraffe", v: "🦒" }, { n: "fox", v: "🦊" } ] },
  { id: "fruits", label: "Fruits", icon: "🍎", color: "#ff6b6b", items: [
    { n: "apple", v: "🍎" }, { n: "banana", v: "🍌" }, { n: "grapes", v: "🍇" }, { n: "orange", v: "🍊" },
    { n: "strawberry", v: "🍓" }, { n: "watermelon", v: "🍉" }, { n: "peach", v: "🍑" }, { n: "pineapple", v: "🍍" },
    { n: "cherry", v: "🍒" }, { n: "mango", v: "🥭" }, { n: "lemon", v: "🍋" }, { n: "coconut", v: "🥥" },
    { n: "pear", v: "🍐" }, { n: "kiwi", v: "🥝" }, { n: "tomato", v: "🍅" }, { n: "blueberries", v: "🫐" } ] },
  { id: "vehicles", label: "Vehicles", icon: "🚗", color: "#4dabf7", items: [
    { n: "car", v: "🚗" }, { n: "bus", v: "🚌" }, { n: "fire truck", v: "🚒" }, { n: "police car", v: "🚓" },
    { n: "bicycle", v: "🚲" }, { n: "airplane", v: "✈️" }, { n: "rocket", v: "🚀" }, { n: "boat", v: "⛵" },
    { n: "train", v: "🚂" }, { n: "helicopter", v: "🚁" }, { n: "tractor", v: "🚜" }, { n: "truck", v: "🚚" },
    { n: "taxi", v: "🚕" }, { n: "scooter", v: "🛴" }, { n: "ambulance", v: "🚑" }, { n: "ship", v: "🚢" } ] },
  { id: "food", label: "Food", icon: "🍕", color: "#ffa94d", items: [
    { n: "pizza", v: "🍕" }, { n: "burger", v: "🍔" }, { n: "fries", v: "🍟" }, { n: "hot dog", v: "🌭" },
    { n: "donut", v: "🍩" }, { n: "cookie", v: "🍪" }, { n: "cake", v: "🍰" }, { n: "ice cream", v: "🍦" },
    { n: "sandwich", v: "🥪" }, { n: "cheese", v: "🧀" }, { n: "carrot", v: "🥕" }, { n: "corn", v: "🌽" },
    { n: "bread", v: "🍞" }, { n: "egg", v: "🥚" }, { n: "milk", v: "🥛" }, { n: "popcorn", v: "🍿" }, { n: "candy", v: "🍬" } ] },
  { id: "ocean", label: "Ocean", icon: "🐬", color: "#3bc9db", items: [
    { n: "fish", v: "🐟" }, { n: "dolphin", v: "🐬" }, { n: "whale", v: "🐳" }, { n: "octopus", v: "🐙" },
    { n: "crab", v: "🦀" }, { n: "shrimp", v: "🦐" }, { n: "shark", v: "🦈" }, { n: "shell", v: "🐚" },
    { n: "turtle", v: "🐢" }, { n: "squid", v: "🦑" }, { n: "lobster", v: "🦞" }, { n: "seal", v: "🦭" } ] },
  { id: "bugs", label: "Birds & Bugs", icon: "🦋", color: "#69db7c", items: [
    { n: "bird", v: "🐦" }, { n: "eagle", v: "🦅" }, { n: "duck", v: "🦆" }, { n: "parrot", v: "🦜" },
    { n: "butterfly", v: "🦋" }, { n: "bee", v: "🐝" }, { n: "ladybug", v: "🐞" }, { n: "caterpillar", v: "🐛" },
    { n: "snail", v: "🐌" }, { n: "ant", v: "🐜" }, { n: "spider", v: "🕷️" }, { n: "swan", v: "🦢" } ] },
  { id: "nature", label: "Nature", icon: "🌈", color: "#ffd43b", items: [
    { n: "sun", v: "☀️" }, { n: "moon", v: "🌙" }, { n: "star", v: "⭐" }, { n: "rainbow", v: "🌈" },
    { n: "cloud", v: "☁️" }, { n: "rain", v: "🌧️" }, { n: "snow", v: "❄️" }, { n: "wave", v: "🌊" },
    { n: "tree", v: "🌳" }, { n: "flower", v: "🌸" }, { n: "mushroom", v: "🍄" }, { n: "leaf", v: "🍃" },
    { n: "cactus", v: "🌵" }, { n: "fire", v: "🔥" } ] },
  { id: "body", label: "My Body", icon: "👀", color: "#f06595", kind: "emoji", items: [
    { n: "eye", v: "👁️" }, { n: "ear", v: "👂" }, { n: "nose", v: "👃" }, { n: "mouth", v: "👄" },
    { n: "hand", v: "✋" }, { n: "foot", v: "🦶" }, { n: "tooth", v: "🦷" }, { n: "tongue", v: "👅" },
    { n: "leg", v: "🦵" }, { n: "arm", v: "💪" } ] },
  { id: "home", label: "At Home", icon: "🏠", color: "#b197fc", items: [
    { n: "chair", v: "🪑" }, { n: "bed", v: "🛏️" }, { n: "door", v: "🚪" }, { n: "window", v: "🪟" },
    { n: "lamp", v: "💡" }, { n: "tv", v: "📺" }, { n: "phone", v: "📱" }, { n: "teddy", v: "🧸" },
    { n: "balloon", v: "🎈" }, { n: "gift", v: "🎁" }, { n: "candle", v: "🕯️" }, { n: "clock", v: "⏰" },
    { n: "book", v: "📖" }, { n: "key", v: "🔑" }, { n: "umbrella", v: "☂️" }, { n: "cup", v: "🥤" } ] },
  { id: "clothes", label: "Clothes", icon: "👕", color: "#f783ac", items: [
    { n: "shirt", v: "👕" }, { n: "pants", v: "👖" }, { n: "dress", v: "👗" }, { n: "shoe", v: "👞" },
    { n: "sneaker", v: "👟" }, { n: "sock", v: "🧦" }, { n: "glove", v: "🧤" }, { n: "hat", v: "👒" },
    { n: "cap", v: "🧢" }, { n: "scarf", v: "🧣" }, { n: "coat", v: "🧥" }, { n: "boot", v: "🥾" },
    { n: "crown", v: "👑" }, { n: "glasses", v: "👓" }, { n: "backpack", v: "🎒" } ] },
  { id: "music", label: "Music", icon: "🎸", color: "#a855ff", items: [
    { n: "guitar", v: "🎸" }, { n: "drum", v: "🥁" }, { n: "trumpet", v: "🎺" }, { n: "violin", v: "🎻" },
    { n: "piano", v: "🎹" }, { n: "saxophone", v: "🎷" }, { n: "banjo", v: "🪕" }, { n: "microphone", v: "🎤" },
    { n: "accordion", v: "🪗" }, { n: "maracas", v: "🪇" }, { n: "bell", v: "🔔" } ] },
  { id: "sports", label: "Sports", icon: "⚽", color: "#15aabf", items: [
    { n: "soccer ball", v: "⚽" }, { n: "basketball", v: "🏀" }, { n: "football", v: "🏈" }, { n: "baseball", v: "⚾" },
    { n: "tennis ball", v: "🎾" }, { n: "volleyball", v: "🏐" }, { n: "ping pong", v: "🏓" }, { n: "frisbee", v: "🥏" },
    { n: "badminton", v: "🏸" }, { n: "bowling", v: "🎳" }, { n: "hockey", v: "🏒" }, { n: "skateboard", v: "🛹" } ] },
  { id: "colors", label: "Colors", icon: "🎨", color: "#9775fa", kind: "color", items: [
    { n: "red", v: "#ff4d4d" }, { n: "blue", v: "#4d8bff" }, { n: "green", v: "#2fb344" }, { n: "yellow", v: "#ffd000" },
    { n: "orange", v: "#ff922b" }, { n: "purple", v: "#9b59b6" }, { n: "pink", v: "#ff6fae" }, { n: "brown", v: "#a9744f" },
    { n: "black", v: "#222222" }, { n: "white", v: "#ffffff" } ] },
  { id: "shapes", label: "Shapes", icon: "⭐", color: "#ffb703", kind: "shape", items: [
    { n: "circle" }, { n: "square" }, { n: "triangle" }, { n: "star" }, { n: "heart" }, { n: "diamond" }, { n: "rectangle" }, { n: "oval" } ] },
  { id: "numbers", label: "Numbers", icon: "🔢", color: "#20c997", kind: "num", items: [
    { n: "one", v: "1" }, { n: "two", v: "2" }, { n: "three", v: "3" }, { n: "four", v: "4" }, { n: "five", v: "5" },
    { n: "six", v: "6" }, { n: "seven", v: "7" }, { n: "eight", v: "8" }, { n: "nine", v: "9" } ] },
];
const KID_COUNTING = { id: "counting", label: "Counting", icon: "🔢", color: "#f783ac" };
const KID_SURPRISE = { id: "surprise", label: "Surprise Mix!", icon: "🎉", color: "#ff8fab" };
const KID_ROUND = 8;
const COUNT_EMOJIS = ["🍎", "🍌", "⭐", "🐶", "🐱", "🐰", "🐸", "🐝", "🦋", "🌸", "🌟", "🎈", "🎁", "🍓", "🚗", "🎲", "🐠", "🐢"];
const KID_PRAISE = ["Yay! That's right!", "Woohoo! Great job!", "Awesome!", "You got it!", "Hooray!", "Well done!", "Super!", "Brilliant!"];
const KID_NUDGE = ["Oops! Try again!", "Almost! Try another one!", "Not that one — you can do it!", "Try again!", "So close! Have another go!"];
const krand = (a) => a[Math.floor(Math.random() * a.length)];
function kshade(hex) {
  const h = String(hex).replace("#", ""); const n = parseInt(h, 16);
  const r = Math.round(((n >> 16) & 255) * 0.78), g = Math.round(((n >> 8) & 255) * 0.78), b = Math.round((n & 255) * 0.78);
  return `rgb(${r},${g},${b})`;
}

let _kctx;
function kidSound(type) {
  try {
    _kctx = _kctx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _kctx, now = ctx.currentTime;
    const notes = type === "correct" ? [523, 659, 784] : type === "win" ? [523, 659, 784, 1047, 1319] : [392, 294];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = type === "wrong" ? "sine" : "triangle"; o.frequency.value = f;
      const t = now + i * (type === "win" ? 0.12 : 0.09), vol = type === "wrong" ? 0.12 : 0.16;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
      o.connect(g); g.connect(ctx.destination); o.start(t); o.stop(t + 0.24);
    });
  } catch {}
}
function kidSpeak(text) {
  if (!("speechSynthesis" in window)) return;
  try { window.speechSynthesis.cancel(); const u = new SpeechSynthesisUtterance(text); u.rate = 0.95; u.pitch = 1.2; window.speechSynthesis.speak(u); } catch {}
}

function kidPrompt(kind, name) {
  return kind === "color" ? `Which one is ${name}?`
    : kind === "shape" ? `Which one is a ${name}?`
    : kind === "num" ? `Find the number ${name}.`
    : `Which one is the ${name}?`;
}
function makeKidQuestionFor(d, target) {
  const kind = d.kind || "emoji";
  const others = shuffle(d.items.filter((x) => x !== target)).slice(0, 3);
  const opts = shuffle([target, ...others]);
  return { opts, correctIndex: opts.indexOf(target), kind, name: target.n, prompt: kidPrompt(kind, target.n) };
}
function makeKidQuestion(d) { return makeKidQuestionFor(d, d.items[Math.floor(Math.random() * d.items.length)]); }

// Build a target sequence that uses every item before any repeats (and never
// repeats back-to-back), so questions don't recur within a round.
function pickSequence(items, count) {
  const seq = []; let pool = [];
  while (seq.length < count) {
    if (pool.length === 0) {
      pool = shuffle(items);
      if (seq.length && pool.length > 1 && pool[0] === seq[seq.length - 1]) { const t = pool.shift(); pool.splice(1, 0, t); }
    }
    seq.push(pool.shift());
  }
  return seq;
}

function makeKidCountQuestion(emoji, n) {
  emoji = emoji || krand(COUNT_EMOJIS);
  n = n || (1 + Math.floor(Math.random() * 5)); // 1..5
  const nums = new Set([n]);
  while (nums.size < 4) nums.add(1 + Math.floor(Math.random() * 6)); // distractors 1..6
  const opts = shuffle([...nums]).map((x) => ({ n: String(x), v: String(x) }));
  return { kind: "count", countEmoji: emoji, n, opts, correctIndex: opts.findIndex((o) => Number(o.v) === n), name: String(n), prompt: "How many do you see? Count them!" };
}

// Item-level safety for Little Learners: an innocent topic (e.g. "clothes") can
// still yield a single inappropriate item (e.g. "bikini"). Every generated item is
// screened by BOTH its word and its emoji; failures are dropped from the deck.
const KID_BAD_WORD = /\b(bikini|swim\s*suit|swimsuit|swimwear|swim\s*trunks|lingerie|underwear|undies|panty|panties|thong|bra|bras|brassiere|boxers|briefs|g[-\s]?string|negligee|nightie|nightgown|corset|garter|speedo|cleavage|nude|naked|sexy|sex|cigar|cigarette|tobacco|vape|hookah|beer|wine|whiskey|vodka|\brum\b|alcohol|liquor|cocktail|martini|gun|rifle|pistol|revolver|knife|dagger|sword|bomb|grenade|blood|drug|pill|syringe|condom|tampon|coffin|grave|tombstone|zombie|devil|demon|skull|gamble|casino|cannabis|marijuana|cocaine)\b/i;
const KID_BAD_EMOJI = new Set([" ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " "]);
function kidItemOK(name, v) {
  if (KID_BAD_WORD.test(name)) return false;
  const clean = String(v).replace(/\uFE0F/g, "");
  for (const ch of Array.from(clean)) if (KID_BAD_EMOJI.has(ch)) return false;
  return true;
}

function parseKidDeck(text) {
  if (!text) return null;
  let t = String(text).trim().replace(/```json/gi, "").replace(/```/g, "").trim();
  const s = t.indexOf("{"), e = t.lastIndexOf("}");
  if (s === -1 || e === -1 || e < s) return null;
  let obj; try { obj = JSON.parse(t.slice(s, e + 1)); } catch { return null; }
  if (!obj || !Array.isArray(obj.items)) return null;
  const seen = new Set(); const items = [];
  for (const it of obj.items) {
    if (!it || typeof it.n !== "string" || typeof it.v !== "string") continue;
    const name = it.n.trim().toLowerCase(); const v = it.v.trim();
    if (!name || !v) continue;
    if (!/[^\x00-\x7F]/.test(v)) continue;          // must contain an emoji (non-ASCII)
    if (Array.from(v).length > 4) continue;          // keep it a small glyph
    if (!kidItemOK(name, v)) continue;               // drop anything not wholesome for ages 3-5
    if (seen.has(name)) continue; seen.add(name);
    items.push({ n: name, v });
    if (items.length >= 14) break;
  }
  return { label: typeof obj.label === "string" && obj.label.trim() ? obj.label.trim() : "My Game", items };
}

async function generateKidDeck(src) {
  const sys = `You design a "tap the picture" quiz for a 3 to 5 year old child. Output ONLY a single minified JSON object: {"label":string,"items":[{"n":string,"v":string}]}. "n" is a simple lowercase word a young child knows (one or two words). "v" is ONE emoji that best pictures that word. Give 12 to 14 concrete, friendly, instantly recognizable, age-appropriate items related to the requested topic. Every single item must be wholesome for a 3 to 5 year old. For clothing, use only items like shirt, t-shirt, dress, skirt, pants, shorts, socks, shoes, boots, sandals, hat, cap, mittens, gloves, scarf, jacket, coat, sweater, or pajamas — NEVER swimwear (no bikini, no swimsuit), underwear, lingerie, or anything revealing or adult. For ANY topic, never include alcohol, cigarettes, drugs, weapons, blood, gambling, scary creatures, or anything frightening, sad, sexual, or adult. No duplicate words or emojis. If a topic is abstract, choose concrete, picturable objects related to it. If the topic itself is NOT appropriate for a 3 to 5 year old, DO NOT make a game — respond with EXACTLY {"refuse":true,"reason":"short reason"} and nothing else. Otherwise respond with the JSON object and NOTHING else.`;
  let messages;
  if (src.type === "image" && src.imageData) {
    const userText = `Make a picture quiz for a small child based on the attached image.${src.topic ? " Focus topic: " + src.topic + "." : ""} Return the JSON object only.`;
    messages = [{ role: "user", content: [
      { type: "image", source: { type: "base64", media_type: src.imageMime || "image/png", data: src.imageData } },
      { type: "text", text: userText },
    ]}];
  } else {
    const basis = src.text ? `Base it on this material:\n"""${String(src.text).slice(0, 3000)}"""`
      : src.link ? `The grown-up shared this link about the topic: ${src.link}. Make the game about that topic.`
      : `Topic: ${src.topic || "fun things for kids"}.`;
    messages = [{ role: "user", content: `Make a picture quiz for a small child.\n${basis}\nReturn the JSON object only.` }];
  }
  const text = await callClaude(messages, sys);
  if (parseRefusal(text)) throw blockedError(true);
  const deck = parseKidDeck(text);
  if (!deck) throw new Error("Hmm, I couldn't make that game. Let's try a different idea!");
  if (deck.items.length < 4) throw blockedError(true);   // not enough wholesome items → gentle redirect
  return deck;
}

function KidShape({ name, color }) {
  const c = { fill: color, stroke: "rgba(0,0,0,.12)", strokeWidth: 2 };
  return (
    <svg width="106" height="106" viewBox="0 0 100 100" style={{ filter: "drop-shadow(0 6px 8px rgba(0,0,0,.18))" }}>
      {name === "circle" && <circle cx="50" cy="50" r="40" {...c} />}
      {name === "square" && <rect x="14" y="14" width="72" height="72" rx="12" {...c} />}
      {name === "rectangle" && <rect x="8" y="28" width="84" height="44" rx="8" {...c} />}
      {name === "oval" && <ellipse cx="50" cy="50" rx="42" ry="30" {...c} />}
      {name === "triangle" && <polygon points="50,12 86,84 14,84" {...c} />}
      {name === "diamond" && <polygon points="50,8 90,50 50,92 10,50" {...c} />}
      {name === "star" && <polygon points="50,8 60,36 90,37 66,55 75,84 50,67 25,84 34,55 10,37 40,36" {...c} />}
      {name === "heart" && <path d="M50 84 C20 62 22 32 40 30 C48 29 50 36 50 40 C50 36 52 29 60 30 C78 32 80 62 50 84 Z" {...c} />}
    </svg>
  );
}

function KidMascot({ mood = "idle", size = 130 }) {
  const happy = mood === "happy" || mood === "cheer";
  const cheer = mood === "cheer";
  const oops = mood === "oops";
  return (
    <svg width={size} height={size * (150 / 140)} viewBox="0 0 140 150" className="kidz-bob">
      <defs>
        <radialGradient id="kbody" cx="50%" cy="36%" r="72%">
          <stop offset="0%" stopColor="#ffe7a0" /><stop offset="100%" stopColor="#ffb74d" />
        </radialGradient>
      </defs>
      <ellipse cx="70" cy="141" rx="40" ry="7" fill="rgba(0,0,0,.12)" />
      <path d="M70 16 C61 1 79 -1 81 12 C91 5 93 21 78 25 Z" fill="#7ddf7d" />
      <ellipse cx="54" cy="133" rx="12" ry="8" fill="#ff9f1c" />
      <ellipse cx="86" cy="133" rx="12" ry="8" fill="#ff9f1c" />
      <ellipse cx="70" cy="80" rx="50" ry="48" fill="url(#kbody)" stroke="#ffae42" strokeWidth="2" />
      <ellipse cx="42" cy="88" rx="10" ry="7" fill="#ff8fb0" opacity=".8" />
      <ellipse cx="98" cy="88" rx="10" ry="7" fill="#ff8fb0" opacity=".8" />
      {happy ? (
        <>
          <path d="M44 67 Q52 57 60 67" fill="none" stroke="#3b2b1a" strokeWidth="4" strokeLinecap="round" />
          <path d="M80 67 Q88 57 96 67" fill="none" stroke="#3b2b1a" strokeWidth="4" strokeLinecap="round" />
        </>
      ) : (
        <>
          <ellipse cx="52" cy="68" rx="9" ry="11" fill="#fff" />
          <ellipse cx="88" cy="68" rx="9" ry="11" fill="#fff" />
          <circle cx={oops ? 54 : 52} cy={oops ? 71 : 68} r="5" fill="#3b2b1a" />
          <circle cx={oops ? 90 : 88} cy={oops ? 71 : 68} r="5" fill="#3b2b1a" />
        </>
      )}
      {cheer ? (
        <path d="M55 91 Q70 116 85 91 Q70 103 55 91 Z" fill="#c0392b" />
      ) : happy ? (
        <path d="M54 92 Q70 112 86 92" fill="none" stroke="#3b2b1a" strokeWidth="5" strokeLinecap="round" />
      ) : oops ? (
        <path d="M60 101 Q70 95 80 101" fill="none" stroke="#3b2b1a" strokeWidth="4.5" strokeLinecap="round" />
      ) : (
        <path d="M58 96 Q70 106 82 96" fill="none" stroke="#3b2b1a" strokeWidth="4.5" strokeLinecap="round" />
      )}
      {oops && <path d="M106 56 q5 8 0 12 q-5 -4 0 -12 Z" fill="#7ec8ff" />}
      {happy && (
        <>
          <text x="12" y="40" fontSize="18"> </text>
          <text x="112" y="44" fontSize="18"> </text>
          {cheer && <text x="20" y="100" fontSize="16"> </text>}
        </>
      )}
    </svg>
  );
}

function KidConfetti({ burst }) {
  if (!burst) return null;
  const emojis = [" ", " ", " ", " ", " ", " ", " ", " "];
  return (
    <div className="kidz-confetti" key={burst} aria-hidden>
      {Array.from({ length: 22 }).map((_, i) => {
        const left = Math.random() * 100, dur = 1.6 + Math.random() * 1.3, delay = Math.random() * 0.35, size = 22 + Math.random() * 20;
        return <span key={i} style={{ left: left + "%", animationDuration: dur + "s", animationDelay: delay + "s", fontSize: size + "px" }}>{krand(emojis)}</span>;
      })}
    </div>
  );
}

function KidCard({ item, kind, idx, state, onTap }) {
  const palette = ["#ff6b6b", "#4dabf7", "#ffd43b", "#69db7c", "#b197fc", "#ffa94d", "#ff8cc6", "#63e6be"];
  const col = palette[idx % palette.length];
  let inner;
  if (kind === "color") inner = <span className="kidz-swatch" style={{ background: item.v }} />;
  else if (kind === "image") inner = (
    <span style={{ display: "grid", placeItems: "center", width: "100%", height: "100%" }}>
      <img src={item.v} alt={item.n || ""} style={{ maxWidth: "82%", maxHeight: "82%", objectFit: "contain", borderRadius: 12, pointerEvents: "none" }} draggable={false} />
    </span>
  );
  else if (kind === "num") inner = (
    <span style={{ display: "grid", placeItems: "center" }}>
      <span className="kidz-num">{item.v}</span>
      <span className="kidz-dots">{Array.from({ length: Number(item.v) }).map((_, k) => <i key={k} />)}</span>
    </span>
  );
  else if (kind === "digit") inner = <span className="kidz-num">{item.v}</span>;
  else if (kind === "shape") inner = <KidShape name={item.n} color={col} />;
  else inner = <span className="kidz-emoji">{item.v}</span>;
  return (
    <button className={"kidz-card" + (state ? ` kidz-card--${state}` : "")} style={{ "--cc": col }} onClick={onTap} disabled={state === "correct"} data-testid={`kid-card-${idx}`}>
      <span className="kidz-letter" aria-hidden="true">{["A", "B", "C", "D"][idx] || ""}</span>
      {inner}
      {state === "correct" && <span className="kidz-tick">✓</span>}
    </button>
  );
}

const KID_SOURCES = [
  { id: "topic", si: " ", label: "A topic" },
  { id: "paste", si: " ", label: "Paste text" },
  { id: "file", si: " ", label: "Upload" },
  { id: "link", si: " ", label: "A link" },
];

function KidsZone({ onExit }) {
  const emojiDecks = KID_DECKS.filter((d) => !d.kind);
  const [view, setView] = useState("menu"); // menu | setup | loading | play | win | error
  const [deck, setDeck] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [qi, setQi] = useState(0);
  const [stars, setStars] = useState(0);
  const [solved, setSolved] = useState(false);
  const [wrong, setWrong] = useState([]);
  const [mood, setMood] = useState("idle");
  const [burst, setBurst] = useState(0);
  const [mute, setMute] = useState(false);
  const [adminDecks, setAdminDecks] = useState([]);
  // grown-ups custom setup
  const [ksrc, setKsrc] = useState("topic");
  const [ktopic, setKtopic] = useState("");
  const [ktext, setKtext] = useState("");
  const [klink, setKlink] = useState("");
  const [kfileName, setKfileName] = useState(""); const [kBusy, setKBusy] = useState(false);
  const [kfileText, setKfileText] = useState("");
  const [kimg, setKimg] = useState(""); const [kimgMime, setKimgMime] = useState("");
  const [kerr, setKerr] = useState("");
  const timer = useRef(null);
  const q = questions[qi];

  // Load admin-curated kids decks (zero-AI, instant)
  useEffect(() => {
    backend.kidsListDecks().then((d) => {
      const adapted = (d.decks || []).map((dk) => ({
        id: `admin-${dk.id}`,
        label: dk.name,
        icon: dk.icon || "✨",
        color: dk.color || "#7048e8",
        kind: "image",
        admin: true,
        items: (dk.items || []).filter((it) => (it.image_b64 || it.image_url)).map((it) => ({
          n: it.name,
          v: it.image_b64 ? `data:${it.image_mime || "image/png"};base64,${it.image_b64}` : it.image_url,
        })),
      })).filter((dk) => dk.items.length >= 4);
      setAdminDecks(adapted);
    }).catch(() => {});
  }, []);

  const say = (t) => { if (!mute) kidSpeak(t); };
  const play = (t) => { if (!mute) kidSound(t); };

  function buildRound(d) {
    if (d.id === "counting") {
      const out = []; const used = new Set(); let last = null, tries = 0;
      while (out.length < KID_ROUND && tries++ < KID_ROUND * 40) {
        const emoji = krand(COUNT_EMOJIS), n = 1 + Math.floor(Math.random() * 5), key = emoji + n;
        if (used.has(key) || n === last) continue;
        used.add(key); last = n; out.push(makeKidCountQuestion(emoji, n));
      }
      while (out.length < KID_ROUND) out.push(makeKidCountQuestion());
      return out;
    }
    if (d.id === "surprise") {
      const out = []; const used = new Set(); let last = null, tries = 0;
      while (out.length < KID_ROUND && tries++ < KID_ROUND * 40) {
        const dd = krand(emojiDecks), t = krand(dd.items);
        if (t.n === last || used.has(t.n)) continue;
        used.add(t.n); last = t.n; out.push(makeKidQuestionFor(dd, t));
      }
      while (out.length < KID_ROUND) { const dd = krand(emojiDecks); out.push(makeKidQuestionFor(dd, krand(dd.items))); }
      return out;
    }
    const roundLen = Math.min(KID_ROUND, Math.max(4, d.items.length));
    return pickSequence(d.items, roundLen).map((t) => makeKidQuestionFor(d, t));
  }
  function startDeck(d) { clearTimeout(timer.current); setDeck(d); setQuestions(buildRound(d)); setQi(0); setStars(0); setView("play"); }
  function playAgain() { if (deck) startDeck(deck); }

  const kReady = ksrc === "topic" ? ktopic.trim().length > 1
    : ksrc === "paste" ? ktext.trim().length > 10
    : ksrc === "link" ? klink.trim().length > 3
    : ksrc === "file" ? !!(kfileText.trim() || kimg) : false;

  async function kReadUpload(e) {
    const f = e.target.files?.[0]; if (!f) return; setKerr("");
    if (/^image\//.test(f.type)) {
      if (f.size > 4 * 1024 * 1024) { setKerr("Please pick an image under 4 MB."); return; }
      const rd = new FileReader(); rd.onload = () => { const d = String(rd.result || ""); setKimg(d.split(",")[1] || ""); setKimgMime(f.type); setKfileText(""); setKfileName(f.name); }; rd.readAsDataURL(f); return;
    }
    if (f.size > 10 * 1024 * 1024) { setKerr("Please pick a file under 10 MB."); return; }
    setKBusy(true);
    try { const text = await extractFileText(f); if (!text || !text.trim()) throw new Error("That file looks empty — try another."); setKfileText(text); setKimg(""); setKfileName(f.name); }
    catch (err) { setKerr(err && err.message ? err.message : "Please upload a picture, or a .pdf, .txt, .md, .csv, .json, .docx or .xlsx file."); }
    finally { setKBusy(false); }
  }
  async function makeCustom() {
    const src = ksrc === "topic" ? { type: "topic", topic: ktopic.trim() }
      : ksrc === "paste" ? { type: "paste", text: ktext }
      : ksrc === "link" ? { type: "link", link: klink.trim() }
      : kimg ? { type: "image", imageData: kimg, imageMime: kimgMime, topic: ktopic.trim() }
      : { type: "file", text: kfileText };
    const probe = [ktopic, ktext, klink, kfileText].filter(Boolean).join("  ");
    if (screenContent(probe, true)) { setKerr(BLOCK_MSG.kids); setView("error"); return; }
    setKerr(""); setView("loading");
    try { const d = await generateKidDeck(src); startDeck({ id: "custom", label: d.label, color: "#9775fa", items: d.items }); }
    catch (e) { setKerr(e && e.message ? e.message : "Let's try a different idea."); setView("error"); }
  }

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (view !== "play" || !q) return;
    setSolved(false); setWrong([]); setMood("idle");
    const t = setTimeout(() => say(q.prompt), 380);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [qi, view]);
  useEffect(() => {
    if (view !== "win") return;
    setMood("cheer"); setBurst((b) => b + 1); play("win");
    const t = setTimeout(() => say("Hooray! You did it!"), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [view]);

  function tap(i) {
    if (solved || !q) return;
    if (i === q.correctIndex) {
      setSolved(true); setMood("happy"); setStars((s) => s + 1); setBurst((b) => b + 1);
      play("correct"); say(krand(KID_PRAISE));
      clearTimeout(timer.current);
      timer.current = setTimeout(() => { if (qi + 1 >= questions.length) setView("win"); else setQi((n) => n + 1); }, 1350);
    } else {
      setMood("oops"); setWrong((w) => (w.includes(i) ? w : [...w, i])); play("wrong"); say(krand(KID_NUDGE));
      setTimeout(() => setMood((m) => (m === "oops" ? "idle" : m)), 750);
    }
  }

  function topBack() {
    clearTimeout(timer.current);
    if (view === "menu") onExit();
    else if (view === "setup") setView("menu");
    else setView("menu");
  }

  return (
    <div id="kidz">
      <style>{KIDS_CSS}</style>
      <KidConfetti burst={burst} />
      <div className="kidz-top">
        <button className="kidz-round" onClick={topBack} aria-label={view === "menu" ? "Close" : "Back"}>
          {view === "menu"
            ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>}
        </button>
        <div className="kidz-brand">Quizkosh Juniors</div>
        <button className="kidz-round" onClick={() => setMute((m) => !m)} aria-label="Toggle sound">
          {mute
            ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
            : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>}
        </button>
      </div>

      <div className="kidz-wrap">
        {view === "menu" && (
          <div>
            <div className="kidz-mascot"><KidMascot mood="happy" size={124} /></div>
            <h1 className="kidz-title">Tap &amp; Learn!</h1>
            <p className="kidz-sub">Pick a game, little explorer </p>
            <div className="kidz-cats">
              {KID_DECKS.map((d) => (
                <button key={d.id} className="kidz-cat" style={{ background: `linear-gradient(135deg, ${d.color}, ${kshade(d.color)})` }} onClick={() => startDeck(d)}>
                  <span className="e">{d.icon}</span>{d.label}
                </button>
              ))}
              {adminDecks.map((d) => (
                <button key={d.id} className="kidz-cat" data-testid={`kidz-admin-deck-${d.id}`} style={{ background: `linear-gradient(135deg, ${d.color}, ${kshade(d.color)})` }} onClick={() => startDeck(d)}>
                  <span className="e">{d.icon || "✨"}</span>{d.label}
                </button>
              ))}
              <button className="kidz-cat" style={{ background: `linear-gradient(135deg, ${KID_COUNTING.color}, ${kshade(KID_COUNTING.color)})` }} onClick={() => startDeck(KID_COUNTING)}>
                <span className="e">{KID_COUNTING.icon}</span>Counting
              </button>
              <button className="kidz-cat kidz-cat--wide" style={{ background: "linear-gradient(135deg,#ff9ec7,#ffd166,#7ee8fa)", color: "#3a1430", textShadow: "none" }} onClick={() => startDeck(KID_SURPRISE)}>
                <span className="e"> </span>Surprise Mix!
              </button>
            </div>
          </div>
        )}

        {view === "setup" && (
          <div>
            <h1 className="kidz-title">Make a game!</h1>
            <p className="kidz-sub">Grown-ups: tell me what to make a picture game about </p>
            <div className="kidz-panel">
              <div className="kidz-srcrow">
                {KID_SOURCES.map((s) => (
                  <button key={s.id} className={"kidz-src" + (ksrc === s.id ? " on" : "")} onClick={() => { setKsrc(s.id); setKerr(""); }}>
                    <span className="si">{s.si}</span>{s.label}
                  </button>
                ))}
              </div>
              {ksrc === "topic" && <input className="kidz-input" placeholder="e.g. dinosaurs, farm animals, the jungle, space…" value={ktopic} onChange={(e) => setKtopic(e.target.value)} autoFocus />}
              {ksrc === "paste" && <textarea className="kidz-textarea" placeholder="Paste a few sentences — a storybook page, a word list, anything…" value={ktext} onChange={(e) => setKtext(e.target.value)} autoFocus />}
              {ksrc === "link" && <input className="kidz-input" placeholder="Paste a link (e.g. about ocean animals)…" value={klink} onChange={(e) => setKlink(e.target.value)} autoFocus />}
              {ksrc === "file" && (
                <label className="kidz-file">
                  <span className="si" style={{ fontSize: 30 }}> </span>
                  <span style={{ flex: 1 }}>{kBusy ? "Reading…" : (kfileName ? kfileName : "Tap to upload a photo, PDF, Word, Excel, or text file")}</span>
                  <span className="kidz-badge" style={{ background: "#ede7ff", color: "#6a4c93" }}>Browse</span>
                  <input type="file" accept=".pdf,.txt,.md,.csv,.tsv,.json,.docx,.xlsx,.xls,application/pdf,text/*,image/*" onChange={kReadUpload} style={{ display: "none" }} />
                </label>
              )}
              {kerr && <p className="kidz-err">{kerr}</p>}
              <button className="kidz-btn kidz-make" style={{ background: "linear-gradient(135deg,#69db7c,#2f9e44)" }} disabled={!kReady} onClick={makeCustom}> Make my game!</button>
              <p className="kidz-note">Pictures are chosen by AI and read aloud. PDFs, Word docs &amp; full web links are read on the server in the full product.</p>
            </div>
          </div>
        )}

        {view === "loading" && (
          <div style={{ textAlign: "center", paddingTop: 28 }}>
            <div className="kidz-mascot"><KidMascot mood="happy" size={128} /></div>
            <h1 className="kidz-title">Making your game…</h1>
            <p className="kidz-sub">Drawing fun pictures just for you! </p>
            <div className="kidz-loaddots"><i /><i /><i /></div>
          </div>
        )}

        {view === "play" && q && (
          <div>
            <div className="kidz-stars">
              {Array.from({ length: questions.length }).map((_, i) => <span key={i} className={"s" + (i < stars ? " on" : "")}> </span>)}
            </div>
            <div className="kidz-mascot"><KidMascot mood={mood} size={104} /></div>
            {q.kind === "count" && (
              <div className="kidz-count">{Array.from({ length: q.n }).map((_, k) => <span key={k}>{q.countEmoji}</span>)}</div>
            )}
            <div className="kidz-prompt">
              <span className="txt">{q.prompt}</span>
              <button className="kidz-say" onClick={() => say(q.prompt)} aria-label="Hear it again"> </button>
            </div>
            <div className="kidz-grid">
              {q.opts.map((item, i) => {
                const state = solved && i === q.correctIndex ? "correct" : wrong.includes(i) ? "wrong" : "";
                return <KidCard key={i} item={item} kind={q.kind === "count" ? "digit" : q.kind} idx={i} state={state} onTap={() => tap(i)} />;
              })}
            </div>
          </div>
        )}

        {view === "win" && (
          <div className="kidz-win">
            <div className="kidz-mascot"><KidMascot mood="cheer" size={148} /></div>
            <h2 className="kidz-pop">You did it! </h2>
            <div className="kidz-bigstars kidz-pop">{" ".repeat(Math.max(1, stars))}</div>
            <p className="kidz-sub">You earned {stars} star{stars === 1 ? "" : "s"}!</p>
            <div className="kidz-actions">
              <button className="kidz-btn" style={{ background: "linear-gradient(135deg,#69db7c,#2f9e44)" }} onClick={playAgain}> Play again</button>
              <button className="kidz-btn" style={{ background: "linear-gradient(135deg,#4dabf7,#1c7ed6)" }} onClick={() => setView("menu")}> Pick another game</button>
              <button className="kidz-btn" style={{ background: "linear-gradient(135deg,#ffa94d,#f76707)" }} onClick={onExit}> All done</button>
            </div>
          </div>
        )}

        {view === "error" && (
          <div style={{ textAlign: "center", paddingTop: 24 }}>
            <div className="kidz-mascot"><KidMascot mood="oops" size={128} /></div>
            <h1 className="kidz-title">Oopsie!</h1>
            <p className="kidz-sub">{kerr || "Let's try a different idea."}</p>
            <div className="kidz-actions">
              <button className="kidz-btn" style={{ background: "linear-gradient(135deg,#b197fc,#7048e8)" }} onClick={() => setView("setup")}> Try again</button>
              <button className="kidz-btn" style={{ background: "linear-gradient(135deg,#4dabf7,#1c7ed6)" }} onClick={() => setView("menu")}> Pick a ready game</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------- AUTH / FOOTER / POLICY ---------------------- */
function validatePassword(pw) {
  if (!pw || pw.length < 8) return "Password must be at least 8 characters.";
  if (!/[A-Za-z]/.test(pw)) return "Password must include at least one letter.";
  if (!/[0-9]/.test(pw)) return "Password must include at least one number.";
  if (!/[^A-Za-z0-9]/.test(pw)) return "Password must include at least one special character (e.g. ! @ # $).";
  return "";
}

function readImageAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error || new Error("Couldn't read file."));
    r.onload = () => resolve(String(r.result || ""));
    r.readAsDataURL(file);
  });
}

function SchoolLogoPicker({ logo, onChange, err, setErr }) {
  const inputRef = useRef(null);

  async function onFile(e) {
    const f = e.target.files && e.target.files[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    setErr("");
    const mime = (f.type || "").toLowerCase();
    if (!["image/png", "image/jpeg", "image/jpg"].includes(mime)) {
      setErr("Please pick a PNG or JPG image."); return;
    }
    if (f.size > 600 * 1024) {
      setErr("Logo must be 600 KB or smaller."); return;
    }
    try {
      const dataUrl = await readImageAsDataUrl(f);
      const comma = dataUrl.indexOf(",");
      const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
      onChange({ data: b64, mime: mime === "image/jpg" ? "image/jpeg" : mime });
    } catch (e2) {
      setErr(e2.message || "Couldn't read the image.");
    }
  }

  const dataUrl = logo && logo.data ? `data:${logo.mime || "image/png"};base64,${logo.data}` : "";

  return (
    <div data-testid="school-logo-picker" style={{ border: "1px dashed var(--border)", borderRadius: 12, padding: 10, background: "var(--surface)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 52, height: 52, borderRadius: 10, background: "#fff", display: "grid", placeItems: "center", overflow: "hidden", flex: "none", border: "1px solid var(--border)" }}>
          {dataUrl ? <img src={dataUrl} alt="School logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /> : <Layers size={20} color="var(--muted)" />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>School logo <span className="tag" style={{ marginLeft: 4 }}>optional</span></div>
          <div className="qz-muted" style={{ fontSize: 12, marginTop: 2 }}>PNG or JPG, up to 600 KB. Shown beside the Quizkosh logo.</div>
        </div>
        <input data-testid="school-logo-file" ref={inputRef} type="file" accept="image/png,image/jpeg,image/jpg" style={{ display: "none" }} onChange={onFile} />
        <button type="button" data-testid="school-logo-pick" className="btn" style={{ padding: "8px 12px" }} onClick={() => inputRef.current && inputRef.current.click()}>{dataUrl ? "Change" : "Upload"}</button>
        {dataUrl && <button type="button" data-testid="school-logo-clear" className="btn btn--icon" title="Remove logo" style={{ width: 36, height: 36 }} onClick={() => onChange({ data: "", mime: "" })}><X size={14} /></button>}
      </div>
      {err && <p style={{ color: "var(--wrong)", fontSize: 12.5, marginTop: 6 }} data-testid="school-logo-err">{err}</p>}
    </div>
  );
}

function AuthModal({ mode, onClose, onAuthed }) {
  // Internal flow: login | signup | otp | forgot | reset
  const [tab, setTab] = useState(mode || "login");
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState("");
  const [userType, setUserType] = useState("individual"); // individual | school
  const [schoolName, setSchoolName] = useState("");
  const [schoolLogo, setSchoolLogo] = useState({ data: "", mime: "" });
  const [schoolLogoErr, setSchoolLogoErr] = useState("");
  const [interest, setInterest] = useState("");
  const [interestCustom, setInterestCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  function clearMsgs() { setErr(""); setInfo(""); }

  async function submit() {
    clearMsgs(); setBusy(true);
    try {
      if (tab === "login") {
        const data = await backend.login(email.trim(), pw);
        onAuthed && onAuthed(data.user);
        onClose();
      } else if (tab === "signup") {
        if (userType === "school" && !schoolName.trim()) {
          setErr("Please enter the school name."); setBusy(false); return;
        }
        if (!pw || pw.length < 8) {
          setErr("Password must be at least 8 characters."); setBusy(false); return;
        }
        if (pw !== confirmPw) {
          setErr("Passwords do not match. Please re-enter the same password in both fields."); setBusy(false); return;
        }
        if (!interest) {
          setErr("Please pick your Area of Interest."); setBusy(false); return;
        }
        const customWords = interestCustom.trim().split(/\s+/).filter(Boolean);
        if (interest === "Others" && customWords.length === 0) {
          setErr("Please describe your interest in up to 5 words."); setBusy(false); return;
        }
        if (interest === "Others" && customWords.length > 5) {
          setErr("Custom interest must be 5 words or fewer."); setBusy(false); return;
        }
        await backend.requestOtpExt(name.trim() || "User", email.trim(), pw, userType, userType === "school" ? schoolName.trim() : "", userType === "school" ? (schoolLogo.data || "") : "", userType === "school" ? (schoolLogo.mime || "") : "", interest, interest === "Others" ? customWords.slice(0, 5).join(" ") : "");
        setInfo(`We've emailed a 6-digit code to ${email.trim()}. It expires in 10 minutes.`);
        setTab("otp");
      } else if (tab === "otp") {
        const data = await backend.verifyOtp(email.trim(), code.trim());
        onAuthed && onAuthed(data.user);
        onClose();
      } else if (tab === "forgot") {
        await backend.forgotPassword(email.trim());
        setInfo(`If an account exists for ${email.trim()}, we've sent a reset code. Check your inbox.`);
        setTab("reset");
      } else if (tab === "reset") {
        await backend.resetPassword(email.trim(), code.trim(), pw);
        setInfo("Password updated. You can now log in with the new password.");
        setCode(""); setPw("");
        setTab("login");
      }
    } catch (e) {
      setErr(e.message || "Something went wrong. Try again.");
    } finally { setBusy(false); }
  }

  async function resendCode() {
    if (busy) return;
    clearMsgs(); setBusy(true);
    try {
      if (tab === "otp") { await backend.requestOtp(name.trim() || "User", email.trim(), pw); setInfo("New code sent. Check your inbox."); }
      else if (tab === "reset") { await backend.forgotPassword(email.trim()); setInfo("New reset code sent."); }
    } catch (e) { setErr(e.message || "Couldn't resend the code."); }
    finally { setBusy(false); }
  }

  const heading = {
    login: "Log in to Quizkosh",
    signup: "Create your account",
    otp: "Verify your email",
    forgot: "Forgot password",
    reset: "Set a new password",
  }[tab];

  return (
    <div className="ovl" onClick={onClose}>
      <div className="card glow modal" style={{ position: "relative", padding: 26 }} onClick={(e) => e.stopPropagation()}>
        <button className="btn btn--icon modal__x" onClick={onClose} aria-label="Close" data-testid="auth-close-btn"><X size={18} /></button>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <img src="/quizkosh-logo.jpg" alt="Quizkosh" style={{ height: 40, width: "auto" }} />
        </div>
        <div className="qz-eyebrow" style={{ textAlign: "center" }}>Welcome</div>
        <h3 className="qz-display" style={{ fontSize: 24, marginTop: 6, marginBottom: 14, textAlign: "center" }}>{heading}</h3>

        {(tab === "login" || tab === "signup") && (
          <div className="seg" style={{ marginBottom: 16 }}>
            <button data-testid="auth-tab-login" className={tab === "login" ? "act" : ""} onClick={() => { setTab("login"); setConfirmPw(""); clearMsgs(); }}>Log in</button>
            <button data-testid="auth-tab-signup" className={tab === "signup" ? "act" : ""} onClick={() => { setTab("signup"); setConfirmPw(""); clearMsgs(); }}>Sign up</button>
          </div>
        )}

        {(tab === "login" || tab === "signup") && (
          <>
            {/*
              REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
              REDIRECT URLS, THIS BREAKS THE AUTH. The backend derives the
              exact redirect_uri from the incoming request origin, so this
              anchor just links to the /api/auth/google/start endpoint on
              the same domain the user is already on.
            */}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start`}
              data-testid="auth-google-btn"
              className="btn btn--ghost btn--block"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 10, marginBottom: 12, padding: "10px 14px",
                border: "1px solid rgba(127,127,127,0.3)", borderRadius: 10,
                background: "var(--card, #fff)", color: "var(--text, #202124)",
                textDecoration: "none", fontWeight: 600, fontSize: 14,
              }}
              aria-label={tab === "login" ? "Continue with Google" : "Sign up with Google"}
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {tab === "login" ? "Continue with Google" : "Sign up with Google"}
            </a>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 12px", fontSize: 12 }} className="qz-muted">
              <div style={{ flex: 1, height: 1, background: "rgba(127,127,127,0.25)" }} />
              <span>or continue with email</span>
              <div style={{ flex: 1, height: 1, background: "rgba(127,127,127,0.25)" }} />
            </div>
          </>
        )}

        <div className="grid" style={{ gap: 11 }}>
          {tab === "signup" && (
            <div className="seg" data-testid="auth-usertype-seg">
              <button type="button" data-testid="usertype-individual" className={userType === "individual" ? "act" : ""} onClick={() => setUserType("individual")}>I am an Individual</button>
              <button type="button" data-testid="usertype-school" className={userType === "school" ? "act" : ""} onClick={() => setUserType("school")}>I am a School</button>
            </div>
          )}
          {tab === "signup" && userType === "school" && (
            <>
              <input data-testid="auth-school-input" className="input" placeholder="School name" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} />
              <SchoolLogoPicker logo={schoolLogo} onChange={setSchoolLogo} err={schoolLogoErr} setErr={setSchoolLogoErr} />
            </>
          )}
          {tab === "signup" && (
            <>
              <select data-testid="auth-interest-select" className="input" value={interest} onChange={(e) => { setInterest(e.target.value); if (e.target.value !== "Others") setInterestCustom(""); }}>
                <option value="">Area of interest (required)</option>
                {INTEREST_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              {interest === "Others" && (
                <div>
                  <input data-testid="auth-interest-custom" className="input" placeholder="Describe your interest (max 5 words)" value={interestCustom} onChange={(e) => {
                    const words = e.target.value.split(/\s+/).filter(Boolean);
                    if (words.length <= 5) setInterestCustom(e.target.value);
                    else setInterestCustom(words.slice(0, 5).join(" "));
                  }} />
                  <p className="qz-muted" style={{ fontSize: 12, marginTop: 4 }} data-testid="auth-interest-wordcount">{interestCustom.trim().split(/\s+/).filter(Boolean).length}/5 words</p>
                </div>
              )}
            </>
          )}
          {tab === "signup" && <input data-testid="auth-name-input" className="input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />}
          {(tab === "login" || tab === "signup" || tab === "forgot") && (
            <input data-testid="auth-email-input" className="input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
          {(tab === "login" || tab === "signup") && (
            <div style={{ position: "relative" }}>
              <input data-testid="auth-password-input" className="input" type={showPw ? "text" : "password"} placeholder={tab === "signup" ? "Password (8+ chars · letter, number & special)" : "Password"} value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} style={{ paddingRight: 44, width: "100%" }} />
              <button type="button" data-testid="auth-toggle-password" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} title={showPw ? "Hide password" : "Show password"} style={{ position: "absolute", top: 0, right: 0, height: "100%", width: 40, display: "grid", placeItems: "center", background: "transparent", border: "none", cursor: "pointer", color: "var(--muted)" }}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          )}
          {tab === "signup" && (
            <div style={{ position: "relative" }}>
              <input data-testid="auth-confirm-password-input" className="input" type={showPw ? "text" : "password"} placeholder="Confirm password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} style={{ paddingRight: 44, width: "100%" }} />
              {confirmPw && pw && confirmPw !== pw && (
                <p data-testid="auth-confirm-mismatch" style={{ color: "var(--wrong)", fontSize: 12.5, marginTop: 4 }}>Passwords don't match.</p>
              )}
              {confirmPw && pw && confirmPw === pw && (
                <p data-testid="auth-confirm-match" style={{ color: "var(--correct)", fontSize: 12.5, marginTop: 4 }}>Passwords match.</p>
              )}
            </div>
          )}
          {(tab === "otp" || tab === "reset") && (
            <input data-testid="otp-code-input" className="input qz-mono" inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" style={{ textAlign: "center", letterSpacing: 6, fontSize: 18 }} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
          )}
          {tab === "reset" && (
            <input data-testid="reset-newpassword-input" className="input" type="password" placeholder="New password (8+ chars · letter, number & special)" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
          )}

          {info && <p style={{ color: "var(--correct)", fontSize: 13.5 }} data-testid="auth-info">{info}</p>}
          {err && <p style={{ color: "var(--wrong)", fontSize: 13.5 }} data-testid="auth-error">{err}</p>}

          <button data-testid="auth-submit-btn" className="btn btn--primary btn--block" disabled={busy || (tab === "signup" && (pw.length < 8 || pw !== confirmPw || !interest || (interest === "Others" && !interestCustom.trim())))} onClick={submit}>
            {busy ? <Loader2 size={18} className="spin" /> :
              tab === "login" ? <><LogIn size={18} /> Log in</> :
              tab === "signup" ? <><UserPlus size={18} /> Send verification code</> :
              tab === "otp" ? <><Check size={18} /> Verify & create account</> :
              tab === "forgot" ? <><ShieldCheck size={18} /> Send reset code</> :
              <><Check size={18} /> Reset password</>}
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
          {tab === "login" && (
            <button data-testid="auth-forgot-link" className="btn btn--ghost" style={{ padding: "6px 10px", fontSize: 13 }} onClick={() => { setTab("forgot"); clearMsgs(); }}>Forgot password?</button>
          )}
          {(tab === "otp" || tab === "reset") && (
            <button data-testid="otp-resend-link" className="btn btn--ghost" style={{ padding: "6px 10px", fontSize: 13 }} disabled={busy} onClick={resendCode}>Resend code</button>
          )}
          {(tab === "forgot" || tab === "otp" || tab === "reset") && (
            <button className="btn btn--ghost" style={{ padding: "6px 10px", fontSize: 13, marginLeft: "auto" }} onClick={() => { setTab("login"); clearMsgs(); }}>Back to log in</button>
          )}
        </div>

        <div className="note-sec" style={{ marginTop: 14 }}>
          <ShieldCheck size={16} color="var(--accent2)" style={{ flex: "none", marginTop: 1 }} />
          <span>Your account is private and encrypted. We never share or sell your data.</span>
        </div>
      </div>
    </div>
  );
}

/* Admin-only login modal — no signup, no tabs, email + password + Log in. */
function AdminLoginModal({ onClose, onAuthed }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function submit() {
    setErr(""); setBusy(true);
    try {
      const data = await backend.login(email.trim(), pw);
      if (data.user?.role !== "admin") {
        setErr("This account isn't an admin. Use an admin email.");
        try { await backend.logout(); } catch {}
        return;
      }
      onAuthed && onAuthed(data.user);
      onClose();
    } catch (e) { setErr(e.message || "Login failed."); }
    finally { setBusy(false); }
  }
  return (
    <div className="ovl" onClick={onClose}>
      <div className="card glow modal" style={{ position: "relative", padding: 26, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
        <button className="btn btn--icon modal__x" onClick={onClose} aria-label="Close" data-testid="admin-login-close-btn"><X size={18} /></button>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <img src="/quizkosh-logo.jpg" alt="Quizkosh" style={{ height: 40, width: "auto" }} />
        </div>
        <div className="qz-eyebrow" style={{ textAlign: "center" }}><ShieldCheck size={13} style={{ verticalAlign: -2 }} /> Admin only</div>
        <h3 className="qz-display" style={{ fontSize: 22, marginTop: 6, marginBottom: 16, textAlign: "center" }}>Admin login</h3>
        <div className="grid" style={{ gap: 11 }}>
          <input data-testid="admin-login-email-input" className="input" type="email" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-login-password-input" className="input" type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
          {err && <p style={{ color: "var(--wrong)", fontSize: 13.5 }} data-testid="admin-login-error">{err}</p>}
          <button data-testid="admin-login-submit-btn" className="btn btn--primary btn--block" disabled={busy} onClick={submit}>
            {busy ? <Loader2 size={18} className="spin" /> : <><LogIn size={18} /> Log in</>}
          </button>
        </div>
        <p className="qz-muted" style={{ fontSize: 12, marginTop: 14, textAlign: "center" }}>Admin accounts are provisioned by the server (environment allowlist) — no signup available.</p>
      </div>
    </div>
  );
}
function ContactModal({ onClose }) {
  const email = "care@quizkosh.co.in";
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(email); setCopied(true); setTimeout(() => setCopied(false), 1700); }
    catch { setCopied(false); }
  }
  return (
    <div className="ovl" onClick={onClose}>
      <div className="card glow modal" style={{ position: "relative", padding: 26, maxWidth: 380, textAlign: "center" }} onClick={(e) => e.stopPropagation()} data-testid="contact-modal">
        <button className="btn btn--icon modal__x" onClick={onClose} aria-label="Close" data-testid="contact-close-btn"><X size={18} /></button>
        <div style={{ display: "grid", placeItems: "center", marginBottom: 10 }}>
          <img src="/quizkosh-logo.jpg" alt="Quizkosh" style={{ height: 36, width: "auto" }} />
        </div>
        <h3 className="qz-display" style={{ fontSize: 22, marginTop: 4, marginBottom: 8 }}>Get in touch</h3>
        <p className="qz-muted" style={{ fontSize: 13.5, marginBottom: 14 }}>Email us — we read every message.</p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", padding: "12px 14px", background: "var(--surface2)", borderRadius: 12, border: "1px solid var(--border)" }}>
          <a href={`mailto:${email}`} data-testid="contact-email-link" className="qz-mono" style={{ fontSize: 14, fontWeight: 700, color: "var(--accent2)", wordBreak: "break-all" }}>{email}</a>
        </div>
        <button data-testid="contact-copy-btn" className="btn btn--primary btn--block" style={{ marginTop: 14 }} onClick={copy}>
          {copied ? <><Check size={16} /> Copied</> : <><Share2 size={16} /> Copy email address</>}
        </button>
      </div>
    </div>
  );
}



function PolicyModal({ onClose }) {
  return (
    <div className="ovl" onClick={onClose}>
      <div className="card glow modal policy" style={{ position: "relative", padding: 26, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <button className="btn btn--icon modal__x" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <div className="qz-eyebrow">Quizkosh</div>
        <h3 className="qz-display policy" style={{ marginTop: 6 }}>Privacy Policy</h3>
        <p className="qz-muted" style={{ fontSize: 12.5, marginTop: 4 }}>General summary · last updated May 2026</p>
        <div className="divider" />
        <div className="policy">
          <p>This is a general, plain-language summary of how Quizkosh would handle your information. It's provided as a template and is not legal advice.</p>
          <h4>1. Information we collect</h4>
          <p>If you create an account, we may collect your name, email, and the quizzes and scores you generate. If you play as a guest, your activity stays on your device.</p>
          <h4>2. How we use it</h4>
          <p>To run the app, save your quizzes and progress, personalise difficulty and recommendations, and improve the product.</p>
          <h4>3. AI-generated content</h4>
          <p>Topics, notes, files, or images you submit are sent to our AI provider only to generate your quiz. We don't use them to identify you.</p>
          <h4>4. Sharing</h4>
          <p>We do not sell your personal data. We share it only with the service providers needed to run the app, or when required by law.</p>
          <h4>5. Security</h4>
          <p>Data is encrypted in transit (TLS), passwords are hashed, and access is limited. No system is perfectly secure, so we keep only what we need.</p>
          <h4>6. Children</h4>
          <p>The Juniors section is designed for young children and runs without accounts or data collection. A parent or guardian should supervise its use.</p>
          <h4>7. Your choices</h4>
          <p>You can use the app as a guest, request deletion of your account data, and contact us with any privacy question.</p>
          <h4>8. Contact</h4>
          <p>Questions? Reach us at <a href="mailto:care@quizkosh.co.in">care@quizkosh.co.in</a>.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button className="btn btn--primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

function Footer({ onPolicy, onAdmin, onRequest, onContact, onFeedback }) {
  return (
    <footer className="foot">
      <div className="foot-row">
        <div className="qz-logo" style={{ cursor: "default" }}>
          <img src="/quizkosh-logo.jpg" alt="Quizkosh" style={{ height: 28, width: "auto" }} />
        </div>
        <div className="links">
          <a onClick={onPolicy} data-testid="footer-privacy-link">Privacy Policy</a>
          <a onClick={onContact} data-testid="footer-contact-link">Contact</a>
          {onFeedback && <a onClick={onFeedback} data-testid="footer-feedback-link">Provide Feedback</a>}
          {onAdmin && <a onClick={onAdmin} data-testid="footer-admin-link">Admin</a>}
        </div>
        <div className="foot-social" data-testid="footer-social">
          <button type="button" className="foot-social-btn" aria-label="Instagram" data-testid="social-instagram" onClick={(e) => window.open("https://www.instagram.com/quizkosh.co.in/","_blank","noopener,noreferrer")}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>
          </button>
          <button type="button" className="foot-social-btn" aria-label="YouTube" data-testid="social-youtube" onClick={(e) => window.open("https://www.youtube.com/@Quizkosh-k5v","_blank","noopener,noreferrer")}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 29 29 0 0 0-.46-5.33z" /><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" /></svg>
          </button>
          <button type="button" className="foot-social-btn" aria-label="Facebook" data-testid="social-facebook" onClick={(e) => window.open("https://www.facebook.com/profile.php?id=61591300657070","_blank","noopener,noreferrer")}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
          </button>
        </div>
      </div>
      <p className="qz-muted" style={{ fontSize: 12.5, marginTop: 12, lineHeight: 1.6 }} data-testid="footer-copy">
        © 2026 Quizkosh. All rights reserved.
        <br />
        Quizkosh transforms documents, notes, articles, web pages, and study materials into AI-powered quizzes that make learning more interactive, engaging, and effective. Using advanced AI, Quizkosh generates meaningful questions to help reinforce concepts, assess understanding, and identify knowledge gaps across a wide range of subjects and skills.
        <br /><br />
        Designed for students, educators, professionals, and lifelong learners, Quizkosh supports learning, revision, and skill development through interactive practice and knowledge assessment. Quizkosh is intended as a learning and knowledge-enhancement platform and is not a coaching, tutoring, or exam-preparation service. Built with privacy and security in mind, it helps users learn more effectively through active engagement and continuous practice.
        <br />
        <br />
        <strong>AI isn't always perfect.</strong> While Quizkosh strives for accuracy, it may occasionally generate incorrect or incomplete information. Please double-check important facts.
      </p>
    </footer>
  );
}

/* -------------------------------- APP ------------------------------------ */
function AdminModal({ role, onUnlock, pin, setPin, scoring, setScoring, access, setAccess, requests, setReqStatus, gate, setGate, customLog, presets, onPromote, onRemovePreset, adminUsers = [], onDeleteUser, currentUserId, adminLogs = [], onExit, onClose }) {
  const [tab, setTab] = useState("scoring");
  const authed = role === "admin";
  const updNum = (key, field, val) => setScoring((s) => ({ ...s, [key]: { ...s[key], [field]: Number(val) || 0 } }));
  const updArr = (key, field, i, val) => setScoring((s) => ({ ...s, [key]: { ...s[key], [field]: s[key][field].map((row, idx) => idx === i ? [Number(val) || 0, row[1]] : row) } }));
  const toggle = (k) => setAccess((a) => ({ ...a, [k]: a[k] === false ? true : false }));
  const renderRule = (k) => {
    const r = scoring[k];
    if (!r) return null;
    return (
      <div className="card" style={{ marginBottom: 10 }} key={k}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
          <span style={{ fontWeight: 800 }}>{k}</span>
          <span className="tag">{METHOD_LABEL[r.method] || r.method}</span>
        </div>
        {r.method === "negative" && (
          <>
            <p className="qz-muted" style={{ fontSize: 12, marginBottom: 10 }}>Total = correct×(+) + wrong×(−) + unattempted.</p>
            <div className="r3-grid">
              <label className="adm-f">Correct<input className="input" type="number" step="any" value={r.perCorrect} onChange={(e) => updNum(k, "perCorrect", e.target.value)} /></label>
              <label className="adm-f">Wrong<input className="input" type="number" step="any" value={r.perWrong} onChange={(e) => updNum(k, "perWrong", e.target.value)} /></label>
              <label className="adm-f">Unattempted<input className="input" type="number" step="any" value={r.perBlank} onChange={(e) => updNum(k, "perBlank", e.target.value)} /></label>
            </div>
          </>
        )}
        {r.method === "scaled" && (
          <>
            <p className="qz-muted" style={{ fontSize: 12, marginBottom: 10 }}>Accuracy maps to this score range; pass mark sets Pass / Below-pass.</p>
            <div className="r3-grid">
              <label className="adm-f">Min score<input className="input" type="number" step="any" value={r.min} onChange={(e) => updNum(k, "min", e.target.value)} /></label>
              <label className="adm-f">Max score<input className="input" type="number" step="any" value={r.max} onChange={(e) => updNum(k, "max", e.target.value)} /></label>
              <label className="adm-f">Pass mark<input className="input" type="number" step="any" value={r.pass} onChange={(e) => updNum(k, "pass", e.target.value)} /></label>
            </div>
          </>
        )}
        {r.method === "proficiency" && (
          <>
            <p className="qz-muted" style={{ fontSize: 12, marginBottom: 8 }}>Minimum % correct for each tier.</p>
            {r.tiers.map((t, i) => i < r.tiers.length - 1 ? (
              <div className="adm-row" key={i} style={{ borderBottom: "none", padding: "4px 0" }}>
                <span style={{ fontWeight: 700 }}>{t[1]}</span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><input className="input" type="number" step="any" style={{ width: 88 }} value={t[0]} onChange={(e) => updArr(k, "tiers", i, e.target.value)} /><span className="qz-muted">%+</span></span>
              </div>
            ) : (
              <p className="qz-muted" style={{ fontSize: 12, marginTop: 4 }} key={i}>Below {r.tiers[r.tiers.length - 2][0]}% → “{t[1]}”.</p>
            ))}
          </>
        )}
        {r.method === "band" && (
          <>
            <p className="qz-muted" style={{ fontSize: 12, marginBottom: 10 }}>Correct answers scale to /{r.max}; set the minimum raw score per band.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, maxHeight: 220, overflow: "auto" }}>
              {r.bands.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="tag" style={{ minWidth: 62, textAlign: "center" }}>Band {Number(b[1]).toFixed(1)}</span>
                  <input className="input" type="number" step="any" style={{ padding: "8px 10px" }} value={b[0]} onChange={(e) => updArr(k, "bands", i, e.target.value)} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  if (!authed) {
    // Should not render — App component now opens the AuthModal instead.
    return null;
  }

  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620, maxHeight: "86vh", overflow: "auto" }} data-testid="admin-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h3 className="qz-display" style={{ fontSize: 24 }}>Admin panel</h3>
          <button data-testid="admin-close-btn" className="btn btn--icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="banner" style={{ marginBottom: 14 }}>Signed in as admin. Server-enforced role checks · all admin actions are CSRF-protected.</div>
        <div className="seg" style={{ marginBottom: 16 }}>
          <button data-testid="admin-tab-scoring" className={tab === "scoring" ? "act" : ""} onClick={() => setTab("scoring")}>Scoring</button>
          <button data-testid="admin-tab-access" className={tab === "access" ? "act" : ""} onClick={() => setTab("access")}>Access</button>
          <button data-testid="admin-tab-requests" className={tab === "requests" ? "act" : ""} onClick={() => setTab("requests")}>Requests{requests && requests.filter((r) => r.status === "pending").length ? ` (${requests.filter((r) => r.status === "pending").length})` : ""}</button>
          <button data-testid="admin-tab-users" className={tab === "users" ? "act" : ""} onClick={() => setTab("users")}>Users</button>
          <button data-testid="admin-tab-kids" className={tab === "kids" ? "act" : ""} onClick={() => setTab("kids")}>Kids Decks</button>
          <button data-testid="admin-tab-bank" className={tab === "bank" ? "act" : ""} onClick={() => setTab("bank")}>Question Bank</button>
          <button data-testid="admin-tab-credits" className={tab === "credits" ? "act" : ""} onClick={() => setTab("credits")}>Credits</button>
          <button data-testid="admin-tab-logs" className={tab === "logs" ? "act" : ""} onClick={() => setTab("logs")}>AI Logs</button>
          <button data-testid="admin-tab-feedback" className={tab === "feedback" ? "act" : ""} onClick={() => setTab("feedback")}>Feedback</button>
          <button data-testid="admin-tab-pricing" className={tab === "pricing" ? "act" : ""} onClick={() => setTab("pricing")}>Pricing</button>
          <button data-testid="admin-tab-promotions" className={tab === "promotions" ? "act" : ""} onClick={() => setTab("promotions")}>Promotions</button>
          <button data-testid="admin-tab-plans" className={tab === "plans" ? "act" : ""} onClick={() => setTab("plans")}>Plans</button>
          <button data-testid="admin-tab-payments" className={tab === "payments" ? "act" : ""} onClick={() => setTab("payments")}>Payments</button>
          <button data-testid="admin-tab-patterns" className={tab === "patterns" ? "act" : ""} onClick={() => setTab("patterns")}>Exam Patterns</button>
          <button data-testid="admin-tab-upsc" className={tab === "upsc" ? "act" : ""} onClick={() => setTab("upsc")}>UPSC Intelligence</button>
          <button data-testid="admin-tab-traffic" className={tab === "traffic" ? "act" : ""} onClick={() => setTab("traffic")}>Traffic Stats</button>
          <button data-testid="admin-tab-security" className={tab === "security" ? "act" : ""} onClick={() => setTab("security")}>Security</button>
        </div>

        {tab === "scoring" && (
          <>
            {SCORING_GROUPS.map(([group, keys]) => (
              <div key={group} style={{ marginBottom: 4 }}>
                <div className="qz-eyebrow" style={{ margin: "8px 0 10px" }}>{group}</div>
                {keys.filter((k) => scoring[k]).map((k) => renderRule(k))}
              </div>
            ))}
            {presets && presets.length > 0 && (
              <div style={{ marginBottom: 4 }}>
                <div className="qz-eyebrow" style={{ margin: "8px 0 10px" }}>Custom presets</div>
                {presets.filter((p) => scoring[p.name]).map((p) => renderRule(p.name))}
                {presets.filter((p) => !scoring[p.name]).map((p) => (
                  <div className="card" style={{ marginBottom: 10 }} key={p.name}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontWeight: 800 }}>{p.name}</span>
                      <span className="tag">Weighted (default)</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>Promote a custom exam</div>
              <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>Turn a frequently-used custom quiz into an official preset that everyone can pick (its scoring rule comes along).</p>
              {!customLog || customLog.length === 0 ? (
                <p className="qz-muted" style={{ fontSize: 13 }}>No custom quizzes yet. When people run a “Make your own” quiz, it shows up here ranked by use.</p>
              ) : [...customLog].sort((a, b) => b.count - a.count).map((e) => {
                const isPreset = presets && presets.some((p) => p.name.toLowerCase() === e.name.toLowerCase());
                return (
                  <div key={e.name} className="req-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{e.name} <span className="req-badge req-pending">{e.count}×</span></div>
                      <div className="qz-muted" style={{ fontSize: 12 }}>{METHOD_LABEL[e.rule && e.rule.method] || "Weighted (default)"}</div>
                    </div>
                    {isPreset
                      ? <button className="btn btn--icon" title="Remove preset" style={{ width: 34, height: 34, color: "var(--wrong)" }} onClick={() => onRemovePreset(e.name)}><X size={16} /></button>
                      : <button className="btn" style={{ padding: "8px 12px", flex: "none" }} onClick={() => onPromote(e.name)}><Star size={15} /> Promote</button>}
                  </div>
                );
              })}
            </div>

            <div className="note-sec"><b>Everything else</b> — General Knowledge, Daily Challenge, Little Learners, and custom quizzes — uses the default weighted method unless a custom rule is set at quiz start: base points × difficulty (easy ×1, medium ×1.5, hard ×2) + speed bonus + streak bonus.</div>
          </>
        )}

        {tab === "access" && (
          <div className="card">
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Feature access</div>
            <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 8 }}>Turn a feature off to hide it from everyone in this session.</p>
            {[["kids", "Little Learners (ages 3–5)"], ["multiplayer", "Multiplayer mode"], ["survival", "Survival mode"], ["challenge", "Challenge mode"]].map(([k, lbl]) => (
              <label key={k} className="adm-row">
                <span style={{ fontWeight: 600 }}>{lbl}</span>
                <button type="button" className={"tog" + (access[k] !== false ? " on" : "")} onClick={() => toggle(k)}><span className="knob" /></button>
              </label>
            ))}
          </div>
        )}

        {tab === "requests" && (
          <div>
            <div className="card" style={{ marginBottom: 12 }}>
              <label className="adm-row" style={{ borderBottom: "none", padding: "2px 0", alignItems: "flex-start" }}>
                <span style={{ paddingRight: 12 }}><b>Require approval to use the app</b><br /><span className="qz-muted" style={{ fontSize: 12 }}>Gated preview — only approved people get in. You (admin) always have access.</span></span>
                <button type="button" className={"tog" + (gate ? " on" : "")} onClick={() => setGate(!gate)}><span className="knob" /></button>
              </label>
            </div>
            <div className="card">
              <div style={{ fontWeight: 800, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Access requests</span>
                <span className="qz-muted qz-mono" style={{ fontSize: 13 }}>{(requests || []).filter((r) => r.status === "pending").length} pending</span>
              </div>
              {!requests || requests.length === 0 ? (
                <p className="qz-muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>No requests yet. People can request access from the footer link (“Request access”), or from the gate screen when approval is required.</p>
              ) : requests.map((r) => (
                <div key={r.id} className="req-row">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{r.name}<span className={"req-badge req-" + r.status}>{r.status}</span></div>
                    <div className="qz-muted" style={{ fontSize: 12.5, wordBreak: "break-all" }}>{r.email}</div>
                    {r.note && <div className="qz-muted" style={{ fontSize: 12, marginTop: 3, fontStyle: "italic" }}>“{r.note}”</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flex: "none" }}>
                    {r.status !== "approved" && <button className="btn btn--icon" title="Approve" style={{ width: 34, height: 34, color: "var(--correct)" }} onClick={() => setReqStatus(r.id, "approved")}><Check size={16} /></button>}
                    {r.status !== "rejected" && <button className="btn btn--icon" title="Reject" style={{ width: 34, height: 34, color: "var(--wrong)" }} onClick={() => setReqStatus(r.id, "rejected")}><X size={16} /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="card" data-testid="admin-users-panel">
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Registered users <span className="tag" style={{ marginLeft: 6 }}>{adminUsers.length}</span></div>
            <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>People with an account. Admins are highlighted. You cannot delete your own account.</p>
            {adminUsers.length === 0 ? <p className="qz-muted" style={{ fontSize: 13 }}>No users yet.</p> : adminUsers.map((u) => (
              <div key={u.id} className="req-row" data-testid={`admin-user-row-${u.id}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{u.name} {u.role === "admin" && <span className="req-badge req-approved">admin</span>}</div>
                  <div className="qz-muted" style={{ fontSize: 12.5, wordBreak: "break-all" }}>{u.email}</div>
                  <div className="qz-muted" style={{ fontSize: 11.5 }}>joined {new Date(u.created_at).toLocaleString()}</div>
                </div>
                {u.id !== currentUserId && (
                  <button data-testid={`admin-delete-user-${u.id}`} className="btn btn--icon" title="Delete user" style={{ width: 34, height: 34, color: "var(--wrong)" }} onClick={() => onDeleteUser && onDeleteUser(u.id)}><X size={16} /></button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "logs" && (
          <div className="card" data-testid="admin-logs-panel">
            <div style={{ fontWeight: 800, marginBottom: 6 }}>AI request logs <span className="tag" style={{ marginLeft: 6 }}>{adminLogs.length}</span></div>
            <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>Every AI generation runs through the secure backend proxy. Prompts and replies are stored as short summaries (no raw secrets).</p>
            {adminLogs.length === 0 ? <p className="qz-muted" style={{ fontSize: 13 }}>No AI calls yet.</p> : adminLogs.map((l) => (
              <div key={l.id} className="req-row" data-testid={`admin-log-row-${l.id}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                    {l.user_email} <span className="tag" style={{ marginLeft: 4 }}>{l.model}</span>
                    {l.blocked && <span className="req-badge req-rejected" style={{ marginLeft: 6 }}>blocked</span>}
                    {l.has_image && <span className="tag" style={{ marginLeft: 4, color: "var(--accent2)" }}>image</span>}
                  </div>
                  <div className="qz-muted" style={{ fontSize: 12, marginTop: 3 }}>Prompt: {l.prompt_summary}</div>
                  <div className="qz-muted" style={{ fontSize: 11.5, marginTop: 2 }}>{new Date(l.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "kids" && (
          <KidsDecksPanel />
        )}

        {tab === "bank" && (
          <QuestionBankPanel />
        )}

        {tab === "credits" && (
          <CreditsAdminPanel />
        )}

        {tab === "feedback" && (
          <FeedbackAdminPanel />
        )}

        {tab === "pricing" && (
          <PricingAdminPanel />
        )}

        {tab === "promotions" && (
          <PromotionsAdminPanel />
        )}

        {tab === "plans" && (
          <PlansAdminPanel />
        )}

        {tab === "payments" && (
          <PaymentsAdminPanel />
        )}

        {tab === "patterns" && (
          <ExamPatternLibraryPanel />
        )}

        {tab === "upsc" && (
          <UpscIntelligencePanel />
        )}

        {tab === "traffic" && (
          <TrafficStatsPanel />
        )}

        {tab === "security" && (
          <ChangePasswordPanel />
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <button className="btn" onClick={() => setScoring(JSON.parse(JSON.stringify(SCORING_RULES)))}><RotateCcw size={16} /> Reset scoring</button>
          <button data-testid="admin-logout-btn" className="btn btn--ghost" style={{ marginLeft: "auto" }} onClick={onExit}><LogIn size={16} /> Log out</button>
          <button data-testid="admin-done-btn" className="btn btn--primary" onClick={onClose}><Check size={16} /> Done</button>
        </div>
      </div>
    </div>
  );
}

function TrafficStatsPanel() {
  const [range, setRange] = useState("week");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function load(nextRange) {
    setBusy(true); setErr("");
    try {
      const d = await backend.adminAnalyticsSummary(nextRange || range);
      setData(d || null);
    } catch (e) {
      setErr(e.message || "Couldn't load analytics.");
    } finally { setBusy(false); }
  }

  useEffect(() => { load(range); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range]);

  const ov = data && data.overview ? data.overview : null;
  const sources = (data && data.sources) || [];
  const totalSrc = sources.reduce((s, r) => s + (r.count || 0), 0) || 1;
  const devices = (data && data.devices) || [];
  const browsers = (data && data.browsers) || [];
  const oses = (data && data.os) || [];
  const countries = (data && data.locations && data.locations.countries) || [];
  const regions = (data && data.locations && data.locations.regions) || [];
  const cities = (data && data.locations && data.locations.cities) || [];
  const recent = (data && data.recent) || [];

  const RANGES = [
    ["today", "Today"],
    ["week", "Last 7 days"],
    ["month", "Last 30 days"],
    ["all", "All time"],
  ];

  function fmtTs(iso) {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso || "";
      return d.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch (_) { return iso || ""; }
  }

  function StatCard({ label, value, testid }) {
    return (
      <div className="card" style={{ padding: 12, minWidth: 0 }} data-testid={testid}>
        <div className="qz-muted" style={{ fontSize: 12 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{value}</div>
      </div>
    );
  }

  function BarRow({ label, count, total, testid }) {
    const pct = total > 0 ? Math.round((count * 100) / total) : 0;
    return (
      <div style={{ marginBottom: 8 }} data-testid={testid}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
          <span style={{ fontWeight: 600 }}>{label}</span>
          <span className="qz-muted">{count} · {pct}%</span>
        </div>
        <div style={{ height: 6, background: "var(--card-alt, rgba(127,127,127,0.15))", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "var(--accent2, #6ab7ff)" }} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-traffic-panel">
      <div className="banner" style={{ marginBottom: 12 }}>
        Anonymous page-view analytics. Never records emails, quiz content, or IPs (only a salted hash). Data auto-expires after {data && data.ttl_days ? data.ttl_days : 180} days.
      </div>

      <div className="seg" style={{ marginBottom: 14 }} data-testid="admin-traffic-range">
        {RANGES.map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-traffic-range-${key}`}
            className={range === key ? "act" : ""}
            onClick={() => setRange(key)}
          >{label}</button>
        ))}
      </div>

      {err && <p style={{ color: "var(--wrong)", fontSize: 13 }} data-testid="admin-traffic-error">{err}</p>}
      {busy && !data && <p className="qz-muted" style={{ fontSize: 13 }} data-testid="admin-traffic-loading">Loading…</p>}

      {ov && (
        <>
          <div className="qz-eyebrow" style={{ margin: "6px 0 10px" }}>Overview</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
            <StatCard label="Total page visits" value={ov.total_visits} testid="traffic-stat-total" />
            <StatCard label="Unique visitors" value={ov.unique_visitors} testid="traffic-stat-unique" />
            <StatCard label="Returning visitors" value={ov.returning_visitors} testid="traffic-stat-returning" />
            <StatCard label="Visits today" value={ov.visits_today} testid="traffic-stat-today" />
            <StatCard label="This week" value={ov.visits_week} testid="traffic-stat-week" />
            <StatCard label="This month" value={ov.visits_month} testid="traffic-stat-month" />
          </div>

          <div className="qz-eyebrow" style={{ margin: "6px 0 10px" }}>Traffic sources</div>
          <div className="card" style={{ padding: 14, marginBottom: 16 }} data-testid="traffic-sources">
            {sources.map((r) => (
              <BarRow key={r.label} label={r.label} count={r.count} total={totalSrc} testid={`traffic-source-${r.label.toLowerCase()}`} />
            ))}
          </div>

          <div className="qz-eyebrow" style={{ margin: "6px 0 10px" }}>Visitor locations</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 16 }}>
            <div className="card" style={{ padding: 12 }} data-testid="traffic-countries">
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Country</div>
              {countries.length === 0 ? <p className="qz-muted" style={{ fontSize: 12 }}>No data yet.</p> :
                countries.slice(0, 10).map((r) => <BarRow key={r.label} label={r.label} count={r.count} total={ov.total_visits} />)}
            </div>
            <div className="card" style={{ padding: 12 }} data-testid="traffic-regions">
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Region</div>
              {regions.length === 0 ? <p className="qz-muted" style={{ fontSize: 12 }}>No data yet.</p> :
                regions.slice(0, 10).map((r) => <BarRow key={r.label} label={r.label} count={r.count} total={ov.total_visits} />)}
            </div>
            <div className="card" style={{ padding: 12 }} data-testid="traffic-cities">
              <div style={{ fontWeight: 800, marginBottom: 6 }}>City</div>
              {cities.length === 0 ? <p className="qz-muted" style={{ fontSize: 12 }}>No data yet.</p> :
                cities.slice(0, 10).map((r) => <BarRow key={r.label} label={r.label} count={r.count} total={ov.total_visits} />)}
            </div>
          </div>

          <div className="qz-eyebrow" style={{ margin: "6px 0 10px" }}>Devices & platforms</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 16 }}>
            <div className="card" style={{ padding: 12 }} data-testid="traffic-devices">
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Device</div>
              {devices.length === 0 ? <p className="qz-muted" style={{ fontSize: 12 }}>No data yet.</p> :
                devices.map((r) => <BarRow key={r.label} label={r.label} count={r.count} total={ov.total_visits} />)}
            </div>
            <div className="card" style={{ padding: 12 }} data-testid="traffic-browsers">
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Browser</div>
              {browsers.length === 0 ? <p className="qz-muted" style={{ fontSize: 12 }}>No data yet.</p> :
                browsers.map((r) => <BarRow key={r.label} label={r.label} count={r.count} total={ov.total_visits} />)}
            </div>
            <div className="card" style={{ padding: 12 }} data-testid="traffic-os">
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Operating system</div>
              {oses.length === 0 ? <p className="qz-muted" style={{ fontSize: 12 }}>No data yet.</p> :
                oses.map((r) => <BarRow key={r.label} label={r.label} count={r.count} total={ov.total_visits} />)}
            </div>
          </div>

          <div className="qz-eyebrow" style={{ margin: "6px 0 10px" }}>Recent activity</div>
          <div className="card" style={{ padding: 0, overflow: "auto" }} data-testid="traffic-recent">
            {recent.length === 0 ? (
              <p className="qz-muted" style={{ fontSize: 13, padding: 14 }}>No visits recorded yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr style={{ textAlign: "left" }}>
                    <th style={{ padding: "10px 12px" }}>Time</th>
                    <th style={{ padding: "10px 12px" }}>Page</th>
                    <th style={{ padding: "10px 12px" }}>Source</th>
                    <th style={{ padding: "10px 12px" }}>Country</th>
                    <th style={{ padding: "10px 12px" }}>Device</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r, i) => (
                    <tr key={i} style={{ borderTop: "1px solid rgba(127,127,127,0.15)" }} data-testid={`traffic-recent-row-${i}`}>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{fmtTs(r.ts)}</td>
                      <td style={{ padding: "8px 12px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{r.path}</td>
                      <td style={{ padding: "8px 12px" }}>{r.source}</td>
                      <td style={{ padding: "8px 12px" }}>{r.city ? `${r.city}, ${r.country}` : (r.country || "—")}</td>
                      <td style={{ padding: "8px 12px" }}>{r.device}{r.browser ? ` · ${r.browser}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ChangePasswordPanel() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ kind: "", text: "" });
  async function submit() {
    setMsg({ kind: "", text: "" });
    if (next.length < 8) { setMsg({ kind: "err", text: "New password must be at least 8 characters." }); return; }
    if (next !== confirm) { setMsg({ kind: "err", text: "New password and confirmation don't match." }); return; }
    setBusy(true);
    try {
      await backend.changePassword(cur, next);
      setMsg({ kind: "ok", text: "Password changed. Next time you log in, use the new password." });
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      setMsg({ kind: "err", text: e.message || "Couldn't change password." });
    } finally { setBusy(false); }
  }
  return (
    <div className="card" data-testid="admin-security-panel">
      <div style={{ fontWeight: 800, marginBottom: 6 }}>Change password</div>
      <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>This updates your own account password. Sessions on other devices stay active until they expire (7 days).</p>
      <div className="grid" style={{ gap: 11 }}>
        <input data-testid="cp-current" className="input" type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} />
        <input data-testid="cp-new" className="input" type="password" placeholder="New password (min 8 chars)" value={next} onChange={(e) => setNext(e.target.value)} />
        <input data-testid="cp-confirm" className="input" type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
        {msg.text && <p style={{ color: msg.kind === "ok" ? "var(--correct)" : "var(--wrong)", fontSize: 13 }} data-testid="cp-msg">{msg.text}</p>}
        <button data-testid="cp-submit" className="btn btn--primary btn--block" disabled={busy || !cur || !next || !confirm} onClick={submit}>
          {busy ? <Loader2 size={18} className="spin" /> : <><ShieldCheck size={18} /> Update password</>}
        </button>
      </div>
    </div>
  );
}

function RequestForm({ onSubmit }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);
  const valid = /\S+@\S+\.\S+/.test(email);
  function submit() { if (!valid) return; onSubmit({ name, email, note }); setSent(true); }
  if (sent) return (
    <div className="pop" style={{ textAlign: "center" }}>
      <BadgeCheck size={34} color="var(--correct)" />
      <h3 className="qz-display" style={{ fontSize: 20, marginTop: 10 }}>Request sent!</h3>
      <p className="qz-muted" style={{ marginTop: 6, fontSize: 14, lineHeight: 1.5 }}>The admin will review it. Once you're approved, you'll be able to jump in.</p>
    </div>
  );
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <input className="input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
      <input className="input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <textarea className="textarea" style={{ minHeight: 76 }} placeholder="Anything the admin should know (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button className="btn btn--primary btn--block" disabled={!valid} onClick={submit}><UserPlus size={18} /> Request access</button>
      <p className="qz-muted" style={{ fontSize: 12 }}>Used only to manage preview access — not shared.</p>
    </div>
  );
}

function RequestModal({ onClose, onSubmit }) {
  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 className="qz-display" style={{ fontSize: 22 }}>Request access</h3>
          <button className="btn btn--icon" onClick={onClose}><X size={18} /></button>
        </div>
        <p className="qz-muted" style={{ fontSize: 13.5, marginBottom: 14 }}>Ask the admin for preview access. They'll review and approve your request.</p>
        <RequestForm onSubmit={onSubmit} />
      </div>
    </div>
  );
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") { resolve(false); return; }
    if (window.Razorpay) { resolve(true); return; }
    const existing = document.getElementById("razorpay-checkout-script");
    if (existing) { existing.addEventListener("load", () => resolve(true)); return; }
    const s = document.createElement("script");
    s.id = "razorpay-checkout-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function BuyCreditsModal({ user, onClose, onCredited, onOpenHistory }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");
  const [done, setDone] = useState(null); // {credits, plan}

  useEffect(() => {
    backend.listPlans().then((d) => { setPlans(d.plans || []); setLoading(false); })
      .catch((e) => { setErr(e.message || "Couldn't load plans."); setLoading(false); });
  }, []);

  async function buy(plan) {
    setErr(""); setBusyId(plan.id);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) throw new Error("Couldn't load Razorpay Checkout. Please try again.");
      const order = await backend.paymentsCreateOrder(plan.id);
      const options = {
        key: order.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Quizkosh",
        description: `${plan.name} · ${plan.credits} credits`,
        order_id: order.order_id,
        prefill: { name: order.user?.name || user.name || "", email: order.user?.email || user.email || "" },
        theme: { color: "#7ee8fa" },
        handler: async (resp) => {
          try {
            const out = await backend.paymentsVerify({
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (out && out.ok) {
              setDone({ credits: out.credits, added: out.added ?? plan.credits, plan: plan.name });
              onCredited && onCredited(out.added ?? plan.credits);
            } else {
              setErr("Payment verified but credits weren't added. Please contact support.");
            }
          } catch (e) {
            setErr(e.message || "Payment verification failed. If you were charged, please contact support.");
          } finally { setBusyId(""); }
        },
        modal: { ondismiss: () => setBusyId("") },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        setErr((resp && resp.error && resp.error.description) || "Payment failed. Please try again.");
        setBusyId("");
      });
      rzp.open();
    } catch (e) {
      setErr(e.message || "Couldn't start checkout.");
      setBusyId("");
    }
  }

  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }} data-testid="buy-credits-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 className="qz-display" style={{ fontSize: 22 }}><Zap size={18} style={{ verticalAlign: -3, marginRight: 6, color: "var(--accent2)" }} />Buy credits</h3>
          <button className="btn btn--icon" onClick={onClose} data-testid="buy-credits-close"><X size={18} /></button>
        </div>
        {done ? (
          <div data-testid="buy-credits-success" style={{ padding: "18px 4px", textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: 18, margin: "0 auto 14px", background: "linear-gradient(135deg, var(--correct), var(--accent2))", display: "grid", placeItems: "center" }}><Check size={32} color="#0b0f17" /></div>
            <h4 className="qz-display" style={{ fontSize: 22, marginBottom: 6 }}>+{done.added} credits added!</h4>
            <p className="qz-muted" style={{ fontSize: 13.5, marginBottom: 18 }}>{done.plan} plan · You now have <b>{done.credits}</b> credits in your wallet.</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button className="btn btn--primary" onClick={onClose}>Start a quiz</button>
              <button className="btn" onClick={onOpenHistory}>See receipt</button>
            </div>
          </div>
        ) : (
          <>
            <p className="qz-muted" style={{ fontSize: 13.5, marginBottom: 14 }}>Top up your wallet with one of the plans below. Credits don't expire and stack with anything you already have.</p>
            {loading && <p className="qz-muted" style={{ fontSize: 13 }}>Loading plans…</p>}
            {err && <p style={{ color: "var(--wrong)", fontSize: 13.5, marginBottom: 10 }} data-testid="buy-credits-err">{err}</p>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 12 }}>
              {plans.map((p, i) => (
                <div key={p.id} className="card" data-testid={`plan-card-${p.id}`} style={{ padding: 18, border: i === 1 ? "2px solid var(--accent2)" : "1px solid var(--border)", position: "relative" }}>
                  {i === 1 && <span className="tag" style={{ position: "absolute", top: 10, right: 10, color: "var(--accent2)" }}>Popular</span>}
                  <div style={{ fontWeight: 800, fontSize: 18 }}>{p.name}</div>
                  <div style={{ marginTop: 4, marginBottom: 8 }}><span style={{ fontSize: 26, fontWeight: 800 }}>₹{p.price_inr}</span></div>
                  <div className="qz-muted" style={{ fontSize: 13 }}>{p.credits.toLocaleString()} credits</div>
                  <div className="qz-muted" style={{ fontSize: 11.5, marginTop: 2 }}>≈ ₹{(p.price_inr / p.credits).toFixed(3)} / credit</div>
                  {p.description && <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 8, minHeight: 28 }}>{p.description}</p>}
                  <button data-testid={`plan-buy-${p.id}`} className="btn btn--primary btn--block" disabled={!!busyId} onClick={() => buy(p)} style={{ marginTop: 10 }}>
                    {busyId === p.id ? <Loader2 size={16} className="spin" /> : <><Zap size={14} /> Buy</>}
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
              <span className="qz-muted" style={{ fontSize: 12 }}>Secure checkout powered by Razorpay · INR</span>
              <button className="btn" data-testid="buy-history-link" onClick={onOpenHistory} style={{ padding: "6px 10px", fontSize: 12 }}>Payment history →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PaymentHistoryModal({ onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    backend.paymentsMe().then(setData).catch((e) => setErr(e.message || "Couldn't load history."));
  }, []);
  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }} data-testid="payment-history-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h3 className="qz-display" style={{ fontSize: 22 }}>Payment history</h3>
          <button className="btn btn--icon" onClick={onClose}><X size={18} /></button>
        </div>
        {!data && !err && <p className="qz-muted" style={{ fontSize: 13 }}>Loading…</p>}
        {err && <p style={{ color: "var(--wrong)", fontSize: 13.5 }}>{err}</p>}
        {data && (
          <>
            <div className="glass" style={{ padding: 10, marginBottom: 12 }}>
              <div className="qz-muted" style={{ fontSize: 12 }}>Current wallet balance</div>
              <div style={{ fontWeight: 800, fontSize: 20 }}>{data.current_credits.toLocaleString()} credits</div>
            </div>
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>Payments</div>
            {data.payments.length === 0 && <p className="qz-muted" style={{ fontSize: 12.5 }}>No payments yet — pick a plan to get started.</p>}
            {data.payments.map((p) => (
              <div key={p.id} className="req-row" data-testid={`history-row-${p.id}`} style={{ alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{p.plan_name} · ₹{p.amount_inr}</div>
                  <div className="qz-muted" style={{ fontSize: 12 }}>{new Date(p.created_at).toLocaleString()}</div>
                  <div className="qz-muted" style={{ fontSize: 11.5, wordBreak: "break-all" }}>
                    Order: {p.razorpay_order_id}{p.razorpay_payment_id ? ` · Payment: ${p.razorpay_payment_id}` : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span className="tag" style={{ color: p.status === "success" ? "var(--correct)" : (p.status === "failed" ? "var(--wrong)" : "var(--gold)") }}>{p.status}</span>
                  {p.status === "success" && <div style={{ fontSize: 12.5, marginTop: 4 }}>+{p.credits} credits</div>}
                </div>
              </div>
            ))}
            {data.ledger && data.ledger.length > 0 && (
              <>
                <div style={{ fontWeight: 800, fontSize: 14, margin: "14px 0 6px" }}>Credit history</div>
                {data.ledger.slice(0, 30).map((l) => (
                  <div key={l.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "4px 0", borderBottom: "1px dashed var(--border)" }}>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 700 }}>{l.source.replaceAll("_", " ")}</span>
                      {l.note && <span className="qz-muted"> · {l.note}</span>}
                      <div className="qz-muted" style={{ fontSize: 11 }}>{new Date(l.created_at).toLocaleString()}</div>
                    </div>
                    <div style={{ color: l.delta >= 0 ? "var(--correct)" : "var(--wrong)", fontWeight: 700, whiteSpace: "nowrap", marginLeft: 8 }}>
                      {l.delta >= 0 ? `+${l.delta}` : l.delta} <span className="qz-muted" style={{ fontWeight: 400, fontSize: 11 }}>· bal {l.balance_after}</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InterestBanner({ onSet }) {
  const [open, setOpen] = useState(false);
  const [interest, setInterest] = useState("");
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function save() {
    setErr("");
    if (!interest) { setErr("Pick an interest first."); return; }
    const words = custom.trim().split(/\s+/).filter(Boolean);
    if (interest === "Others" && words.length === 0) { setErr("Describe your interest in up to 5 words."); return; }
    if (interest === "Others" && words.length > 5) { setErr("Custom interest must be 5 words or fewer."); return; }
    setBusy(true);
    try {
      await backend.setInterest(interest, interest === "Others" ? words.slice(0, 5).join(" ") : "");
      const me = await backend.me();
      if (me && me.user) onSet(me.user);
      setOpen(false);
    } catch (e) { setErr(e.message || "Couldn't save."); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div
        data-testid="interest-banner"
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0, padding: 12,
          display: "flex", justifyContent: "center", pointerEvents: "none", zIndex: 25,
        }}
      >
        <div className="glass" style={{ pointerEvents: "auto", padding: "12px 16px", display: "flex", gap: 12, alignItems: "center", maxWidth: 720, borderLeft: "3px solid var(--accent2)" }}>
          <Flame size={18} color="var(--accent2)" />
          <div style={{ fontSize: 13.5, flex: 1, minWidth: 0 }}>
            <b>Personalize your Daily Challenge</b> — pick your area of interest so we tailor questions to you.
          </div>
          <button data-testid="interest-banner-open" className="btn btn--primary" style={{ padding: "6px 12px" }} onClick={() => setOpen(true)}>Choose</button>
        </div>
      </div>
      {open && (
        <div className="ovl" onClick={() => setOpen(false)}>
          <div className="modal" data-testid="interest-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <h3 className="qz-display" style={{ fontSize: 22 }}>What interests you?</h3>
              <button className="btn btn--icon" onClick={() => setOpen(false)} data-testid="interest-modal-close"><X size={18} /></button>
            </div>
            <p className="qz-muted" style={{ fontSize: 13.5, marginBottom: 12 }}>We'll generate Daily Challenge questions around this. Change it anytime.</p>
            <select data-testid="interest-modal-select" className="input" value={interest} onChange={(e) => { setInterest(e.target.value); if (e.target.value !== "Others") setCustom(""); }}>
              <option value="">Pick one</option>
              {INTEREST_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            {interest === "Others" && (
              <div style={{ marginTop: 8 }}>
                <input data-testid="interest-modal-custom" className="input" placeholder="Describe your interest (max 5 words)" value={custom} onChange={(e) => {
                  const w = e.target.value.split(/\s+/).filter(Boolean);
                  if (w.length <= 5) setCustom(e.target.value); else setCustom(w.slice(0, 5).join(" "));
                }} />
                <p className="qz-muted" style={{ fontSize: 12, marginTop: 4 }}>{custom.trim().split(/\s+/).filter(Boolean).length}/5 words</p>
              </div>
            )}
            {err && <p style={{ color: "var(--wrong)", fontSize: 13, marginTop: 8 }} data-testid="interest-modal-err">{err}</p>}
            <button data-testid="interest-modal-save" className="btn btn--primary btn--block" disabled={busy} onClick={save} style={{ marginTop: 12 }}>{busy ? <Loader2 size={16} className="spin" /> : <Check size={16} />} Save</button>
          </div>
        </div>
      )}
    </>
  );
}

function SchoolLogoModal({ user, onClose, onUpdated }) {
  const [logo, setLogo] = useState({ data: user.school_logo_data || "", mime: user.school_logo_mime || "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  async function save() {
    setErr(""); setMsg(""); setBusy(true);
    try {
      const d = await backend.setSchoolLogo(logo.data || "", logo.mime || "");
      onUpdated && onUpdated({ ...user, school_logo_data: d.school_logo_data, school_logo_mime: d.school_logo_mime });
      setMsg("Logo saved.");
    } catch (e) { setErr(e.message || "Couldn't save the logo."); }
    finally { setBusy(false); }
  }

  async function clear() {
    setErr(""); setMsg(""); setBusy(true);
    try {
      await backend.clearSchoolLogo();
      setLogo({ data: "", mime: "" });
      onUpdated && onUpdated({ ...user, school_logo_data: "", school_logo_mime: "" });
      setMsg("Logo removed.");
    } catch (e) { setErr(e.message || "Couldn't remove the logo."); }
    finally { setBusy(false); }
  }

  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }} data-testid="school-logo-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 className="qz-display" style={{ fontSize: 22 }}>School logo</h3>
          <button className="btn btn--icon" onClick={onClose} data-testid="school-logo-modal-close"><X size={18} /></button>
        </div>
        <p className="qz-muted" style={{ fontSize: 13.5, marginBottom: 12 }}>Upload your school's logo (PNG or JPG, up to 600 KB). It will appear beside the Quizkosh logo across the app.</p>
        <SchoolLogoPicker logo={logo} onChange={setLogo} err={err} setErr={setErr} />
        {msg && <p style={{ color: "var(--correct)", fontSize: 13, marginTop: 8 }} data-testid="school-logo-saved">{msg}</p>}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="btn btn--primary" disabled={busy || !logo.data} onClick={save} data-testid="school-logo-save">{busy ? <Loader2 size={16} className="spin" /> : <Check size={16} />} Save logo</button>
          {(user.school_logo_data || logo.data) && <button className="btn" disabled={busy} onClick={clear} data-testid="school-logo-remove" style={{ color: "var(--wrong)" }}><X size={16} /> Remove</button>}
        </div>
      </div>
    </div>
  );
}

function FeedbackModal({ onClose }) {
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const max = 500;
  const remaining = max - message.length;

  async function submit() {
    setErr("");
    if (rating < 1) { setErr("Please pick a star rating from 1 to 5."); return; }
    const trimmed = message.trim();
    if (!trimmed) { setErr("Please share a quick note so we know what to improve."); return; }
    if (trimmed.length > max) { setErr(`Please keep your feedback under ${max} characters.`); return; }
    setBusy(true);
    try {
      await backend.submitFeedback(trimmed, rating);
      setDone(true);
    } catch (e) {
      setErr(e.message || "We couldn't send your feedback. Please try again.");
    } finally { setBusy(false); }
  }

  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }} data-testid="feedback-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 className="qz-display" style={{ fontSize: 22 }}>Provide feedback</h3>
          <button className="btn btn--icon" onClick={onClose} data-testid="feedback-close-btn"><X size={18} /></button>
        </div>
        {done ? (
          <div data-testid="feedback-thanks" style={{ padding: "18px 4px", textAlign: "center" }}>
            <div style={{ width: 48, height: 48, borderRadius: 16, margin: "0 auto 14px", background: "linear-gradient(135deg, var(--correct), var(--accent2))", display: "grid", placeItems: "center" }}><Check size={26} color="#0b0f17" /></div>
            <h4 className="qz-display" style={{ fontSize: 20, marginBottom: 6 }}>Thanks for the feedback!</h4>
            <p className="qz-muted" style={{ fontSize: 13.5, marginBottom: 18 }}>We read every note and use it to make Quizkosh better. ✨</p>
            <button className="btn btn--primary btn--block" onClick={onClose} data-testid="feedback-thanks-close">Close</button>
          </div>
        ) : (
          <>
            <p className="qz-muted" style={{ fontSize: 13.5, marginBottom: 14 }}>Tell us what's working, what's not, or what you'd love to see next.</p>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Your rating</div>
              <div data-testid="feedback-stars" style={{ display: "flex", gap: 6 }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = (hover || rating) >= n;
                  return (
                    <button key={n} type="button" data-testid={`feedback-star-${n}`} className="btn btn--icon" aria-label={`${n} star`} title={`${n} star${n > 1 ? "s" : ""}`} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} style={{ width: 38, height: 38, color: filled ? "var(--gold)" : "var(--muted)" }}>
                      <Star size={22} fill={filled ? "currentColor" : "none"} strokeWidth={1.8} />
                    </button>
                  );
                })}
              </div>
            </div>
            <textarea data-testid="feedback-message" className="input" style={{ width: "100%", height: 110, padding: 12 }} placeholder="Share your thoughts (max 500 characters)…" value={message} maxLength={max} onChange={(e) => setMessage(e.target.value)} />
            <p className="qz-muted" style={{ fontSize: 12, marginTop: 4, textAlign: "right" }} data-testid="feedback-charcount">{remaining} characters left</p>
            {err && <p style={{ color: "var(--wrong)", fontSize: 13, marginTop: 6 }} data-testid="feedback-err">{err}</p>}
            <button className="btn btn--primary btn--block" disabled={busy || rating < 1 || !message.trim()} onClick={submit} style={{ marginTop: 12 }} data-testid="feedback-submit-btn">
              {busy ? <Loader2 size={18} className="spin" /> : <><Check size={18} /> Submit feedback</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function GateScreen({ requests, myEmail, onSubmit, onAdmin }) {
  const mine = myEmail ? requests.find((r) => r.email.toLowerCase() === myEmail.toLowerCase()) : null;
  const status = mine ? mine.status : null;
  return (
    <div className="qz-wrap" style={{ display: "grid", placeItems: "center", minHeight: "78vh" }}>
      <div className="card glow" style={{ maxWidth: 460, padding: 32, textAlign: "center" }}>
        <div className="mark" style={{ width: 48, height: 48, borderRadius: 14, margin: "0 auto 14px", display: "grid", placeItems: "center" }}><ShieldCheck size={24} /></div>
        <h2 className="qz-display" style={{ fontSize: 26 }}>Approved access only</h2>
        <p className="qz-muted" style={{ marginTop: 8, marginBottom: 18, lineHeight: 1.5 }}>This preview is invite-only right now. Request access and the admin will let you in.</p>
        {status === "pending" && <div className="banner">Your request is pending — hang tight while the admin reviews it.</div>}
        {status === "rejected" && <div className="banner" style={{ borderColor: "var(--wrong)" }}>This request wasn't approved. You can submit a new one below.</div>}
        {status !== "pending" && <div style={{ marginTop: status === "rejected" ? 14 : 0, textAlign: "left" }}><RequestForm onSubmit={onSubmit} /></div>}
        <p className="qz-muted" style={{ fontSize: 12, marginTop: 16 }}>Are you the owner? <a onClick={onAdmin} style={{ color: "var(--accent2)", cursor: "pointer", fontWeight: 700 }}>Open admin</a></p>
      </div>
    </div>
  );
}

const KIDS_DECK_COLORS = ["#7048e8", "#ff6b6b", "#4dabf7", "#ffd43b", "#69db7c", "#b197fc", "#ffa94d", "#ff8cc6", "#63e6be", "#ff9ec7"];
const KIDS_DECK_ICONS = ["✨", "🎈", "🦒", "🚀", "🌈", "🐠", "🍎", "⭐", "🎨", "🦋", "🐶", "📚"];

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.onload = () => {
      const s = String(reader.result || "");
      const i = s.indexOf(",");
      resolve({ b64: i >= 0 ? s.slice(i + 1) : s, mime: file.type || "image/png" });
    };
    reader.readAsDataURL(file);
  });
}

function KidsDecksPanel() {
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | <deck-id>
  const [form, setForm] = useState({ name: "", icon: "✨", color: KIDS_DECK_COLORS[0], items: [] });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ kind: "", text: "" });

  async function refresh() {
    setLoading(true);
    try { const d = await backend.kidsListDecks(); setDecks(d.decks || []); }
    catch (e) { setMsg({ kind: "err", text: e.message || "Couldn't load decks." }); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  function startNew() {
    setForm({ name: "", icon: "✨", color: KIDS_DECK_COLORS[0], items: [{ name: "", image_url: "", image_b64: "", image_mime: "" }] });
    setEditing("new"); setMsg({ kind: "", text: "" });
  }
  function startEdit(d) {
    setForm({ name: d.name, icon: d.icon, color: d.color, items: d.items.map((it) => ({ ...it })) });
    setEditing(d.id); setMsg({ kind: "", text: "" });
  }
  function cancelEdit() { setEditing(null); setMsg({ kind: "", text: "" }); }

  function patchItem(i, patch) { setForm((f) => ({ ...f, items: f.items.map((it, k) => k === i ? { ...it, ...patch } : it) })); }
  function addItem() { setForm((f) => ({ ...f, items: [...f.items, { name: "", image_url: "", image_b64: "", image_mime: "" }] })); }
  function removeItem(i) { setForm((f) => ({ ...f, items: f.items.filter((_, k) => k !== i) })); }

  async function pickFile(i, file) {
    if (!file) return;
    if (file.size > 200 * 1024) { setMsg({ kind: "err", text: `Image for "${form.items[i].name || "item"}" is too large (max 200 KB).` }); return; }
    try {
      const { b64, mime } = await fileToBase64(file);
      patchItem(i, { image_b64: b64, image_mime: mime, image_url: "" });
    } catch (e) { setMsg({ kind: "err", text: e.message || "Couldn't read file." }); }
  }

  async function save() {
    setMsg({ kind: "", text: "" });
    if (!form.name.trim()) { setMsg({ kind: "err", text: "Give the deck a name." }); return; }
    const ready = form.items.filter((it) => it.name.trim() && (it.image_b64 || it.image_url));
    if (ready.length < 4) { setMsg({ kind: "err", text: "Each deck needs at least 4 items (each item needs a name AND a picture)." }); return; }
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), icon: form.icon, color: form.color, items: ready };
      if (editing === "new") await backend.adminCreateKidsDeck(payload);
      else await backend.adminUpdateKidsDeck(editing, payload);
      setEditing(null);
      setMsg({ kind: "ok", text: "Saved. Kids will see this deck next time they open Juniors." });
      refresh();
    } catch (e) {
      setMsg({ kind: "err", text: e.message || "Couldn't save deck." });
    } finally { setBusy(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this deck? Kids will no longer see it.")) return;
    try { await backend.adminDeleteKidsDeck(id); refresh(); }
    catch (e) { setMsg({ kind: "err", text: e.message || "Couldn't delete." }); }
  }

  if (editing) {
    return (
      <div className="card" data-testid="admin-kids-edit">
        <div style={{ fontWeight: 800, marginBottom: 6 }}>{editing === "new" ? "New kids deck" : "Edit deck"}</div>
        <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>Each item = one picture + the word/name. Kids tap the correct picture out of 4 — the app auto-generates "Which one is the X?". Minimum 4 items per deck.</p>
        <div className="grid" style={{ gap: 11 }}>
          <input data-testid="kd-name" className="input" placeholder="Deck name (e.g. Farm animals)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span className="qz-muted" style={{ fontSize: 12.5 }}>Icon:</span>
            {KIDS_DECK_ICONS.map((ic) => (
              <button key={ic} type="button" className={"btn btn--icon" + (form.icon === ic ? " on" : "")} style={{ width: 36, height: 36, fontSize: 18 }} onClick={() => setForm({ ...form, icon: ic })}>{ic}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="qz-muted" style={{ fontSize: 12.5 }}>Color:</span>
            {KIDS_DECK_COLORS.map((c) => (
              <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} title={c} style={{ width: 26, height: 26, borderRadius: "50%", background: c, border: form.color === c ? "3px solid var(--accent2)" : "2px solid var(--border)", cursor: "pointer" }} />
            ))}
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <b style={{ fontSize: 14 }}>Items <span className="tag" style={{ marginLeft: 6 }}>{form.items.length}</span></b>
            <button data-testid="kd-add-item" className="btn" style={{ padding: "8px 12px" }} onClick={addItem}><Plus size={14} /> Add item</button>
          </div>
          {form.items.map((it, i) => {
            const preview = it.image_b64 ? `data:${it.image_mime || "image/png"};base64,${it.image_b64}` : it.image_url;
            return (
              <div key={i} className="req-row" data-testid={`kd-item-${i}`} style={{ alignItems: "flex-start" }}>
                <div style={{ width: 64, height: 64, borderRadius: 12, background: "var(--surface2)", display: "grid", placeItems: "center", overflow: "hidden", flex: "none" }}>
                  {preview ? <img src={preview} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /> : <Baby size={26} color="var(--muted)" />}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: "grid", gap: 6 }}>
                  <input className="input" placeholder="Name (e.g. Tiger)" value={it.name} onChange={(e) => patchItem(i, { name: e.target.value })} />
                  <input className="input" placeholder="Image URL (https://…)" value={it.image_url} onChange={(e) => patchItem(i, { image_url: e.target.value, image_b64: "" })} />
                  <label className="btn btn--ghost" style={{ alignSelf: "start", padding: "8px 12px", cursor: "pointer" }}>
                    <Upload size={14} /> {it.image_b64 ? "Replace upload" : "Upload picture"}
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => pickFile(i, e.target.files?.[0])} />
                  </label>
                </div>
                <button className="btn btn--icon" style={{ color: "var(--wrong)" }} onClick={() => removeItem(i)} title="Remove"><X size={16} /></button>
              </div>
            );
          })}
        </div>

        {msg.text && <p style={{ color: msg.kind === "ok" ? "var(--correct)" : "var(--wrong)", fontSize: 13, marginTop: 10 }}>{msg.text}</p>}

        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button data-testid="kd-save" className="btn btn--primary" disabled={busy} onClick={save}>{busy ? <Loader2 size={16} className="spin" /> : <Check size={16} />} Save deck</button>
          <button className="btn btn--ghost" onClick={cancelEdit}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="admin-kids-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontWeight: 800 }}>Kids decks <span className="tag" style={{ marginLeft: 6 }}>{decks.length}</span></div>
        <button data-testid="kd-new" className="btn btn--primary" style={{ padding: "8px 12px" }} onClick={startNew}><Plus size={14} /> New deck</button>
      </div>
      <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>One-time uploads. Kids tap-and-play, zero AI tokens used. Built-in emoji decks stay alongside yours.</p>
      {msg.text && <p style={{ color: msg.kind === "ok" ? "var(--correct)" : "var(--wrong)", fontSize: 13, marginBottom: 10 }}>{msg.text}</p>}
      {loading ? <p className="qz-muted" style={{ fontSize: 13 }}>Loading…</p>
        : decks.length === 0 ? <p className="qz-muted" style={{ fontSize: 13 }}>No decks yet. Click <b>New deck</b> to add pictures + names — kids will tap them.</p>
        : decks.map((d) => (
          <div key={d.id} className="req-row" data-testid={`kd-row-${d.id}`}>
            <div style={{ width: 44, height: 44, borderRadius: 12, display: "grid", placeItems: "center", fontSize: 22, background: `linear-gradient(135deg, ${d.color}, ${d.color}aa)`, flex: "none" }}>{d.icon || "✨"}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800 }}>{d.name}</div>
              <div className="qz-muted" style={{ fontSize: 12.5 }}>{d.items.length} items · updated {new Date(d.updated_at || d.created_at).toLocaleDateString()}</div>
            </div>
            <button data-testid={`kd-edit-${d.id}`} className="btn" style={{ padding: "8px 12px" }} onClick={() => startEdit(d)}><Wand2 size={14} /> Edit</button>
            <button data-testid={`kd-del-${d.id}`} className="btn btn--icon" style={{ color: "var(--wrong)" }} onClick={() => remove(d.id)} title="Delete"><X size={16} /></button>
          </div>
        ))
      }
    </div>
  );
}


/* ---------- Question Bank admin panel ---------- */
function parseCsv(text) {
  // Lightweight CSV parser (handles quoted fields and commas inside quotes).
  const rows = [];
  let i = 0, field = "", row = [], inQ = false;
  while (i < text.length) {
    const c = text[i];
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
      if (c === '"') { inQ = false; i++; continue; }
      field += c; i++; continue;
    }
    if (c === '"') { inQ = true; i++; continue; }
    if (c === ",") { row.push(field); field = ""; i++; continue; }
    if (c === "\r") { i++; continue; }
    if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
    field += c; i++;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => c && c.trim()));
}

function buildBankItemsFromCsv(text) {
  const rows = parseCsv(text);
  if (!rows.length) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const idx = (name) => header.indexOf(name);
  const iQ = idx("q") >= 0 ? idx("q") : idx("question");
  const iA = idx("a") >= 0 ? idx("a") : idx("option_a");
  const iB = idx("b") >= 0 ? idx("b") : idx("option_b");
  const iC = idx("c") >= 0 ? idx("c") : idx("option_c");
  const iD = idx("d") >= 0 ? idx("d") : idx("option_d");
  const iCorrect = idx("correct");
  const iExpl = idx("explanation");
  const iCat = idx("category");
  const iDiff = idx("difficulty");
  if ([iQ, iA, iB, iC, iD, iCorrect].some((x) => x < 0)) return [];
  const out = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const correctRaw = (row[iCorrect] || "").trim().toUpperCase();
    let correct = parseInt(correctRaw, 10);
    if (isNaN(correct)) correct = ({ "A": 0, "B": 1, "C": 2, "D": 3 })[correctRaw];
    if (correct == null || correct < 0 || correct > 3) continue;
    const q = (row[iQ] || "").trim();
    const options = [row[iA], row[iB], row[iC], row[iD]].map((s) => (s || "").trim());
    if (!q || options.some((o) => !o)) continue;
    out.push({
      q, options, correct,
      explanation: iExpl >= 0 ? (row[iExpl] || "").trim() : "",
      category: iCat >= 0 ? (row[iCat] || "General").trim() : "General",
      difficulty: iDiff >= 0 ? (row[iDiff] || "medium").trim().toLowerCase() : "medium",
      hint: "", style: "concept",
    });
  }
  return out;
}

function QuestionBankPanel() {
  const [stats, setStats] = useState({ total: 0, by_category: [] });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ kind: "", text: "" });
  async function refresh() {
    try { const s = await backend.adminBankStats(); setStats(s); }
    catch (e) { setMsg({ kind: "err", text: e.message || "Couldn't load stats." }); }
  }
  useEffect(() => { refresh(); }, []);

  async function pickFile(file) {
    if (!file) return;
    setMsg({ kind: "", text: "" });
    const t = await file.text();
    if (file.name.toLowerCase().endsWith(".csv")) {
      const items = buildBankItemsFromCsv(t);
      if (!items.length) { setMsg({ kind: "err", text: "CSV needs headers: q, a, b, c, d, correct (A-D or 0-3). Optional: explanation, category, difficulty." }); return; }
      setText(JSON.stringify(items, null, 2));
      setMsg({ kind: "ok", text: `Parsed ${items.length} rows from CSV — review below and click Upload.` });
    } else {
      setText(t);
    }
  }

  async function upload() {
    setBusy(true); setMsg({ kind: "", text: "" });
    try {
      let arr;
      try { arr = JSON.parse(text); }
      catch { setMsg({ kind: "err", text: "Not valid JSON. Either upload a CSV or paste a JSON array." }); setBusy(false); return; }
      if (!Array.isArray(arr) || !arr.length) { setMsg({ kind: "err", text: "Expected a non-empty JSON array of questions." }); setBusy(false); return; }
      const d = await backend.adminBankBulkUpload(arr);
      setMsg({ kind: "ok", text: `Inserted ${d.inserted} questions.` });
      setText("");
      refresh();
    } catch (e) { setMsg({ kind: "err", text: e.message || "Upload failed." }); }
    finally { setBusy(false); }
  }

  async function deleteCat(cat) {
    if (!window.confirm(`Delete ALL questions in category "${cat}"?`)) return;
    try { await backend.adminBankDeleteCategory(cat); refresh(); }
    catch (e) { setMsg({ kind: "err", text: e.message || "Couldn't delete." }); }
  }

  return (
    <div className="card" data-testid="admin-bank-panel">
      <div style={{ fontWeight: 800, marginBottom: 4 }}>Question bank <span className="tag" style={{ marginLeft: 6 }}>{stats.total}</span></div>
      <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>Upload thousands of questions in CSV or JSON. When a user picks a category with ≥4 banked questions, we serve from the bank (zero AI cost). Otherwise we fall back to AI.</p>

      <div className="banner" style={{ marginBottom: 12, fontSize: 12.5, lineHeight: 1.5 }}>
        <b>CSV format:</b> headers <span className="qz-mono">q, a, b, c, d, correct, explanation, category, difficulty</span>. <b>JSON:</b> array of <span className="qz-mono">{`{q, options:[…4], correct:0-3, category, difficulty, explanation}`}</span>.
      </div>

      <label className="btn btn--ghost" style={{ alignSelf: "start", padding: "8px 12px", cursor: "pointer", display: "inline-flex" }}>
        <Upload size={14} /> Upload CSV or JSON file
        <input data-testid="bank-file-input" type="file" accept=".csv,.json,text/csv,application/json,text/plain" style={{ display: "none" }} onChange={(e) => pickFile(e.target.files?.[0])} />
      </label>
      <textarea data-testid="bank-text" className="input" style={{ width: "100%", height: 220, marginTop: 10, fontFamily: "var(--fm)", fontSize: 12 }} placeholder='Or paste JSON directly here, e.g. [{"q":"...","options":["A","B","C","D"],"correct":1,"category":"GK"}]' value={text} onChange={(e) => setText(e.target.value)} />
      {msg.text && <p style={{ color: msg.kind === "ok" ? "var(--correct)" : "var(--wrong)", fontSize: 13, marginTop: 8 }} data-testid="bank-msg">{msg.text}</p>}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button data-testid="bank-upload-btn" className="btn btn--primary" disabled={busy || !text} onClick={upload}>{busy ? <Loader2 size={16} className="spin" /> : <Upload size={16} />} Upload</button>
        <button className="btn btn--ghost" onClick={() => setText("")}>Clear</button>
      </div>

      <div style={{ marginTop: 18 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>By category</div>
        {stats.by_category.length === 0 ? <p className="qz-muted" style={{ fontSize: 13 }}>No questions banked yet.</p> :
          stats.by_category.map((c) => (
            <div key={c.name} className="req-row" data-testid={`bank-cat-${c.name}`}>
              <div style={{ flex: 1 }}><b>{c.name}</b> <span className="tag" style={{ marginLeft: 6 }}>{c.count} questions</span></div>
              <button className="btn btn--icon" style={{ color: "var(--wrong)" }} onClick={() => deleteCat(c.name)} title="Delete category"><X size={16} /></button>
            </div>
          ))
        }
      </div>
    </div>
  );
}


function CreditsAdminPanel() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [msg, setMsg] = useState({ kind: "", text: "" });
  const [pending, setPending] = useState({}); // uid -> {amount, action, note}

  async function search() {
    setMsg({ kind: "", text: "" }); setBusy(true);
    try { const d = await backend.adminSearchUsers(q.trim()); setUsers(d.users || []); }
    catch (e) { setMsg({ kind: "err", text: e.message || "Couldn't search." }); }
    finally { setBusy(false); }
  }

  function patch(uid, p) { setPending((s) => ({ ...s, [uid]: { ...(s[uid] || { action: "add", amount: 10, note: "" }), ...p } })); }

  async function apply(u) {
    const p = pending[u.id] || { action: "add", amount: 10, note: "" };
    const amount = parseInt(p.amount, 10);
    if (isNaN(amount) || amount < 0) { setMsg({ kind: "err", text: "Amount must be a positive number." }); return; }
    try {
      const d = await backend.adminAdjustCredits(u.id, p.action, amount, p.note);
      setUsers((rows) => rows.map((r) => r.id === u.id ? { ...r, credits: d.credits, bonus_credits: d.credits, effective_limit: d.credits } : r));
      setPending((s) => { const c = { ...s }; delete c[u.id]; return c; });
      setMsg({ kind: "ok", text: `Updated ${u.email}: wallet balance = ${d.credits}` });
    } catch (e) { setMsg({ kind: "err", text: e.message || "Couldn't update." }); }
  }

  async function toggleType(u) {
    const next = u.user_type === "school" ? "individual" : "school";
    let schoolName = u.school_name || "";
    if (next === "school") {
      schoolName = window.prompt("School name (optional):", schoolName) ?? schoolName;
    }
    try {
      const d = await backend.adminSetUserType(u.id, next, schoolName);
      setUsers((rows) => rows.map((r) => r.id === u.id ? { ...r, user_type: d.user_type, school_name: d.school_name } : r));
      setMsg({ kind: "ok", text: `${u.email} is now ${d.user_type === "school" ? "a school account" : "an individual account"}.` });
    } catch (e) {
      setMsg({ kind: "err", text: e.message || "Couldn't update user type." });
    }
  }

  return (
    <div className="card" data-testid="admin-credits-panel">
      <div style={{ fontWeight: 800, marginBottom: 4 }}>User credit management</div>
      <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
        Every user has a daily AI quota of <b>50 questions</b> (free tier). Bonus credits are added on top of that limit and persist until used.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input data-testid="credits-search-input" className="input" placeholder="Search by email or name" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
        <button data-testid="credits-search-btn" className="btn btn--primary" disabled={busy || !q.trim()} onClick={search}>{busy ? <Loader2 size={16} className="spin" /> : <ShieldCheck size={16} />} Search</button>
      </div>
      {msg.text && <p style={{ color: msg.kind === "ok" ? "var(--correct)" : "var(--wrong)", fontSize: 13, marginBottom: 10 }} data-testid="credits-msg">{msg.text}</p>}
      {users.length === 0 && <p className="qz-muted" style={{ fontSize: 13 }}>Search to find a user.</p>}
      {users.map((u) => {
        const p = pending[u.id] || { action: "add", amount: 10, note: "" };
        return (
          <div key={u.id} className="req-row" data-testid={`credits-row-${u.id}`} style={{ alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700 }}>
                {u.name} <span className="tag" style={{ marginLeft: 6 }}>{u.role}</span>
                {u.user_type === "school" && <span className="tag" style={{ marginLeft: 6, background: "rgba(244,134,38,.18)", color: "#f48626", border: "1px solid rgba(244,134,38,.4)" }}>school</span>}
              </div>
              <div className="qz-muted" style={{ fontSize: 12.5, wordBreak: "break-all" }}>{u.email}{u.school_name ? ` · ${u.school_name}` : ""}</div>
              <div className="qz-muted" style={{ fontSize: 12, marginTop: 4 }}>
                Credits: <b>{u.credits ?? u.bonus_credits}</b>{u.used_today > 0 && <> · last 24h activity: {u.used_today} questions</>}
              </div>
              <div style={{ marginTop: 6 }}>
                <button data-testid={`type-toggle-${u.id}`} className="btn" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => toggleType(u)}>
                  {u.user_type === "school" ? "Set as individual" : "Mark as school"}
                </button>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <select data-testid={`credits-action-${u.id}`} className="input" style={{ padding: "8px 10px", width: 100 }} value={p.action} onChange={(e) => patch(u.id, { action: e.target.value })}>
                <option value="add">Add</option>
                <option value="deduct">Deduct</option>
                <option value="set">Set</option>
              </select>
              <input data-testid={`credits-amount-${u.id}`} className="input" type="number" min="0" style={{ width: 90, padding: "8px 10px" }} value={p.amount} onChange={(e) => patch(u.id, { amount: e.target.value })} />
              <input data-testid={`credits-note-${u.id}`} className="input" placeholder="note (optional)" style={{ width: 160, padding: "8px 10px" }} value={p.note} onChange={(e) => patch(u.id, { note: e.target.value })} />
              <button data-testid={`credits-apply-${u.id}`} className="btn btn--primary" style={{ padding: "8px 12px" }} onClick={() => apply(u)}><Check size={14} /> Apply</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}


/* ---------- Question Paper Generator (Teacher module) ---------- */

function PlansAdminPanel() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState({ name: "", price_inr: "", credits: "", active: true, order: 99, description: "" });

  async function load() {
    setErr("");
    try { const d = await backend.adminListPlans(); setPlans(d.plans || []); }
    catch (e) { setErr(e.message || "Couldn't load plans."); }
  }
  useEffect(() => { load(); }, []);

  function patch(id, k, v) { setPlans((rows) => rows.map((p) => p.id === id ? { ...p, [k]: v } : p)); }

  async function saveRow(p) {
    setBusy(true); setMsg(""); setErr("");
    try {
      await backend.adminUpdatePlan(p.id, {
        name: p.name, price_inr: parseInt(p.price_inr, 10) || 0,
        credits: parseInt(p.credits, 10) || 0,
        active: !!p.active, order: parseInt(p.order, 10) || 0,
        description: p.description || "",
      });
      setMsg(`Saved ${p.name}.`); await load();
    } catch (e) { setErr(e.message || "Couldn't save."); }
    finally { setBusy(false); }
  }

  async function disable(p) {
    if (!window.confirm(`Hide the "${p.name}" plan from the buy page? (Historical payments stay intact.)`)) return;
    try { await backend.adminDeletePlan(p.id); await load(); setMsg(`${p.name} disabled.`); }
    catch (e) { setErr(e.message || "Couldn't disable."); }
  }

  async function create() {
    setErr(""); setMsg("");
    if (!draft.name.trim()) { setErr("Plan name is required."); return; }
    const price = parseInt(draft.price_inr, 10); const credits = parseInt(draft.credits, 10);
    if (!price || price < 1) { setErr("Price (₹) must be at least 1."); return; }
    if (!credits || credits < 1) { setErr("Credits must be at least 1."); return; }
    try {
      await backend.adminCreatePlan({
        name: draft.name.trim(), price_inr: price, credits, active: true,
        order: parseInt(draft.order, 10) || 99, description: draft.description.slice(0, 200),
      });
      setDraft({ name: "", price_inr: "", credits: "", active: true, order: 99, description: "" });
      setMsg("Plan created.");
      await load();
    } catch (e) { setErr(e.message || "Couldn't create plan."); }
  }

  return (
    <div data-testid="admin-plans-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Create a new plan</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 100px 80px 1fr", gap: 8 }}>
          <input data-testid="plan-new-name" className="input" placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <input data-testid="plan-new-price" className="input" type="number" min="1" placeholder="₹ Price" value={draft.price_inr} onChange={(e) => setDraft({ ...draft, price_inr: e.target.value })} />
          <input data-testid="plan-new-credits" className="input" type="number" min="1" placeholder="Credits" value={draft.credits} onChange={(e) => setDraft({ ...draft, credits: e.target.value })} />
          <input className="input" type="number" placeholder="Order" value={draft.order} onChange={(e) => setDraft({ ...draft, order: e.target.value })} />
          <input className="input" placeholder="Short description (optional)" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        </div>
        <button data-testid="plan-new-save" className="btn btn--primary" style={{ marginTop: 10 }} onClick={create}><Plus size={14} /> Add plan</button>
      </div>

      {msg && <p style={{ color: "var(--correct)", fontSize: 13 }} data-testid="plans-saved">{msg}</p>}
      {err && <p style={{ color: "var(--wrong)", fontSize: 13 }} data-testid="plans-err">{err}</p>}

      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>All plans ({plans.length})</div>
        {plans.map((p) => (
          <div key={p.id} className="req-row" data-testid={`plan-row-${p.id}`} style={{ alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
            <input className="input" style={{ width: 140 }} value={p.name} onChange={(e) => patch(p.id, "name", e.target.value)} />
            <input className="input" type="number" style={{ width: 90 }} value={p.price_inr} onChange={(e) => patch(p.id, "price_inr", parseInt(e.target.value, 10) || 0)} />
            <input className="input" type="number" style={{ width: 90 }} value={p.credits} onChange={(e) => patch(p.id, "credits", parseInt(e.target.value, 10) || 0)} />
            <input className="input" type="number" style={{ width: 60 }} title="Display order" value={p.order} onChange={(e) => patch(p.id, "order", parseInt(e.target.value, 10) || 0)} />
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12.5 }}>
              <input type="checkbox" checked={!!p.active} onChange={(e) => patch(p.id, "active", e.target.checked)} /> Active
            </label>
            <input className="input" style={{ flex: 1, minWidth: 160 }} placeholder="Description" value={p.description || ""} onChange={(e) => patch(p.id, "description", e.target.value)} />
            <button data-testid={`plan-save-${p.id}`} className="btn btn--primary" disabled={busy} style={{ padding: "6px 10px" }} onClick={() => saveRow(p)}><Check size={14} /></button>
            <button data-testid={`plan-disable-${p.id}`} className="btn btn--icon" title="Disable plan" style={{ color: "var(--wrong)" }} onClick={() => disable(p)}><X size={14} /></button>
          </div>
        ))}
        {plans.length === 0 && <p className="qz-muted" style={{ fontSize: 13 }}>No plans yet.</p>}
      </div>
    </div>
  );
}

function ExamPatternLibraryPanel() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [tags, setTags] = useState("");
  const [note, setNote] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [expanded, setExpanded] = useState({});
  const [analyzingId, setAnalyzingId] = useState("");
  const fileRef = useRef(null);

  async function refresh() {
    setErr("");
    try {
      const d = await backend.adminListPatterns("NEET_UG", filterStatus || undefined);
      setItems(d.items || []);
    } catch (e) { setErr(e.message || "Couldn't load patterns."); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterStatus]);

  async function onPick(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true); setErr("");
    try {
      await backend.adminUploadPattern({ file: f, exam_code: "NEET_UG", tags, note });
      setTags(""); setNote("");
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (e2) { setErr(e2.message || "Upload failed."); }
    finally { setBusy(false); }
  }
  async function toggleStatus(it) {
    setErr("");
    try {
      await backend.adminUpdatePattern(it.id, { status: it.status === "ACTIVE" ? "DISABLED" : "ACTIVE" });
      await refresh();
    } catch (e) { setErr(e.message || "Update failed."); }
  }
  async function updateTags(it) {
    const next = prompt("Comma-separated tags (max 10):", (it.tags || []).join(", "));
    if (next === null) return;
    try {
      await backend.adminUpdatePattern(it.id, { tags: next.split(",").map(t => t.trim()).filter(Boolean) });
      await refresh();
    } catch (e) { setErr(e.message || "Update failed."); }
  }
  async function del(it) {
    if (!window.confirm(`Delete pattern "${it.fileName}"? This cannot be undone.`)) return;
    try { await backend.adminDeletePattern(it.id); await refresh(); }
    catch (e) { setErr(e.message || "Delete failed."); }
  }
  async function analyze(it) {
    if (!window.confirm("Trigger OpenAI Vision analysis? This will use AI credits.")) return;
    setAnalyzingId(it.id); setErr("");
    try { await backend.adminAnalyzePattern(it.id); await refresh(); }
    catch (e) { setErr(e.message || "Analysis failed."); }
    finally { setAnalyzingId(""); }
  }

  return (
    <div data-testid="admin-patterns-panel" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="banner" style={{ marginBottom: 0 }}>
        <b>NEET Pattern Library</b> — reference materials only (NOT a question bank). Upload sample papers / screenshots; click <b>Analyze</b> to extract reusable patterns. Quiz generation uses stored patterns, never re-analyzes uploads.
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Upload reference</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <input className="input" placeholder="Tags (comma-separated, e.g. PYQ-2024, Physics, AR)" value={tags} onChange={(e) => setTags(e.target.value)} data-testid="pattern-tags-input" />
          <input className="input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="pattern-note-input" />
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.pdf,image/png,image/jpeg,image/webp,application/pdf" onChange={onPick} disabled={busy} data-testid="pattern-file-input" />
          {busy && <span className="qz-muted" style={{ fontSize: 13 }}><Loader2 size={14} className="spin" /> Uploading…</span>}
          <span className="qz-muted" style={{ fontSize: 12 }}>PNG / JPG / JPEG / WEBP / PDF · up to 15 MB</span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span className="qz-muted" style={{ fontSize: 13 }}>Filter:</span>
        <div className="seg">
          <button className={!filterStatus ? "act" : ""} onClick={() => setFilterStatus("")}>All</button>
          <button className={filterStatus === "ACTIVE" ? "act" : ""} onClick={() => setFilterStatus("ACTIVE")}>Active</button>
          <button className={filterStatus === "DISABLED" ? "act" : ""} onClick={() => setFilterStatus("DISABLED")}>Disabled</button>
        </div>
        <button className="btn btn--ghost" style={{ padding: "6px 10px" }} onClick={refresh}><RotateCcw size={14} /> Refresh</button>
      </div>

      {err && <p style={{ color: "var(--wrong)", fontSize: 13 }}>{err}</p>}
      {!items && !err && <p className="qz-muted" style={{ fontSize: 13 }}>Loading…</p>}
      {items && items.length === 0 && <p className="qz-muted" style={{ fontSize: 13 }}>No patterns uploaded yet.</p>}

      {items && items.map((it) => (
        <div key={it.id} className="card" style={{ padding: 14 }} data-testid={`pattern-row-${it.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontWeight: 700 }}>{it.fileName}</div>
              <div className="qz-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                {it.fileType} · {Math.round((it.fileSize || 0) / 1024)} KB · {new Date(it.createdAt).toLocaleString()}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                <span className="tag" style={{ color: it.status === "ACTIVE" ? "var(--correct)" : "var(--muted)" }}>{it.status}</span>
                {(it.tags || []).map((t) => <span key={t} className="tag">{t}</span>)}
                {it.analyzedAt
                  ? <span className="tag" style={{ color: "var(--accent2)" }}>Analyzed · {new Date(it.analyzedAt).toLocaleDateString()}</span>
                  : <span className="tag" style={{ color: "var(--gold)" }}>Not analyzed</span>}
              </div>
              {it.note && <p className="qz-muted" style={{ fontSize: 13, marginTop: 8 }}>{it.note}</p>}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <a className="btn btn--ghost" style={{ padding: "6px 10px" }} href={backend.patternFileUrl(it.id)} target="_blank" rel="noreferrer">View</a>
              <button className="btn btn--ghost" style={{ padding: "6px 10px" }} onClick={() => updateTags(it)} data-testid={`pattern-tag-${it.id}`}>Tags</button>
              <button className="btn btn--ghost" style={{ padding: "6px 10px" }} onClick={() => toggleStatus(it)} data-testid={`pattern-toggle-${it.id}`}>{it.status === "ACTIVE" ? "Disable" : "Enable"}</button>
              <button className="btn" style={{ padding: "6px 10px" }} disabled={analyzingId === it.id} onClick={() => analyze(it)} data-testid={`pattern-analyze-${it.id}`}>
                {analyzingId === it.id ? <><Loader2 size={14} className="spin" /> Analyzing…</> : (it.analyzedAt ? "Reprocess" : "Analyze")}
              </button>
              <button className="btn btn--ghost" style={{ padding: "6px 10px", color: "var(--wrong)" }} onClick={() => del(it)} data-testid={`pattern-delete-${it.id}`}>Delete</button>
            </div>
          </div>
          {it.analysis && (
            <div style={{ marginTop: 10 }}>
              <button className="btn btn--ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => setExpanded((e) => ({ ...e, [it.id]: !e[it.id] }))}>
                {expanded[it.id] ? "Hide analysis ▲" : "Show analysis ▼"}
              </button>
              {expanded[it.id] && (
                <pre style={{ marginTop: 8, padding: 10, background: "rgba(0,0,0,0.2)", borderRadius: 8, fontSize: 12, maxHeight: 280, overflow: "auto" }}>{JSON.stringify(it.analysis, null, 2)}</pre>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PaymentsAdminPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState("");
  useEffect(() => {
    setErr("");
    backend.adminPayments(days).then(setData).catch((e) => setErr(e.message || "Couldn't load."));
  }, [days]);
  if (!data && !err) return <p className="qz-muted" style={{ fontSize: 13 }}>Loading…</p>;
  if (err) return <p style={{ color: "var(--wrong)", fontSize: 13 }}>{err}</p>;
  const t = data.totals || {};
  return (
    <div data-testid="admin-payments-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 800 }}>Payments overview</div>
        <select className="input" style={{ width: 130, padding: "6px 8px" }} value={days} onChange={(e) => setDays(parseInt(e.target.value, 10) || 30)}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10 }}>
        <div className="glass" style={{ padding: 10 }}><div className="qz-muted" style={{ fontSize: 12 }}>Revenue</div><b style={{ fontSize: 18 }}>₹{t.revenue_inr || 0}</b></div>
        <div className="glass" style={{ padding: 10 }}><div className="qz-muted" style={{ fontSize: 12 }}>Successful</div><b style={{ fontSize: 18 }}>{t.successful || 0}</b></div>
        <div className="glass" style={{ padding: 10 }}><div className="qz-muted" style={{ fontSize: 12 }}>Failed</div><b style={{ fontSize: 18 }}>{t.failed || 0}</b></div>
        <div className="glass" style={{ padding: 10 }}><div className="qz-muted" style={{ fontSize: 12 }}>Pending</div><b style={{ fontSize: 18 }}>{t.pending || 0}</b></div>
        <div className="glass" style={{ padding: 10 }}><div className="qz-muted" style={{ fontSize: 12 }}>Credits sold</div><b style={{ fontSize: 18 }}>{(t.credits_sold || 0).toLocaleString()}</b></div>
      </div>

      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 6 }}>By plan</div>
        {(data.by_plan || []).map((r) => (
          <div key={r.plan_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px dashed var(--border)" }}>
            <span><b>{r.plan_name}</b> <span className="qz-muted">· {r.count} purchases</span></span>
            <span>₹{r.revenue} · {(r.credits || 0).toLocaleString()} credits</span>
          </div>
        ))}
        {(data.by_plan || []).length === 0 && <div className="qz-muted" style={{ fontSize: 12.5 }}>No successful purchases yet.</div>}
      </div>

      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Recent payments</div>
        {(data.payments || []).slice(0, 40).map((p) => (
          <div key={p.id} className="req-row" data-testid={`adm-pay-${p.id}`} style={{ alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>{p.user_email}</div>
              <div className="qz-muted" style={{ fontSize: 12 }}>{p.plan_name} · ₹{p.amount_inr} · {p.credits} credits</div>
              <div className="qz-muted" style={{ fontSize: 11.5, wordBreak: "break-all" }}>
                {new Date(p.created_at).toLocaleString()} · order {p.razorpay_order_id}
              </div>
            </div>
            <span className="tag" style={{ color: p.status === "success" ? "var(--correct)" : (p.status === "failed" ? "var(--wrong)" : "var(--gold)") }}>{p.status}</span>
          </div>
        ))}
        {(data.payments || []).length === 0 && <div className="qz-muted" style={{ fontSize: 12.5 }}>No payments in this window.</div>}
      </div>
    </div>
  );
}

// ─── Promotions Admin Panel (Feb 2026, additive only) ─────────────────────
// Edits the single document in MongoDB collection `promotions`. Everything
// the popup renders is driven from these fields; nothing here is hardcoded.
function PromotionsAdminPanel() {
  const [promo, setPromoCfg] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);

  async function load() {
    setErr("");
    try {
      const [d, pl] = await Promise.all([
        backend.adminGetPromotion(),
        backend.adminListPlans().catch(() => ({ plans: [] })),
      ]);
      setPromoCfg(d.promotion);
      setPlans((pl && pl.plans) || []);
    } catch (e) { setErr(e.message || "Could not load promotion."); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!promo) return;
    setBusy(true); setMsg(""); setErr("");
    try {
      const body = {
        enabled: !!promo.enabled,
        title: promo.title || "",
        subtitle: promo.subtitle || "",
        description: promo.description || "",
        button_text: promo.button_text || "",
        success_message: promo.success_message || "",
        bonus_percentage: Number(promo.bonus_percentage) || 0,
        eligible_plan_ids: promo.eligible_plan_ids || [],
        start_date: promo.start_date || null,
        end_date: promo.end_date || null,
        start_time: promo.start_time || null,
        end_time: promo.end_time || null,
      };
      const r = await backend.adminPutPromotion(body);
      setPromoCfg(r.promotion);
      setMsg("Promotion saved. New settings apply immediately.");
    } catch (e) { setErr(e.message || "Couldn't save."); }
    finally { setBusy(false); }
  }

  async function runPreview() {
    setErr("");
    try {
      const p = await backend.adminPromotionPreview({
        enabled: true,
        title: promo.title, subtitle: promo.subtitle,
        description: promo.description,
        button_text: promo.button_text,
        success_message: promo.success_message,
        bonus_percentage: Number(promo.bonus_percentage) || 0,
      });
      setPreview(p);
    } catch (e) { setErr(e.message || "Preview failed."); }
  }

  function togglePlan(pid) {
    setPromoCfg((p) => {
      const set = new Set((p.eligible_plan_ids || []).map(String));
      if (set.has(String(pid))) set.delete(String(pid)); else set.add(String(pid));
      return { ...p, eligible_plan_ids: [...set] };
    });
  }

  if (!promo) return <div className="qz-muted" style={{ padding: 16 }}>Loading promotion config…</div>;

  return (
    <div data-testid="promotions-admin-panel" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {err && <p style={{ color: "var(--wrong)" }} data-testid="promo-admin-err">{err}</p>}
      {msg && <p style={{ color: "var(--correct)" }} data-testid="promo-admin-msg">{msg}</p>}

      {/* General */}
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>General</div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}>
          <input
            type="checkbox" data-testid="promo-enabled"
            checked={!!promo.enabled}
            onChange={(e) => setPromoCfg((p) => ({ ...p, enabled: e.target.checked }))}
          />
          <span>Promotion {promo.enabled ? "Enabled" : "Disabled"}</span>
          <span className="tag" style={{ color: promo.active ? "var(--correct)" : "var(--muted)" }}>
            {promo.active ? "ACTIVE NOW" : "INACTIVE"}
          </span>
        </label>
      </div>

      {/* Offer copy */}
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Offer content</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 10 }}>
          <label className="adm-f">Title
            <input data-testid="promo-title" className="input" value={promo.title || ""} onChange={(e) => setPromoCfg((p) => ({ ...p, title: e.target.value }))} />
          </label>
          <label className="adm-f">Subtitle
            <input data-testid="promo-subtitle" className="input" value={promo.subtitle || ""} onChange={(e) => setPromoCfg((p) => ({ ...p, subtitle: e.target.value }))} />
          </label>
          <label className="adm-f">Button text
            <input data-testid="promo-button-text" className="input" value={promo.button_text || ""} onChange={(e) => setPromoCfg((p) => ({ ...p, button_text: e.target.value }))} />
          </label>
        </div>
        <label className="adm-f" style={{ marginTop: 8 }}>Description
          <textarea data-testid="promo-description" className="input" rows={2} value={promo.description || ""} onChange={(e) => setPromoCfg((p) => ({ ...p, description: e.target.value }))} />
        </label>
        <label className="adm-f" style={{ marginTop: 8 }}>Success message
          <textarea data-testid="promo-success-message" className="input" rows={2} value={promo.success_message || ""} onChange={(e) => setPromoCfg((p) => ({ ...p, success_message: e.target.value }))} />
        </label>
      </div>

      {/* Bonus */}
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Bonus</div>
        <label className="adm-f" style={{ maxWidth: 220 }}>Bonus percentage (0–500)
          <input data-testid="promo-bonus-pct" className="input" type="number" min="0" max="500" step="1"
            value={promo.bonus_percentage ?? 0}
            onChange={(e) => setPromoCfg((p) => ({ ...p, bonus_percentage: Math.max(0, Math.min(500, parseInt(e.target.value, 10) || 0)) }))}
          />
        </label>
        <div className="qz-muted" style={{ fontSize: 12, marginTop: 4 }}>
          Example: a {promo.bonus_percentage || 0}% bonus on a 1,000-credit pack credits an extra <b>{Math.floor((1000 * (Number(promo.bonus_percentage) || 0)) / 100)}</b> credits.
        </div>
      </div>

      {/* Active dates (IST) */}
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Active dates (IST)</div>
        <div className="qz-muted" style={{ fontSize: 12, marginBottom: 8 }}>
          All times are evaluated in <b>Asia/Kolkata</b> regardless of your browser
          timezone. Leave dates blank to remove that boundary.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 }}>
          <label className="adm-f">Start date
            <input data-testid="promo-start-date" className="input" type="date" value={promo.start_date || ""} onChange={(e) => setPromoCfg((p) => ({ ...p, start_date: e.target.value || null }))} />
          </label>
          <label className="adm-f">Start time (optional)
            <input data-testid="promo-start-time" className="input" type="time" value={promo.start_time || ""} onChange={(e) => setPromoCfg((p) => ({ ...p, start_time: e.target.value || null }))} />
          </label>
          <label className="adm-f">End date
            <input data-testid="promo-end-date" className="input" type="date" value={promo.end_date || ""} onChange={(e) => setPromoCfg((p) => ({ ...p, end_date: e.target.value || null }))} />
          </label>
          <label className="adm-f">End time (optional)
            <input data-testid="promo-end-time" className="input" type="time" value={promo.end_time || ""} onChange={(e) => setPromoCfg((p) => ({ ...p, end_time: e.target.value || null }))} />
          </label>
        </div>
      </div>

      {/* Eligible packages */}
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Eligible packages</div>
        <div className="qz-muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Leave all unchecked to apply the bonus to <b>every</b> plan automatically.
        </div>
        {plans.length === 0 ? (
          <p className="qz-muted">No plans configured yet. Add plans under the Plans tab first.</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 8 }}>
            {plans.map((pl) => {
              const checked = (promo.eligible_plan_ids || []).map(String).includes(String(pl.id));
              return (
                <label key={pl.id} className="adm-f" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, padding: 8, border: "1px solid var(--border)", borderRadius: 8 }}>
                  <input
                    data-testid={`promo-plan-${pl.id}`}
                    type="checkbox" checked={checked} onChange={() => togglePlan(pl.id)}
                  />
                  <span style={{ fontWeight: 700 }}>{pl.name}</span>
                  <span className="qz-muted" style={{ fontSize: 12, marginLeft: "auto" }}>₹{pl.price_inr} · {pl.credits} cr</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button data-testid="promo-save-btn" className="btn btn--primary" disabled={busy} onClick={save}>
          {busy ? <Loader2 size={16} className="spin" /> : <Wand2 size={16} />} Save
        </button>
        <button data-testid="promo-preview-btn" className="btn" onClick={runPreview}>
          <Sparkles size={16} /> Preview popup
        </button>
        <span className="qz-muted" style={{ fontSize: 12, marginLeft: "auto" }}>
          Last updated: {promo.updated_at || "—"}
        </span>
      </div>

      {preview && (
        <PromoModal promo={{ ...preview, active: true }} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}


function PricingAdminPanel() {
  const [cfg, setCfg] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  async function load() {
    setErr("");
    try {
      const [p, a] = await Promise.all([
        backend.adminGetPricing(),
        backend.adminCostAnalytics(days).catch(() => ({})),
      ]);
      setCfg(p.pricing);
      setAnalytics(a);
    } catch (e) { setErr(e.message || "Couldn't load pricing."); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    backend.adminCostAnalytics(days).then(setAnalytics).catch(() => {});
  }, [days]);

  async function save() {
    if (!cfg) return;
    setBusy(true); setMsg(""); setErr("");
    try {
      const d = await backend.adminPutPricing({
        signup_bonus: parseInt(cfg.signup_bonus, 10) || 0,
        min_charge: parseInt(cfg.min_charge, 10) || 0,
        max_charge: parseInt(cfg.max_charge, 10) || 0,
        tier_costs: cfg.tier_costs,
        model_tiers: cfg.model_tiers,
        character_tiers: cfg.character_tiers,
        brave_search_cost: parseInt(cfg.brave_search_cost, 10) || 0,
        validation_per_question: parseFloat(cfg.validation_per_question) || 0,
        default_provider: cfg.default_provider,
        default_model: cfg.default_model,
      });
      setCfg(d.pricing);
      setMsg("Pricing saved. New rates apply to the next generation.");
    } catch (e) { setErr(e.message || "Couldn't save."); }
    finally { setBusy(false); }
  }

  function setTier(t, v) { setCfg((c) => ({ ...c, tier_costs: { ...c.tier_costs, [t]: parseInt(v, 10) || 0 } })); }
  function setCharTier(i, field, v) {
    setCfg((c) => {
      const tiers = [...((c.character_tiers && c.character_tiers.tiers) || [])];
      tiers[i] = { ...tiers[i], [field]: parseInt(v, 10) || 0 };
      return { ...c, character_tiers: { ...(c.character_tiers || {}), tiers } };
    });
  }
  function setCharOver(field, v) {
    setCfg((c) => ({ ...c, character_tiers: { ...(c.character_tiers || {}), [field]: parseInt(v, 10) || 0 } }));
  }
  function setModelTier(provider, model, tier) {
    setCfg((c) => ({ ...c, model_tiers: { ...c.model_tiers, [provider]: { ...(c.model_tiers[provider] || {}), [model]: tier } } }));
  }
  function addModel(provider) {
    const name = (window.prompt(`New ${provider} model id (e.g. gpt-5.2):`) || "").trim();
    if (!name) return;
    const tier = window.prompt("Tier (Economy / Standard / Advanced / Premium):", "Economy") || "Economy";
    setCfg((c) => ({ ...c, model_tiers: { ...c.model_tiers, [provider]: { ...(c.model_tiers[provider] || {}), [name]: tier } } }));
  }
  function removeModel(provider, model) {
    setCfg((c) => {
      const next = { ...(c.model_tiers[provider] || {}) };
      delete next[model];
      return { ...c, model_tiers: { ...c.model_tiers, [provider]: next } };
    });
  }
  function addProvider() {
    const p = (window.prompt("New provider name (e.g. Mistral, Grok, OpenRouter):") || "").trim();
    if (!p) return;
    setCfg((c) => ({ ...c, model_tiers: { ...c.model_tiers, [p]: c.model_tiers[p] || {} } }));
  }

  if (!cfg) return <p className="qz-muted" style={{ fontSize: 13 }}>{err || "Loading pricing config…"}</p>;
  const tierOptions = Object.keys(cfg.tier_costs || { Economy: 1 });

  return (
    <div data-testid="admin-pricing-panel" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Tier costs */}
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Tier costs (credits per question)</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
          {Object.entries(cfg.tier_costs).map(([name, val]) => (
            <label key={name} className="adm-f">{name}
              <input data-testid={`tier-cost-${name}`} className="input" type="number" min="0" value={val} onChange={(e) => setTier(name, e.target.value)} />
            </label>
          ))}
        </div>
      </div>

      {/* Document processing credits (character-based tiers) */}
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Document processing credits (character-based)</div>
        <div className="qz-muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Charge based on the total extracted character count of the user&apos;s
          uploads (PDF / DOC / TXT / OCR&apos;d images, combined). The first row
          whose <i>max chars</i> covers the count is billed. Anything beyond
          the largest tier uses the over-tier step row.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 8, alignItems: "center", marginBottom: 6 }}>
          <div className="qz-muted" style={{ fontSize: 12 }}>Tier</div>
          <div className="qz-muted" style={{ fontSize: 12 }}>Max chars (inclusive)</div>
          <div className="qz-muted" style={{ fontSize: 12 }}>Credits</div>
        </div>
        {((cfg.character_tiers && cfg.character_tiers.tiers) || []).map((t, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Tier {i + 1}</div>
            <input data-testid={`char-tier-${i}-max`} className="input" type="number" min="1" value={t.max_chars} onChange={(e) => setCharTier(i, "max_chars", e.target.value)} />
            <input data-testid={`char-tier-${i}-credits`} className="input" type="number" min="0" value={t.credits} onChange={(e) => setCharTier(i, "credits", e.target.value)} />
          </div>
        ))}
        <div style={{ height: 1, background: "var(--border)", margin: "10px 0" }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
          <label className="adm-f">Over-tier base chars
            <input className="input" type="number" min="1" value={(cfg.character_tiers || {}).over_max_chars || 100000} onChange={(e) => setCharOver("over_max_chars", e.target.value)} />
          </label>
          <label className="adm-f">Over-tier base credits
            <input className="input" type="number" min="0" value={(cfg.character_tiers || {}).over_max_credits || 80} onChange={(e) => setCharOver("over_max_credits", e.target.value)} />
          </label>
          <label className="adm-f">Step chars (per block above base)
            <input className="input" type="number" min="1" value={(cfg.character_tiers || {}).over_step_chars || 50000} onChange={(e) => setCharOver("over_step_chars", e.target.value)} />
          </label>
          <label className="adm-f">Step credits (per block)
            <input className="input" type="number" min="0" value={(cfg.character_tiers || {}).over_step_credits || 40} onChange={(e) => setCharOver("over_step_credits", e.target.value)} />
          </label>
        </div>
      </div>

      {/* Service usage charges */}
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Service usage charges</div>
        <div className="qz-muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Debited only when the service actually ran for the request.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
          <label className="adm-f">Brave Search (per call)
            <input data-testid="pricing-brave-cost" className="input" type="number" min="0" value={cfg.brave_search_cost ?? 1} onChange={(e) => setCfg((c) => ({ ...c, brave_search_cost: e.target.value }))} />
          </label>
          <label className="adm-f">Validation (per question)
            <input data-testid="pricing-validation-cost" className="input" type="number" min="0" step="0.1" value={cfg.validation_per_question ?? 0.5} onChange={(e) => setCfg((c) => ({ ...c, validation_per_question: e.target.value }))} />
          </label>
        </div>
      </div>

      {/* Business rules */}
      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Business rules</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
          <label className="adm-f">Signup bonus (one-time)<input data-testid="pricing-signup-bonus" className="input" type="number" min="0" value={cfg.signup_bonus} onChange={(e) => setCfg((c) => ({ ...c, signup_bonus: e.target.value }))} /></label>
          <label className="adm-f">Min charge per call<input className="input" type="number" min="0" value={cfg.min_charge} onChange={(e) => setCfg((c) => ({ ...c, min_charge: e.target.value }))} /></label>
          <label className="adm-f">Max charge per call (0 = unlimited)<input className="input" type="number" min="0" value={cfg.max_charge} onChange={(e) => setCfg((c) => ({ ...c, max_charge: e.target.value }))} /></label>
          <label className="adm-f">Default provider<input className="input" value={cfg.default_provider} onChange={(e) => setCfg((c) => ({ ...c, default_provider: e.target.value }))} /></label>
          <label className="adm-f">Default model<input className="input" value={cfg.default_model} onChange={(e) => setCfg((c) => ({ ...c, default_model: e.target.value }))} /></label>
        </div>
      </div>

      {/* Model → tier mapping */}
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontWeight: 800 }}>Model tiers (per provider)</div>
          <button className="btn" style={{ padding: "6px 10px", fontSize: 12 }} onClick={addProvider} data-testid="pricing-add-provider"><Plus size={14} /> Provider</button>
        </div>
        {Object.entries(cfg.model_tiers).map(([provider, models]) => (
          <div key={provider} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <b style={{ fontSize: 13.5 }}>{provider}</b>
              <button className="btn" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => addModel(provider)} data-testid={`pricing-add-model-${provider}`}><Plus size={12} /> model</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {Object.keys(models || {}).length === 0 && <div className="qz-muted" style={{ fontSize: 12 }}>No models yet — click "+ model".</div>}
              {Object.entries(models || {}).map(([m, t]) => (
                <div key={m} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <code style={{ flex: 1, fontSize: 12.5, padding: "6px 8px", background: "var(--surface)", borderRadius: 6, border: "1px solid var(--border)" }}>{m}</code>
                  <select className="input" style={{ width: 130, padding: "6px 8px" }} value={t} onChange={(e) => setModelTier(provider, m, e.target.value)}>
                    {tierOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <button className="btn btn--icon" style={{ width: 30, height: 30 }} title="Remove" onClick={() => removeModel(provider, m)}><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {msg && <p style={{ color: "var(--correct)", fontSize: 13 }} data-testid="pricing-saved">{msg}</p>}
      {err && <p style={{ color: "var(--wrong)", fontSize: 13 }} data-testid="pricing-err">{err}</p>}
      <button className="btn btn--primary btn--block" disabled={busy} onClick={save} data-testid="pricing-save-btn">{busy ? <Loader2 size={16} className="spin" /> : <><Check size={16} /> Save pricing</>}</button>

      {/* Analytics */}
      <div className="card" style={{ marginTop: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontWeight: 800 }}>Cost analytics</div>
          <select className="input" style={{ width: 130, padding: "6px 8px" }} value={days} onChange={(e) => setDays(parseInt(e.target.value, 10) || 30)}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
        {!analytics && <p className="qz-muted" style={{ fontSize: 13 }}>Loading…</p>}
        {analytics && analytics.totals && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10, marginBottom: 10 }}>
              <div className="glass" style={{ padding: 10 }}><div className="qz-muted" style={{ fontSize: 12 }}>Credits used</div><b style={{ fontSize: 18 }}>{analytics.totals.credits || 0}</b></div>
              <div className="glass" style={{ padding: 10 }}><div className="qz-muted" style={{ fontSize: 12 }}>Questions generated</div><b style={{ fontSize: 18 }}>{analytics.totals.questions || 0}</b></div>
              <div className="glass" style={{ padding: 10 }}><div className="qz-muted" style={{ fontSize: 12 }}>AI calls</div><b style={{ fontSize: 18 }}>{analytics.totals.calls || 0}</b></div>
              <div className="glass" style={{ padding: 10 }}><div className="qz-muted" style={{ fontSize: 12 }}>File credits</div><b style={{ fontSize: 18 }}>{analytics.totals.file_credits || 0}</b></div>
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>By model</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {(analytics.by_model || []).map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "4px 0", borderBottom: "1px dashed var(--border)" }}>
                  <span><b>{r.model || "?"}</b> <span className="qz-muted">· {r.provider || "?"} · {r.feature || "quiz"}</span></span>
                  <span><b>{r.credits}</b> credits · {r.calls} calls</span>
                </div>
              ))}
              {(analytics.by_model || []).length === 0 && <div className="qz-muted" style={{ fontSize: 12 }}>No usage yet.</div>}
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, margin: "10px 0 6px" }}>Top spenders</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {(analytics.top_users || []).map((u, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "4px 0", borderBottom: "1px dashed var(--border)" }}>
                  <span style={{ wordBreak: "break-all" }}>{u.email || "anonymous"}</span>
                  <span><b>{u.credits}</b> credits · {u.calls} calls</span>
                </div>
              ))}
              {(analytics.top_users || []).length === 0 && <div className="qz-muted" style={{ fontSize: 12 }}>No spend recorded.</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function fileThumb(file) {
  return new Promise((resolve) => {
    if (!/^image\//i.test(file.type || "")) { resolve(null); return; }
    const r = new FileReader();
    r.onerror = () => resolve(null);
    r.onload = () => resolve(String(r.result || ""));
    r.readAsDataURL(file);
  });
}

function UpscIntelligencePanel() {
  const [metrics, setMetrics] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [patternsAgg, setPatternsAgg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [hint, setHint] = useState("pyq");
  const [processingId, setProcessingId] = useState("");
  const fileRef = useRef(null);

  async function refresh() {
    setErr("");
    try {
      const [m, u, p] = await Promise.all([
        backend.upscAdminMetrics(),
        backend.upscAdminListUploads(),
        backend.upscAdminListPatterns(),
      ]);
      setMetrics(m);
      setUploads(u.uploads || []);
      setPatternsAgg(p.aggregate || null);
    } catch (e) {
      setErr(e.message || "Couldn't load UPSC data.");
    }
  }
  useEffect(() => { refresh(); }, []);

  async function onPick(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true); setErr("");
    try {
      await backend.upscAdminUpload({ files, source_hint: hint });
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (e2) {
      setErr(e2.message || "Upload failed.");
    } finally { setBusy(false); }
  }

  async function processOne(id) {
    setProcessingId(id); setErr("");
    try {
      const r = await backend.upscAdminProcess(id);
      if (r.errors && r.errors.length) setErr(`Processed with warnings: ${r.errors.join("; ")}`);
      await refresh();
    } catch (e) { setErr(e.message || "Processing failed."); }
    finally { setProcessingId(""); }
  }

  async function processAll() {
    setBusy(true); setErr("");
    try {
      await backend.upscAdminProcessAll();
      await refresh();
    } catch (e) { setErr(e.message || "Bulk processing failed."); }
    finally { setBusy(false); }
  }

  async function deleteOne(id) {
    if (!window.confirm("Delete this upload and all derived patterns?")) return;
    try {
      await backend.upscAdminDelete(id);
      await refresh();
    } catch (e) { setErr(e.message || "Delete failed."); }
  }

  const SOURCES = [
    { v: "pyq", l: "UPSC PYQ" },
    { v: "vision_ias", l: "Vision IAS" },
    { v: "forum_ias", l: "ForumIAS" },
    { v: "insights", l: "Insights IAS" },
    { v: "vajiram", l: "Vajiram" },
    { v: "ncert", l: "NCERT" },
    { v: "pib", l: "PIB" },
    { v: "govt_report", l: "Govt Report" },
    { v: "mock_test", l: "Mock Test" },
    { v: "other", l: "Other" },
  ];

  return (
    <div data-testid="upsc-intel-panel">
      <div style={{ fontWeight: 800, marginBottom: 4, fontSize: 16 }}>UPSC Intelligence Repository</div>
      <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 14 }}>
        Upload authentic UPSC reference material (PYQs, Vision IAS, ForumIAS, NCERT, PIB, mock tests).
        The system extracts text via OCR, classifies subject/topic, embeds the content, and learns
        question STYLE only — original question text is never reused.
      </p>
      {err && <p style={{ color: "var(--wrong)", fontSize: 13 }} data-testid="upsc-err">{err}</p>}

      {/* Metrics */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="qz-eyebrow" style={{ marginBottom: 8 }}>System Metrics</div>
        {metrics ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
            <Stat label="Uploads" value={metrics.uploads_total} testid="upsc-stat-uploads" />
            <Stat label="Processed" value={metrics.uploads_processed} testid="upsc-stat-processed" />
            <Stat label="Failed" value={metrics.uploads_failed} testid="upsc-stat-failed" />
            <Stat label="Patterns" value={metrics.patterns} testid="upsc-stat-patterns" />
            <Stat label="Extracted Docs" value={metrics.extracted_docs} testid="upsc-stat-extracted" />
            <Stat label="Bank Questions" value={metrics.bank_questions} testid="upsc-stat-bank" />
            <Stat label="User-History Rows" value={metrics.user_history_entries} testid="upsc-stat-history" />
          </div>
        ) : <p className="qz-muted" style={{ fontSize: 13 }}>Loading…</p>}
      </div>

      {/* Upload */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="qz-eyebrow" style={{ marginBottom: 8 }}>Upload Reference Material</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <label className="qz-muted" style={{ fontSize: 12.5 }}>Source:</label>
          <select data-testid="upsc-source-select" className="input" style={{ maxWidth: 180 }} value={hint} onChange={(e) => setHint(e.target.value)}>
            {SOURCES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <input
            ref={fileRef}
            data-testid="upsc-file-input"
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
            onChange={onPick}
            disabled={busy}
            style={{ flex: 1, minWidth: 200 }}
          />
          <button data-testid="upsc-process-all-btn" className="btn" onClick={processAll} disabled={busy}>
            {busy ? "Working…" : "Process All Pending"}
          </button>
        </div>
        <p className="qz-muted" style={{ fontSize: 12, marginTop: 8 }}>
          Supported: PDF, JPG, PNG, WebP. Max 25&nbsp;MB per file.
        </p>
      </div>

      {/* Uploads list */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="qz-eyebrow" style={{ marginBottom: 8 }}>Uploads ({uploads.length})</div>
        {uploads.length === 0 ? (
          <p className="qz-muted" style={{ fontSize: 13 }}>No uploads yet.</p>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {uploads.map((u) => (
              <div key={u.id} data-testid={`upsc-upload-row-${u.id}`} className="req-row" style={{ alignItems: "center" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.filename}
                    <span className="tag" style={{ marginLeft: 6 }}>{u.source_hint || "other"}</span>
                    <span className="tag" style={{ marginLeft: 4 }}>{Math.round((u.size || 0) / 1024)} KB</span>
                  </div>
                  <div className="qz-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                    <span className={`req-badge ${u.ocr_status === "done" ? "req-approved" : u.ocr_status === "failed" ? "req-rejected" : ""}`}>{u.ocr_status}</span>
                    {u.classification && <span style={{ marginLeft: 6 }}>{(u.classification.subject || "—")} · {(u.classification.difficulty || "—")}{u.classification.year ? ` · ${u.classification.year}` : ""}</span>}
                    {u.pattern_extracted && <span className="tag" style={{ marginLeft: 6, color: "var(--accent2)" }}>patterns</span>}
                    <span style={{ marginLeft: 6 }}>{new Date(u.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    data-testid={`upsc-process-btn-${u.id}`}
                    className="btn btn--xs"
                    disabled={processingId === u.id}
                    onClick={() => processOne(u.id)}
                  >
                    {processingId === u.id ? "…" : (u.ocr_status === "done" ? "Re-process" : "Process")}
                  </button>
                  <button
                    data-testid={`upsc-delete-btn-${u.id}`}
                    className="btn btn--xs"
                    style={{ color: "var(--wrong)" }}
                    onClick={() => deleteOne(u.id)}
                  >Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Aggregated Patterns */}
      <div className="card">
        <div className="qz-eyebrow" style={{ marginBottom: 8 }}>Aggregated Style Patterns</div>
        {!patternsAgg || patternsAgg.samples === 0 ? (
          <p className="qz-muted" style={{ fontSize: 13 }}>No patterns extracted yet. Upload UPSC material to start building the pattern library.</p>
        ) : (
          <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
            <div><b>Samples analysed:</b> {patternsAgg.samples}</div>
            {patternsAgg.questionStructures?.length > 0 && <div><b>Structures:</b> {patternsAgg.questionStructures.join(" · ")}</div>}
            {patternsAgg.statementPatterns?.length > 0 && <div><b>Statement framing:</b> {patternsAgg.statementPatterns.join(" · ")}</div>}
            {patternsAgg.distractorStyles?.length > 0 && <div><b>Distractor styles:</b> {patternsAgg.distractorStyles.join(" · ")}</div>}
            {patternsAgg.eliminationLogic?.length > 0 && <div><b>Elimination logic:</b> {patternsAgg.eliminationLogic.join(" · ")}</div>}
            {patternsAgg.questionTypeMix && Object.keys(patternsAgg.questionTypeMix).length > 0 && (
              <div><b>Observed type mix:</b> {Object.entries(patternsAgg.questionTypeMix).map(([k, v]) => `${k}=${v}%`).join(" · ")}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="card" style={{ padding: "10px 12px", margin: 0 }} data-testid={testid}>
      <div className="qz-muted" style={{ fontSize: 11, marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 800, fontSize: 20 }}>{value ?? "—"}</div>
    </div>
  );
}


function FeedbackAdminPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true); setErr("");
    try { const d = await backend.adminListFeedback(); setItems(d.feedback || []); }
    catch (e) { setErr(e.message || "Couldn't load feedback."); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function remove(id) {
    if (!window.confirm("Delete this feedback entry?")) return;
    try { await backend.adminDeleteFeedback(id); setItems((rows) => rows.filter((r) => r.id !== id)); }
    catch (e) { setErr(e.message || "Couldn't delete."); }
  }

  const avg = items.length > 0 ? (items.reduce((s, r) => s + (Number(r.rating) || 0), 0) / items.length) : 0;

  return (
    <div className="card" data-testid="admin-feedback-panel">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontWeight: 800 }}>User feedback <span className="tag" style={{ marginLeft: 6 }}>{items.length}</span></div>
        {items.length > 0 && <div className="tag" style={{ color: "var(--gold)" }}><Star size={12} style={{ verticalAlign: -2 }} /> {avg.toFixed(1)} avg</div>}
      </div>
      <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>Submissions from the “Provide Feedback” modal in the footer. Anonymous submissions are allowed.</p>
      {loading && <p className="qz-muted" style={{ fontSize: 13 }}>Loading…</p>}
      {err && <p style={{ color: "var(--wrong)", fontSize: 13 }} data-testid="feedback-admin-err">{err}</p>}
      {!loading && items.length === 0 && !err && <p className="qz-muted" style={{ fontSize: 13 }}>No feedback yet.</p>}
      {items.map((f) => (
        <div key={f.id} className="req-row" data-testid={`feedback-row-${f.id}`} style={{ alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 2 }} aria-label={`${f.rating} of 5 stars`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={14} color={n <= f.rating ? "var(--gold)" : "var(--faint)"} fill={n <= f.rating ? "currentColor" : "none"} />
                ))}
              </div>
              <span className="tag">{f.rating}/5</span>
              <span className="qz-muted" style={{ fontSize: 12 }}>{f.user_email || "anonymous"}</span>
            </div>
            <p style={{ fontSize: 13.5, marginTop: 6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{f.message}</p>
            <div className="qz-muted" style={{ fontSize: 11.5, marginTop: 4 }}>{new Date(f.created_at).toLocaleString()}</div>
          </div>
          <button className="btn btn--icon" title="Delete" data-testid={`feedback-delete-${f.id}`} style={{ width: 34, height: 34, color: "var(--wrong)", flex: "none" }} onClick={() => remove(f.id)}><X size={16} /></button>
        </div>
      ))}
    </div>
  );
}

function QuestionPaperGenerator({ onClose }) {
  const [meta, setMeta] = useState({ title: "", subject: "", grade: "", chapter: "", examType: "Unit Test", totalQuestions: 15, duration: "60 minutes", instructions: "" });
  const [files, setFiles] = useState([]); // {id, file, kind:'pdf'|'image', text?, b64?, mime?, preview?}
  const [customPrompt, setCustomPrompt] = useState("");
  const [sections, setSections] = useState([
    { name: "Foundation", count: 5, marksPerQ: 2 },
    { name: "Proficient", count: 4, marksPerQ: 5 },
    { name: "Advanced", count: 2, marksPerQ: 10 },
  ]);
  const [autoBalance, setAutoBalance] = useState(false);
  const [includeAnswerKey, setIncludeAnswerKey] = useState(true);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1); // 1=configure 2=preview
  const [paper, setPaper] = useState(null); // {meta, sections:[{name, items:[{q,answer?,marks}]}]}
  const [err, setErr] = useState("");
  const [stage, setStage] = useState("");

  // Auto-calculated total marks: Σ (count × marksPerQ) across all sections
  const computedTotalMarks = sections.reduce(
    (sum, s) => sum + (parseInt(s.count, 10) || 0) * (parseInt(s.marksPerQ, 10) || 0),
    0,
  );
  const computedTotalQuestions = sections.reduce(
    (sum, s) => sum + (parseInt(s.count, 10) || 0),
    0,
  );

  async function onDrop(e) {
    e.preventDefault();
    const list = Array.from(e.dataTransfer?.files || []);
    await ingestFiles(list);
  }
  async function pickFiles(e) { await ingestFiles(Array.from(e.target.files || [])); e.target.value = ""; }

  async function ingestFiles(list) {
    setErr("");
    const fresh = [];
    for (const f of list) {
      const id = Math.random().toString(36).slice(2, 9);
      const isPdf = /pdf/i.test(f.type) || /\.pdf$/i.test(f.name);
      const isImg = /^image\/(png|jpe?g|gif|webp)$/i.test(f.type);
      if (!isPdf && !isImg) { setErr(`Unsupported file: ${f.name}. Use PDF or images (JPG/PNG/WEBP).`); continue; }
      if (f.size > 15 * 1024 * 1024) { setErr(`${f.name} is over 15 MB — please use a smaller file.`); continue; }
      const item = { id, file: f, name: f.name, kind: isPdf ? "pdf" : "image" };
      if (isImg) {
        item.preview = await fileThumb(f);
      }
      fresh.push(item);
    }
    setFiles((arr) => [...arr, ...fresh]);
  }

  function removeFile(id) { setFiles((arr) => arr.filter((f) => f.id !== id)); }
  function moveFile(id, dir) {
    setFiles((arr) => {
      const i = arr.findIndex((f) => f.id === id); if (i < 0) return arr;
      const j = i + dir; if (j < 0 || j >= arr.length) return arr;
      const c = [...arr]; [c[i], c[j]] = [c[j], c[i]]; return c;
    });
  }

  function patchSection(i, p) { setSections((s) => s.map((x, k) => k === i ? { ...x, ...p } : x)); }

  function validate() {
    if (!meta.title.trim()) return "Question paper title is required.";
    if (!meta.subject.trim()) return "Subject is required.";
    if (files.length === 0) return "Upload at least one file (PDF or image).";
    const totalQ = sections.reduce((s, x) => s + (parseInt(x.count, 10) || 0), 0);
    if (totalQ <= 0) return "Add at least one question to a section.";
    return "";
  }

  async function generate() {
    const v = validate(); if (v) { setErr(v); return; }
    setErr(""); setBusy(true); setStage("Uploading material…");
    try {
      // Inject the auto-calculated total marks/questions so the docx + preview stay in sync
      const enrichedMeta = { ...meta, totalMarks: computedTotalMarks, totalQuestions: computedTotalQuestions };
      const fd = new FormData();
      for (const f of files) fd.append("files", f.file, f.name);
      fd.append("meta", JSON.stringify(enrichedMeta));
      fd.append("sections", JSON.stringify(sections));
      fd.append("custom_prompt", customPrompt || "");
      fd.append("include_answer_key", includeAnswerKey ? "true" : "false");
      fd.append("auto_balance", autoBalance ? "true" : "false");

      setStage("Reading files & generating with AI…");
      const data = await backend.teacherGeneratePaper(fd);
      if (data && data.quota) _emitQuota(data.quota);
      const obj = data && data.paper;
      if (!obj || !Array.isArray(obj.sections)) throw new Error("Unexpected AI response. Please try again.");
      setPaper({ meta: enrichedMeta, sections: obj.sections, includeAnswerKey });
      setStep(2);
    } catch (e) {
      setErr(e.message || "Generation failed. Please try again.");
    } finally { setBusy(false); setStage(""); }
  }

  async function downloadDocx() {
    if (!paper) return;
    setErr("");
    try {
      const blob = await backend.teacherDownloadDocx({
        meta: paper.meta,
        sections: paper.sections,
        includeAnswerKey: !!paper.includeAnswerKey,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(paper.meta.title || "question_paper").replace(/[^a-z0-9]+/gi, "_")}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      setErr(e.message || "Couldn't build the Word file.");
    }
  }

  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820, maxHeight: "92vh", overflow: "auto" }} data-testid="qpg-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 className="qz-display" style={{ fontSize: 24 }}><Wand2 size={20} style={{ verticalAlign: -3, marginRight: 8, color: "#f48626" }} />Teachers Section – Build Question Paper</h3>
          <button className="btn btn--icon" data-testid="qpg-close" onClick={onClose}><X size={18} /></button>
        </div>
        <p className="qz-muted" style={{ fontSize: 13.5, marginBottom: 14 }}>Upload your chapter material to generate an exam-ready paper.</p>

        {step === 1 && (
          <div className="grid" style={{ gap: 18 }}>
            {/* Paper details */}
            <section className="card" style={{ padding: 16 }}>
              <h4 style={{ fontSize: 15, marginBottom: 10, fontWeight: 800 }}>1. Question Paper Details</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input data-testid="qpg-title" className="input" placeholder="Question Paper Title *" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} style={{ gridColumn: "span 2" }} />
                <input data-testid="qpg-subject" className="input" placeholder="Subject *" value={meta.subject} onChange={(e) => setMeta({ ...meta, subject: e.target.value })} />
                <input data-testid="qpg-grade" className="input" placeholder="Class / Grade (e.g. 9-B)" value={meta.grade} onChange={(e) => setMeta({ ...meta, grade: e.target.value })} />
                <input data-testid="qpg-chapter" className="input" placeholder="Chapter / Unit name" value={meta.chapter} onChange={(e) => setMeta({ ...meta, chapter: e.target.value })} />
                <select className="input" value={meta.examType} onChange={(e) => setMeta({ ...meta, examType: e.target.value })}>
                  {["Unit Test", "Midterm", "Final", "Practice Test", "Custom"].map((t) => <option key={t}>{t}</option>)}
                </select>
                <div data-testid="qpg-total-marks" className="input" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(126,232,250,.08)", borderColor: "rgba(126,232,250,.35)", cursor: "default" }} title="Calculated automatically from Section counts × marks per question">
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>Total Marks</span>
                  <span style={{ fontWeight: 800, color: "var(--accent2)" }}>{computedTotalMarks}</span>
                </div>
                <input className="input" placeholder="Duration (e.g. 90 minutes)" value={meta.duration} onChange={(e) => setMeta({ ...meta, duration: e.target.value })} />
              </div>
              <textarea className="input" style={{ width: "100%", height: 70, marginTop: 10 }} placeholder="Instructions to appear on the paper (optional)" value={meta.instructions} onChange={(e) => setMeta({ ...meta, instructions: e.target.value })} />
            </section>

            {/* Upload */}
            <section className="card" style={{ padding: 16 }}>
              <h4 style={{ fontSize: 15, marginBottom: 6, fontWeight: 800 }}>2. Upload Study Material</h4>
              <div className="banner" style={{ fontSize: 12.5, marginBottom: 10 }}>
                <b>Naming tip:</b> Name files clearly — e.g. <span className="qz-mono">Chapter 1 - Integers.pdf</span>. For multiple images, prefix with order: <span className="qz-mono">01_Chapter_3_Trigonometry.jpg</span>.
              </div>
              <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop} style={{ border: "2px dashed var(--border)", borderRadius: 12, padding: 24, textAlign: "center", cursor: "pointer", background: "var(--surface2)" }} onClick={() => document.getElementById("qpg-file-input").click()} data-testid="qpg-dropzone">
                <Upload size={28} style={{ color: "var(--accent2)" }} />
                <p style={{ marginTop: 6, fontWeight: 700 }}>Drag &amp; drop PDFs and images here</p>
                <p className="qz-muted" style={{ fontSize: 12.5 }}>or click to choose · PDF, JPG, PNG, WEBP · max 15 MB each</p>
                <input id="qpg-file-input" data-testid="qpg-file-input" type="file" multiple accept=".pdf,image/*" style={{ display: "none" }} onChange={pickFiles} />
              </div>
              {files.length > 0 && (
                <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
                  {files.map((f, i) => (
                    <div key={f.id} className="req-row" data-testid={`qpg-file-${f.id}`} style={{ alignItems: "center", gap: 10 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 8, background: "var(--surface2)", display: "grid", placeItems: "center", overflow: "hidden", flex: "none" }}>
                        {f.preview ? <img src={f.preview} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }} /> : <FileText size={20} color="var(--accent)" />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13.5, wordBreak: "break-all" }}>{f.name}</div>
                        <div className="qz-muted" style={{ fontSize: 11.5 }}>{f.kind.toUpperCase()} · {(f.file.size / 1024).toFixed(0)} KB</div>
                      </div>
                      <button className="btn btn--icon" title="Move up" onClick={() => moveFile(f.id, -1)} disabled={i === 0} style={{ width: 30, height: 30 }}><ChevronLeft size={14} style={{ transform: "rotate(90deg)" }} /></button>
                      <button className="btn btn--icon" title="Move down" onClick={() => moveFile(f.id, 1)} disabled={i === files.length - 1} style={{ width: 30, height: 30 }}><ChevronLeft size={14} style={{ transform: "rotate(-90deg)" }} /></button>
                      <button className="btn btn--icon" title="Remove" style={{ width: 30, height: 30, color: "var(--wrong)" }} onClick={() => removeFile(f.id)}><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Custom prompt */}
            <section className="card" style={{ padding: 16 }}>
              <h4 style={{ fontSize: 15, marginBottom: 6, fontWeight: 800 }}>3. Additional Instructions for AI</h4>
              <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 8 }}>Add custom instructions to guide the AI (focus areas, question style, etc.).</p>
              <textarea data-testid="qpg-prompt" className="input" style={{ width: "100%", height: 80 }} placeholder="e.g. Focus on application-based questions, avoid repeats, include board-style pattern…" value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} />
            </section>

            {/* Difficulty sections */}
            <section className="card" style={{ padding: 16 }}>
              <h4 style={{ fontSize: 15, marginBottom: 6, fontWeight: 800 }}>4. Difficulty Structure</h4>
              <p className="qz-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>Define question counts and marks for each level.</p>
              {sections.map((s, i) => (
                <div key={s.name} style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 8, marginBottom: 8, alignItems: "center" }}>
                  <span style={{ fontWeight: 800 }}>{s.name}</span>
                  <input className="input" type="number" min="0" placeholder="Questions" value={s.count} onChange={(e) => patchSection(i, { count: parseInt(e.target.value, 10) || 0 })} />
                  <input className="input" type="number" min="0" placeholder="Marks per question" value={s.marksPerQ} onChange={(e) => patchSection(i, { marksPerQ: parseInt(e.target.value, 10) || 0 })} />
                </div>
              ))}
              <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, fontSize: 13 }}>
                <input type="checkbox" checked={autoBalance} onChange={(e) => setAutoBalance(e.target.checked)} /> Auto-balance marks by AI based on difficulty
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, fontSize: 13 }}>
                <input data-testid="qpg-answerkey" type="checkbox" checked={includeAnswerKey} onChange={(e) => setIncludeAnswerKey(e.target.checked)} /> Generate answer key
              </label>
            </section>

            {err && <p style={{ color: "var(--wrong)", fontSize: 13 }} data-testid="qpg-err">{err}</p>}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
              <button data-testid="qpg-generate-btn" className="btn btn--primary btn--lg" disabled={busy} onClick={generate}>
                {busy ? <><Loader2 size={18} className="spin" /> {stage || "Generating…"}</> : <><Wand2 size={18} /> Generate Question Paper</>}
              </button>
            </div>
          </div>
        )}

        {step === 2 && paper && (
          <div data-testid="qpg-preview">
            <div className="card" style={{ padding: 20, marginBottom: 14 }}>
              <h2 style={{ textAlign: "center", fontWeight: 900, fontSize: 22 }}>{paper.meta.title}</h2>
              <p style={{ textAlign: "center", fontWeight: 700, fontSize: 16 }}>{paper.meta.subject}</p>
              <p className="qz-muted" style={{ textAlign: "center", fontSize: 12.5 }}>
                {[paper.meta.grade && `Class: ${paper.meta.grade}`, `Type: ${paper.meta.examType}`, `Total Marks: ${paper.meta.totalMarks}`, paper.meta.duration && `Duration: ${paper.meta.duration}`].filter(Boolean).join("  ·  ")}
              </p>
              {paper.meta.instructions && <p style={{ fontSize: 13, marginTop: 12 }}><b>Instructions:</b> {paper.meta.instructions}</p>}
              {(() => { let n = 1; return paper.sections.map((sec) => (
                <div key={sec.name} style={{ marginTop: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 800, color: "var(--accent)" }}>{sec.name}</h3>
                  {(sec.items || []).map((it) => (
                    <p key={n} style={{ marginTop: 6, fontSize: 13.5, lineHeight: 1.5 }}><b>{n++}.</b> {it.q} <span className="qz-muted" style={{ fontStyle: "italic" }}>[{it.marks} marks]</span></p>
                  ))}
                </div>
              )); })()}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button data-testid="qpg-download" className="btn btn--primary btn--lg" onClick={downloadDocx}><Upload size={18} style={{ transform: "rotate(180deg)" }} /> Download as Word Document</button>
              <button className="btn" onClick={() => { setPaper(null); setStep(1); }}><RotateCcw size={16} /> Regenerate</button>
              <button className="btn btn--ghost" style={{ marginLeft: "auto" }} onClick={onClose}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default function App() {
  const [theme, setTheme] = useState("voltage");
  const [sound, setSound] = useState(false);
  const [voice, setVoice] = useState(false);
  const [screen, setScreen] = useState("landing"); // landing | setup | loading | quiz | results | error
  const [name, setName] = useState("");
  const [config, setConfig] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState({ made: 0, total: 0 });
  const [genError, setGenError] = useState("");
  const [auth, setAuth] = useState(null);     // null | "login" | "signup"
  const [adminAuth, setAdminAuth] = useState(false); // shows AdminLoginModal
  const [policy, setPolicy] = useState(false);
  const [contact, setContact] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [user, setUser] = useState(null);     // {id, name, email, role}
  const [bootDone, setBootDone] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [scoring, setScoring] = useState(() => JSON.parse(JSON.stringify(SCORING_RULES)));
  const [access, setAccess] = useState({ kids: true, multiplayer: true, survival: true, challenge: true });
  const [requests, setRequests] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [gate, setGate] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [logoMgrOpen, setLogoMgrOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [customLog, setCustomLog] = useState([]);
  const [presets, setPresets] = useState([]);
  const [toast, setToast] = useState("");
  const [challenge, setChallenge] = useState(null);
  // Promotional bonus state (Feb 2026, additive).
  // - `promo` mirrors GET /promotions/active and stays null until the
  //   fetch resolves OR if the user isn't signed in (the endpoint is
  //   authed; the popup only matters for logged-in users anyway).
  // - `promoOpen` controls the popup visibility — auto-opens once per
  //   browser session per active promotion (key = updated_at).
  const [promo, setPromo] = useState(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const [quota, setQuota] = useState(null);
  const [teacherOpen, setTeacherOpen] = useState(false);
  const settings = { sound, voice };
  const role = user?.role || "user";

  // Load current user on mount
  useEffect(() => {
    let cancelled = false;
    backend.me().then((d) => {
      if (cancelled) return;
      setUser(d.user || null); setBootDone(true);
      if (d.user) backend.getQuota().then((q) => !cancelled && setQuota(q)).catch(() => {});
    }).catch(() => { if (!cancelled) setBootDone(true); });
    return () => { cancelled = true; };
  }, []);

  // ── Anonymous page-view tracking (Traffic Stats) ──────────────────────
  // Fire-and-forget. Records the current SPA "screen" as a virtual path so
  // the admin dashboard reflects navigation inside the single-page app.
  // Never blocks rendering; failures are swallowed inside analytics.js.
  useEffect(() => {
    try { trackPageView(`/${screen || "landing"}`); } catch (_) { /* noop */ }
  }, [screen]);

  // ── Google Sign-in redirect landing ─────────────────────────────────
  // After /api/auth/google/callback sets the qz_session cookie it 302s
  // back to `/?google_auth=ok` (or an error code). The cookie is already
  // present, so the me() effect above will pick up the fresh user. Here
  // we just show a toast + clean the query string so the URL stays tidy.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const flag = params.get("google_auth");
      if (!flag) return;
      const messages = {
        ok: "Signed in with Google.",
        cancelled: "Google sign-in was cancelled.",
        state_mismatch: "Sign-in expired for security. Please try again.",
        exchange_failed: "Couldn't complete Google sign-in. Please try again.",
        verify_failed: "Google could not verify the account. Please try again.",
        bad_audience: "Sign-in was rejected for security. Please try again.",
        bad_nonce: "Sign-in was rejected for security. Please try again.",
        email_unverified: "Your Google email is not verified.",
        no_email: "Google did not share an email address.",
        no_id_token: "Google sign-in didn't return a token. Please try again.",
      };
      const msg = messages[flag] || "Google sign-in failed. Please try again.";
      setToast(msg);
      setTimeout(() => setToast(""), 3500);
      params.delete("google_auth");
      const q = params.toString();
      const clean = window.location.pathname + (q ? `?${q}` : "") + (window.location.hash || "");
      window.history.replaceState({}, "", clean);
    } catch (_) { /* noop */ }
  }, []);

  // Subscribe to quota updates from every AI call
  useEffect(() => {
    const unsub = subscribeQuota((q) => setQuota(q));
    return () => unsub();
  }, []);

  // Fetch active promotion whenever the user logs in. Auto-open the popup
  // ONCE per session per promotion-version (keyed by `updated_at`) so
  // returning visitors aren't nagged on every page load.
  useEffect(() => {
    if (!user) { setPromo(null); setPromoOpen(false); return; }
    let cancelled = false;
    backend.promotionsActive().then((p) => {
      if (cancelled) return;
      setPromo(p || null);
      if (p && p.active) {
        try {
          const seenKey = `qz_promo_seen_${p.updated_at || ""}`;
          if (!sessionStorage.getItem(seenKey)) {
            setPromoOpen(true);
            sessionStorage.setItem(seenKey, "1");
          }
        } catch { setPromoOpen(true); }
      }
    }).catch(() => { if (!cancelled) setPromo(null); });
    return () => { cancelled = true; };
  }, [user]);

  // Refresh quota whenever the user changes (login / logout)
  useEffect(() => {
    if (!user) { setQuota(null); return; }
    backend.getQuota().then(setQuota).catch(() => {});
  }, [user]);

  // Detect ?c=<slug> in URL and fetch the challenge
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get("c");
    if (!slug) return;
    backend.getShare(slug).then((d) => { if (d && d.share) setChallenge(d.share); }).catch(() => {});
  }, []);

  function acceptChallenge() {
    if (!challenge) return;
    const qs = (challenge.questions || []).map((q) => ({ ...q, _order: shuffle([0, 1, 2, 3]) }));
    if (qs.length === 0) { showToast("This challenge has no questions"); return; }
    const cfg = {
      name: (user && user.name) || "You",
      category: challenge.category,
      mode: challenge.mode || "classic",
      timer: challenge.timer || 15,
      difficulty: challenge.difficulty || "medium",
      count: qs.length,
      sourceType: "challenge",
      topic: challenge.topic || "",
      challenge: { challenger_name: challenge.challenger_name, accuracy: challenge.accuracy, score: challenge.score },
    };
    setConfig(cfg); setQuestions(qs); setChallenge(null);
    // strip ?c=... from the URL so refresh doesn't re-trigger
    try { const url = new URL(window.location.href); url.searchParams.delete("c"); window.history.replaceState({}, "", url.toString()); } catch {}
    setScreen("quiz");
  }

  function dismissChallenge() {
    setChallenge(null);
    try { const url = new URL(window.location.href); url.searchParams.delete("c"); window.history.replaceState({}, "", url.toString()); } catch {}
  }

  // When admin opens admin modal, hydrate data from backend
  useEffect(() => {
    if (!adminOpen || role !== "admin") return;
    backend.adminListRequests().then((d) => setRequests(d.requests || [])).catch(() => {});
    backend.adminListUsers().then((d) => setAdminUsers(d.users || [])).catch(() => {});
    backend.adminListLogs().then((d) => setAdminLogs(d.logs || [])).catch(() => {});
  }, [adminOpen, role]);

  function showToast(t) { setToast(t); setTimeout(() => setToast(""), 2000); }

  async function handleLogout() {
    try { await backend.logout(); } catch {}
    setUser(null); setAdminOpen(false); setScreen("landing"); showToast("Logged out");
  }

  function promotePreset(name) {
    const entry = customLog.find((e) => e.name.toLowerCase() === name.toLowerCase());
    if (!entry) return;
    setPresets((ps) => ps.some((p) => p.name.toLowerCase() === name.toLowerCase()) ? ps : [{ name: entry.name, rule: entry.rule || { method: "weighted" } }, ...ps]);
    if (entry.rule && entry.rule.method && entry.rule.method !== "weighted") setScoring((s) => ({ ...s, [entry.name]: entry.rule }));
  }
  function removePreset(name) {
    setPresets((ps) => ps.filter((p) => p.name.toLowerCase() !== name.toLowerCase()));
    setScoring((s) => { const c = { ...s }; delete c[name]; return c; });
  }

  async function submitRequest({ name, email, note }) {
    try {
      const d = await backend.createAccessRequest(name || "Guest", email, note || "");
      setRequests((rs) => {
        const ex = rs.find((r) => r.email.toLowerCase() === d.request.email.toLowerCase());
        if (ex) return rs.map((r) => r.id === ex.id ? d.request : r);
        return [d.request, ...rs];
      });
      showToast("Request sent");
    } catch (e) { showToast(e.message || "Couldn't send"); }
  }

  async function setReqStatus(id, status) {
    try {
      const d = await backend.adminSetRequest(id, status);
      setRequests((rs) => rs.map((r) => r.id === id ? d.request : r));
    } catch (e) { showToast(e.message || "Couldn't update"); }
  }

  async function adminDeleteUser(uid) {
    try {
      await backend.adminDeleteUser(uid);
      setAdminUsers((u) => u.filter((x) => x.id !== uid));
      showToast("User deleted");
    } catch (e) { showToast(e.message || "Couldn't delete"); }
  }

  const approved = !gate || role === "admin" || !!user;  // logged-in users bypass gate
  const locked = !approved;

  function openAdmin() {
    if (role === "admin") { setAdminOpen(true); return; }
    if (user) {
      // Logged in but not admin — show clear message
      showToast(`Only admin accounts can open this section.`);
      return;
    }
    setAdminAuth(true);
  }

  async function generate(cfg) {
    setConfig(cfg); setName(cfg.name);
    if (cfg.custom && cfg.category) {
      setCustomLog((log) => {
        const i = log.findIndex((e) => e.name.toLowerCase() === cfg.category.toLowerCase());
        if (i >= 0) { const c = [...log]; c[i] = { ...c[i], count: c[i].count + 1, rule: cfg.scoreRule || c[i].rule || { method: "weighted" }, at: Date.now() }; return c; }
        return [{ name: cfg.category, rule: cfg.scoreRule || { method: "weighted" }, count: 1, at: Date.now() }, ...log];
      });
    }
    if (cfg._sample) {
      const qs = shuffle(SAMPLE_QUIZ).slice(0, cfg.count).map((q) => ({ ...q, _order: shuffle([0, 1, 2, 3]) }));
      setQuestions(qs); setBlocked(false); setScreen("quiz"); return;
    }
    if (!user) { setAuth("login"); return; }
    const probe = [cfg.category, cfg.topic, cfg.sourceText].filter(Boolean).join("  ");
    if (screenContent(probe, false)) { setGenError(BLOCK_MSG.default); setBlocked(true); setScreen("error"); return; }
    setProgress({ made: 0, total: cfg.count }); setScreen("loading"); setGenError(""); setBlocked(false);
    try {
      let qs = await generateQuiz(cfg, (made, total) => setProgress({ made, total }));
      if (cfg.mode === "challenge") {
        const rk = { easy: 0, medium: 1, hard: 2 };
        qs = [...qs].sort((a, b) => (rk[a.difficulty] === undefined ? 1 : rk[a.difficulty]) - (rk[b.difficulty] === undefined ? 1 : rk[b.difficulty]));
      }
      setQuestions(qs); setScreen("quiz");
    } catch (err) {
      if (err && err.authRequired) { setAuth("login"); setScreen("setup"); return; }
      if (err && err.quotaReached) {
        showToast(err.message);
        backend.getQuota().then(setQuota).catch(() => {});
        setGenError(err.message); setBlocked(false); setScreen("error"); return;
      }
      if (err && err.blocked) { setGenError(err.message); setBlocked(true); }
      else { setGenError((err && err.message) || "Generation failed."); setBlocked(false); }
      setScreen("error");
    }
  }

  function daily() {
    const topic = topicForInterest(user);
    const cat = categoryForInterest(user);
    generate({ name: name || (user?.name || "You"), category: cat, sourceType: "topic", topic, difficulty: "random", count: 5, timer: 15, mode: "classic" });
  }

  function finishQuiz(res) { setResult(res); setScreen("results"); }
  function retryWrong() {
    const wrongQs = result.answers.filter((a) => !a.correct).map((a) => result.questions[a.qIndex]).map((q) => ({ ...q, _order: shuffle([0, 1, 2, 3]) }));
    setQuestions(wrongQs); setScreen("quiz");
  }
  function replay() { setQuestions(result.questions.map((q) => ({ ...q, _order: shuffle([0, 1, 2, 3]) }))); setScreen("quiz"); }

  // Replay with the SAME setup — generates a brand-new quiz reusing the
  // exact config of the quiz the user just completed. Passes the prior
  // questions as an exclusion list so the AI tries not to repeat them.
  // Feb 2026, additive only — does NOT change the "New quiz" flow.
  function replaySameSetup() {
    if (!result || !result.config) return;
    const priorQuestions = (result.questions || [])
      .map((q) => (q && q.q ? String(q.q) : ""))
      .filter(Boolean);
    // Spread first, then override priorQuestions so we never accidentally
    // chain previous replays' lists (each replay only excludes the IMMEDIATELY
    // previous quiz, per spec).
    generate({ ...result.config, priorQuestions });
  }

  if (!bootDone) {
    return (
      <div id="qz-root" data-theme={theme}>
        <style>{CSS}</style>
        <div className="qz-wrap" style={{ display: "grid", placeItems: "center", minHeight: "80vh" }}>
          <Loader2 size={32} className="spin" color="var(--accent)" />
        </div>
      </div>
    );
  }

  return (
    <div id="qz-root" data-theme={theme} data-testid="qz-root">
      <style>{CSS}</style>
      <TopBar theme={theme} setTheme={setTheme} sound={sound} setSound={setSound} voice={voice} setVoice={setVoice} onHome={() => setScreen("landing")} showHome={!locked && screen !== "landing"} onAuth={setAuth} user={user} onLogout={handleLogout} quota={quota} onTeacher={() => setTeacherOpen(true)} onManageLogo={() => setLogoMgrOpen(true)} onBuyCredits={() => setBuyOpen(true)} />

      {locked ? (
        <GateScreen requests={requests} myEmail={user?.email || ""} onSubmit={submitRequest} onAdmin={openAdmin} />
      ) : (
        <>
          {challenge && (screen === "landing" || screen === "setup") && (
            <ChallengeBanner challenge={challenge} onAccept={acceptChallenge} onDismiss={dismissChallenge} />
          )}
          {screen === "landing" && <Landing onStart={() => setScreen("setup")} onDaily={daily} onKids={() => setScreen("kids")} onPolicy={() => setPolicy(true)} access={access} onAdmin={openAdmin} onRequest={() => setReqOpen(true)} onContact={() => setContact(true)} onFeedback={() => setFeedbackOpen(true)} onPromo={() => setPromoOpen(true)} promo={promo} />}
          {screen === "landing" && user && !user.interest && (
            <InterestBanner onSet={(u) => setUser(u)} />
          )}
          {screen === "kids" && <KidsZone onExit={() => setScreen("landing")} />}
          {screen === "setup" && <Setup onComplete={generate} initialName={user?.name || name} access={access} presets={presets} />}
          {screen === "loading" && <Loader progress={progress} config={config} />}
          {screen === "quiz" && (config.mode === "multiplayer"
            ? <MultiplayerQuiz config={config} questions={questions} settings={settings} onHome={() => setScreen("landing")} onNew={() => setScreen("setup")} />
            : <Quiz config={config} questions={questions} settings={settings} onFinish={finishQuiz} onExit={() => setScreen("landing")} />)}
          {screen === "results" && <Results result={result} scoring={scoring} role={role} user={user} onRetryWrong={retryWrong} onReplay={replay} onReplaySameSetup={replaySameSetup} onNew={() => setScreen("setup")} onHome={() => setScreen("landing")} />}
          {screen === "error" && (
            <div className="qz-wrap" style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
              <div className="card glow" style={{ maxWidth: 460, textAlign: "center", padding: 34 }} data-testid="error-card">
                {blocked ? <ShieldAlert size={34} color="var(--gold)" /> : <X size={34} color="var(--wrong)" />}
                <h3 className="qz-display" style={{ fontSize: 22, marginTop: 12 }}>{blocked ? "Let's pick a different topic" : "Couldn't generate that quiz"}</h3>
                <p className="qz-muted" style={{ marginTop: 8, lineHeight: 1.5 }}>{genError}</p>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 18, flexWrap: "wrap" }}>
                  {!blocked && <button className="btn btn--primary" onClick={() => generate(config)}><RotateCcw size={18} /> Try again</button>}
                  <button className="btn" onClick={() => generate({ ...config, _sample: true, count: Math.min((config && config.count) || 5, SAMPLE_QUIZ.length) })}><Shuffle size={18} /> Load a sample</button>
                  <button className="btn btn--ghost" onClick={() => setScreen("setup")}><ChevronLeft size={18} /> Back to setup</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {auth && <AuthModal mode={auth} onClose={() => setAuth(null)} onAuthed={(u) => { setUser(u); showToast(`Welcome, ${u.name}`); }} />}
      {adminAuth && <AdminLoginModal onClose={() => setAdminAuth(false)} onAuthed={(u) => { setUser(u); showToast(`Welcome, ${u.name}`); setAdminOpen(true); }} />}
      {policy && <PolicyModal onClose={() => setPolicy(false)} />}
      {contact && <ContactModal onClose={() => setContact(false)} />}
      {teacherOpen && (user?.user_type === "school" || user?.role === "admin") && (
        <QuestionPaperGenerator onClose={() => setTeacherOpen(false)} />
      )}
      {reqOpen && <RequestModal onClose={() => setReqOpen(false)} onSubmit={submitRequest} />}
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
      {logoMgrOpen && user && (user.user_type === "school" || user.role === "admin") && (
        <SchoolLogoModal user={user} onClose={() => setLogoMgrOpen(false)} onUpdated={(u) => setUser(u)} />
      )}
      {buyOpen && user && (
        <BuyCreditsModal user={user} onClose={() => setBuyOpen(false)} onOpenHistory={() => { setBuyOpen(false); setHistoryOpen(true); }} onCredited={(creditsDelta) => { setQuota((q) => ({ ...(q || {}), balance: ((q && q.balance) || 0) + creditsDelta, credits: ((q && q.credits) || 0) + creditsDelta })); }} />
      )}
      {historyOpen && user && (
        <PaymentHistoryModal onClose={() => setHistoryOpen(false)} />
      )}
      {adminOpen && role === "admin" && (
        <AdminModal
          role={role}
          onUnlock={() => {}}
          pin={""} setPin={() => {}}
          scoring={scoring} setScoring={setScoring}
          access={access} setAccess={setAccess}
          requests={requests} setReqStatus={setReqStatus}
          gate={gate} setGate={setGate}
          customLog={customLog} presets={presets}
          onPromote={promotePreset} onRemovePreset={removePreset}
          adminUsers={adminUsers} onDeleteUser={adminDeleteUser} currentUserId={user?.id}
          adminLogs={adminLogs}
          onExit={handleLogout}
          onClose={() => setAdminOpen(false)}
        />
      )}
      {role === "admin" && !adminOpen && <button data-testid="admin-fab" className="adm-fab" onClick={() => setAdminOpen(true)}><ShieldCheck size={16} /> Admin</button>}
      {/* Promo popup — landing page only, never over the login gate or other screens. */}
      {promoOpen && !locked && screen === "landing" && promo && promo.active && (
        <PromoModal
          promo={promo}
          onClose={() => setPromoOpen(false)}
          onBuyCredits={() => setBuyOpen(true)}
        />
      )}
      {toast && <div className="toast pop" data-testid="toast">{toast}</div>}
    </div>
  );
}


